import { DateTime, Duration, Interval } from "luxon";
import _, { isUndefined } from "lodash";
import { EquipmentTimelineData, Exclude, ExcludesParameters, EquipmentInteractionData, InteractionParameters, PersonInteractionData, PersonTimelineData, PersonExcludesParameters, EquipmentBreak, WidgetParameter, PersonUsageRow, SummaryPersonUsageRow, UsageRowInteraction, UsageRow, SummaryUsageRow, DataRow } from "../stores/TabsStore";
import { DateShift, Equipment, EquipmentAttributes, EquipmentAttributesById, Event, EventsCalculation, EventsCalculationItem, EventsResponseItem, FShift, FShiftBreak, GrouppedData, InteractionFromEvents, NormalizedShiftBreak, Person, ScreenEquipmentItem, ScreenShifts, ShiftBreak, STP, UsageDataset, UsageDatasetByPersonnels } from "../types/types";
import { hexColor } from "../enums/colors";
import { mode } from "./utils";

// ОБОРУДОВАНИЕ

// Генерация взаимодействий
function getEquipmentInteractions(interactions: EquipmentInteractionData[] | undefined, timeline: EquipmentTimelineData[] | [], threshold: number) {
  let defaultValues: InteractionParameters = {
    interactionsAsDuration: undefined,
    interactionsAsPercent: undefined,
    interactionsAsString: undefined,
    workedInInteractions: undefined,
    workedInInteractionsAsPercent: undefined,
    workedInInteractionsAsString: undefined,
    stoppedInInteractionsAsString: undefined,
    stoppedInInteractions: undefined,
    stoppedInInteractionsAsPercent: undefined,
    turnedOffInInteractionsAsString: undefined,
    turnedOffInInteractions: undefined,
    turnedOffInInteractionsAsPercent: undefined
  }
  if(!interactions) { return defaultValues}
  if(!timeline || timeline.length === 0) {
    return {
      ...defaultValues,
      interactionsAsDuration: _.sum(interactions.map(i => i.duration)),
      interactionsAsString: Duration.fromMillis(_.sum(interactions.map(i => i.duration)) * 1000).toFormat("hh:mm") 
    }
  }
  if(timeline && timeline.length > 0) {
    let extendInteractions = []
    for(let it of interactions) {
      extendInteractions.push(extendEquipmentInteractionData(it, timeline, threshold))
    }

    let workedInInteractions = _.sum(extendInteractions.map(i => i.worked))
    let stoppedInInteractions = _.sum(extendInteractions.map(i => i.stopped))
    let turnedOffInInteractions = _.sum(extendInteractions.map(i => i.turnedOff))
    
    return {
      ...defaultValues,
      interactionsAsDuration: _.sum(interactions.map(i => i.duration)),
      interactionsAsString: Duration.fromObject({seconds: _.sum(interactions.map(i => i.duration))}).toFormat("hh:mm"),
      workedInInteractions: workedInInteractions,
      workedInInteractionsAsPercent: workedInInteractions > 0 ? (workedInInteractions / (workedInInteractions + stoppedInInteractions + turnedOffInInteractions)) * 100 : undefined,
      workedInInteractionsAsString: Duration.fromObject({seconds: workedInInteractions}).toFormat("hh:mm"),
      stoppedInInteractions: stoppedInInteractions,
      stoppedInInteractionsAsPercent: stoppedInInteractions > 0 ? (stoppedInInteractions / (workedInInteractions + stoppedInInteractions + turnedOffInInteractions)) * 100 : undefined,
      stoppedInInteractionsAsString: Duration.fromObject({seconds: stoppedInInteractions}).toFormat("hh:mm"),
      turnedOffInInteractions: turnedOffInInteractions,
      turnedOffInInteractionsAsPercent: turnedOffInInteractions > 0 ? (turnedOffInInteractions / (workedInInteractions + stoppedInInteractions + turnedOffInInteractions)) * 100 : undefined,
      turnedOffInInteractionsAsString: Duration.fromObject({seconds: turnedOffInInteractions}).toFormat("hh:mm"),
      interaction: extendInteractions
    }
  }
}

function extendEquipmentInteractionData(interaction: EquipmentInteractionData, timeline: EquipmentTimelineData[], threshold: number) {
  let equipmentStart = getEquipmentStart(
    DateTime.fromSQL(interaction.start),
    DateTime.fromSQL(interaction.finish),
    timeline,
    threshold
  );
  let equipmentEnd = getEquipmentEnd(
    DateTime.fromSQL(interaction.start),
    DateTime.fromSQL(interaction.finish),
    timeline,
    threshold
  );
  let interactionWorked = getEquipmentInteractionWorked(
    DateTime.fromSQL(interaction.start),
    DateTime.fromSQL(interaction.finish),
    timeline
  );
  let interactionStopped = getEquipmentInteractionStopped(
    DateTime.fromSQL(interaction.start),
    DateTime.fromSQL(interaction.finish),
    timeline
  );
  let interactionTurnedOff = getEquipmentInteractionTurnedOff(
    DateTime.fromSQL(interaction.start),
    DateTime.fromSQL(interaction.finish),
    timeline
  );

  return {
    ...interaction,
    equipmentStart: equipmentStart ? DateTime.fromSQL(equipmentStart?.start!) : undefined,
    equipmentEnd: equipmentEnd ? DateTime.fromSQL(equipmentEnd?.finish!) : undefined,
    worked: interactionWorked,
    stopped: interactionStopped,
    turnedOff: interactionTurnedOff
  }
}

function extendEquipmentTimelineData(timeline: EquipmentTimelineData[] | undefined, at: DateTime, shift: FShift, threshold: number) {
  if (!timeline || timeline.length == 0) {
    return {
      timelineIsLoaded: false,
      firstHourWorked: undefined,
      lastHourWorked: undefined,
      firstHourStopped: undefined,
      lastHourStopped: undefined,
      equipmentStartedAt: undefined,
      equipmentStartedAtAsString: undefined,
      equipmentStartViolation: undefined,
      equipmentStartAsStringViolation: undefined,
      equipmentEndedAt: undefined,
      equipmentEndedAtAsString: undefined,
      equipmentEndViolation: undefined,
      equipmentEndViolationAsString: undefined
    }
  }
  let start = at.set({ hour: shift.start_at.hour, minute: shift.start_at.minute, second: shift.start_at.second });
  let end: DateTime;
  if (shift.end_at > shift.start_at) {
    end = at.set({ hour: shift.end_at.hour, minute: shift.end_at.minute, second: shift.end_at.second });
  } else {
    end = at
      .plus({ day: 1 })
      .set({ hour: shift.end_at.hour, minute: shift.end_at.minute, second: shift.end_at.second });
  }

  let equipmentStartedAt = getEquipmentStart(start, end, timeline, threshold)
  let equipmentEndedAt = getEquipmentEnd(start, end, timeline, threshold)
  let fhw = getEquipmentShiftFirstHourWorked(timeline, at, shift)
  let lhw = getEquipmentShiftLastHourWorked(timeline, at, shift)

  return {
    timelineIsLoaded: true,
    firstHourWorked: fhw ? fhw : 0,
    lastHourWorked: lhw ? lhw : 0,
    firstHourStopped: fhw ? 3600 - fhw : 3600,
    lastHourStopped: lhw ? 3600 - lhw : 3600,
    equipmentStartedAt: equipmentStartedAt ? DateTime.fromSQL(equipmentStartedAt?.start!) : undefined,
    equipmentStartedAtAsString: equipmentStartedAt ? DateTime.fromSQL(equipmentStartedAt?.start!).toFormat("HH:mm:ss") : undefined,
    equipmentEndedAt: equipmentEndedAt ? DateTime.fromSQL(equipmentEndedAt?.finish!) : undefined,
    equipmentEndedAtAsString: equipmentEndedAt ? DateTime.fromSQL(equipmentEndedAt?.finish!).toFormat("HH:mm:ss") : undefined,
    equipmentStartViolation: equipmentStartedAt ? DateTime.fromSQL(equipmentStartedAt?.start!).toSeconds() - start.toSeconds() : undefined,
    equipmentStartAsStringViolation: equipmentStartedAt ? Duration.fromObject({seconds: DateTime.fromSQL(equipmentStartedAt?.start!).toSeconds() - start.toSeconds()}).toFormat("hh:mm:ss") : undefined,
    equipmentEndViolation: equipmentStartedAt ? end.toSeconds() - DateTime.fromSQL(equipmentEndedAt?.finish!).toSeconds() : undefined,
    equipmentEndViolationAsString: equipmentStartedAt ? Duration.fromObject({seconds: end.toSeconds() - DateTime.fromSQL(equipmentEndedAt?.finish!).toSeconds()}).toFormat("hh:mm:ss") : undefined
  }
}

// Функция расчета загрузки первого часа работы
function getEquipmentShiftFirstHourWorked(timeline: EquipmentTimelineData[] | undefined, at: DateTime, shift: FShift) {
  if (!timeline || timeline.length === 0) return undefined;
  let start = at.set({ hour: shift.start_at.hour, minute: shift.start_at.minute, second: shift.start_at.second });
  let finish = at.set({ hour: shift.start_at.hour + 1, minute: shift.start_at.minute, second: shift.start_at.second});

  let pt = getEquipmentCorrectTimeline(start, finish, timeline)
  
  return _.sum(pt!.filter((t) => t.state === "worked").map((t) => t.duration));
}
// Функция расчета загрузки последнего часа работы
function getEquipmentShiftLastHourWorked(timeline: EquipmentTimelineData[] | undefined, at: DateTime, shift: FShift) {
  if (!timeline || timeline.length === 0) return undefined;
  let finish: DateTime;
  if (shift.end_at > shift.start_at) {
    finish = at.set({ hour: shift.end_at.hour, minute: shift.end_at.minute, second: shift.end_at.second });
  } else {
    finish = at
      .plus({ day: 1 })
      .set({ hour: shift.end_at.hour, minute: shift.end_at.minute, second: shift.end_at.second });
  }
  let start = finish.minus({seconds: 3600})

  let pt = getEquipmentCorrectTimeline(start, finish, timeline)

  return _.sum(pt!.filter((t) => t.state === "worked").map((t) => t.duration));
}
// Функция получения времени старта работы оборудования
function getEquipmentStart(iStart: DateTime, iFinish: DateTime, timeline: EquipmentTimelineData[] | undefined, threshold: number) {
  if (!timeline || timeline.length == 0) { return undefined;}

  return timeline.filter(
    (t) => t.state === "worked" && t.start_at >= iStart.toSeconds() && t.end_at <= iFinish.toSeconds() && (t.duration + t.no_data_seconds) > threshold
  )[0];
}
// Функция получения времени окончания работы оборудования
function getEquipmentEnd(iStart: DateTime, iFinish: DateTime, timeline: EquipmentTimelineData[] | undefined, threshold: number) {
  if (!timeline || timeline.length == 0) {
    return undefined;
  }
  return timeline
    .filter((t) => t.state === "worked" && t.start_at >= iStart.toSeconds() && t.end_at <= iFinish.toSeconds() && (t.duration + t.no_data_seconds) > threshold)
    .reverse()[0];
}
// Функция получения загрузку оборудования во взоимодействии
function getEquipmentInteractionWorked(start: DateTime, finish: DateTime, timeline: EquipmentTimelineData[] | undefined) {
  if (!timeline || timeline.length == 0) {
    return 0;
  }
  return _.sum(
    timeline
      .filter((t) => t.state === "worked" && t.start_at >= start.toSeconds() && t.end_at <= finish.toSeconds())
      .map((t) => t.duration)
  );
}
// Функция получения простоя оборудования во взоимодействии
function getEquipmentInteractionStopped(start: DateTime, finish: DateTime, timeline: EquipmentTimelineData[] | undefined) {
  if (!timeline || timeline.length == 0) {
    return 0;
  }
  return _.sum(
    timeline
      .filter((t) => t.state === "stopped" && t.start_at >= start.toSeconds() && t.end_at <= finish.toSeconds())
      .map((t) => t.duration)
  );
}
// Функция получения выключено оборудования во взоимодействии
function getEquipmentInteractionTurnedOff(start: DateTime, finish: DateTime, timeline: EquipmentTimelineData[] | undefined) {
  if (!timeline || timeline.length == 0) {
    return 0;
  }
  let to = _.sum(
    timeline
      .filter((t) => t.state === "turned_off" && t.start_at >= start.toSeconds() && t.end_at <= finish.toSeconds())
      .map((t) => t.duration)
  );

  let nd = _.sum(
    timeline
      .filter((t) => t.start_at >= start.toSeconds() && t.end_at <= finish.toSeconds())
      .map((t) =>  t.no_data_seconds)
  );
  return to + nd
}
// Функция расчета отклонения в перерывах
function getEquipmentBreaksViolation(timeline: EquipmentTimelineData[] | undefined, shiftStart: DateTime, shiftEnd: DateTime, excludes: Exclude[]) {
  let breaks: Exclude[] = excludes

  let breaksData: EquipmentBreak[] = []

  if(isUndefined(breaks) || breaks.length === 0) { return }

  breaks.map((b) => {
    let start: DateTime | null = null
    let finish: DateTime | null = null

    let s = DateTime.fromISO(b.exclude_start)
    let f = DateTime.fromISO(b.exclude_end)

    if(s > f) {
      start = shiftStart.set({hour: s.hour, minute: s.minute})
      finish = shiftEnd.set({hour: f.hour, minute: f.minute})
    } else if((s.hour <= shiftEnd.hour) && (s.hour >= shiftEnd.startOf('day').hour)) {
      start = shiftEnd.set({hour: s.hour, minute: s.minute})
      finish = shiftEnd.set({hour: f.hour, minute: f.minute})
    } else {
      start = shiftStart.set({hour: s.hour, minute: s.minute})
      finish = shiftStart.set({hour: f.hour, minute: f.minute})
    }

    let breakDuration = finish.toSeconds() - start.toSeconds()
    let preTimeline = getEquipmentCorrectTimeline(start!, finish!, timeline)

    let worked = _.sum(preTimeline?.filter(t => t.state === "worked").map(t => t.duration + t.no_data_seconds))
    let stopped = _.sum(preTimeline?.filter(t => t.state === "stopped").map(t => t.duration + t.no_data_seconds))
    let turned_off = _.sum(preTimeline?.filter(t => t.state === "turned_off").map(t => t.duration))

    if(start.toSeconds() > shiftStart.toSeconds() && finish.toSeconds() < shiftEnd.toSeconds()) {
      breaksData.push({
        hasViolation: worked < 960,
        violation: ((stopped + turned_off) - breakDuration) > 0 ? ((stopped + turned_off) - breakDuration) : 0,
        breakStart: start,
        breakEnd: finish,
        duration: breakDuration,
        worked: worked,
        stopped: stopped,
        turned_off: turned_off
      })
    }
  })
  
  return {breaks: breaksData}
}

// Функция корректировки таймлайна для расчетов
function getEquipmentCorrectTimeline(start: DateTime, finish: DateTime, timeline: EquipmentTimelineData[] | undefined) {
  if (!timeline || timeline.length == 0) {
    return timeline;
  }
  let startUTC = start.toUTC().toSeconds()
  let endUTC = finish.toUTC().toSeconds()
  
  let preTimeline = timeline.filter(p => p.end_at > startUTC && p.start_at < endUTC)
  
  if(preTimeline.length > 0) {
    if(preTimeline.length === 1) {
      let onePoint = preTimeline[0]
      let newOnePoint = {...onePoint, ...{start_at: startUTC, end_at: endUTC, start: start.toSQL()!, finish: finish.toSQL()!, duration: endUTC - startUTC}}
      preTimeline[0] = correctNoDataSeconds(newOnePoint)
      return preTimeline
    } else {
      let startPoint = preTimeline[0]
      let endPoint = preTimeline[preTimeline.length - 1]
      
      let newStartPoint = {...startPoint, ...{start_at: startUTC, start: start.toSQL()!, duration: startPoint.end_at - startUTC}}
      let newEndPoint = {...endPoint, ...{end_at: endUTC, finish: finish.toSQL()!, duration: endUTC - endPoint.start_at}}
  
  
      preTimeline[0] = correctNoDataSeconds(newStartPoint)
      preTimeline[preTimeline.length - 1] = correctNoDataSeconds(newEndPoint)
      return preTimeline
    }
  } else {
    let bigPoint = timeline.find((p) => p.start_at < startUTC && p.end_at > endUTC)!
    let newBigPoint = {...bigPoint, ...{start_at: startUTC, end_at: endUTC, start: start.toSQL()!, finish: finish.toSQL()!, duration: endUTC - startUTC}}
    return [correctNoDataSeconds(newBigPoint)]
  }
}
// Функция корректировки no_data в таймлайнах
function correctNoDataSeconds(point: EquipmentTimelineData) {
  if(!point.no_data_extended || point?.no_data_extended?.length === 0) { return {...point, no_data_extended: [], no_data_seconds: 0} }
  let no_data_extended: {from: number, to: number, duration: number}[] = point.no_data_extended.map(i => ({from: Number(i.from), to: Number(i.to), duration: Number(i.duration)}))
    .filter(i => i.from <= point.end_at)
    .filter(i => i.to >= point.start_at)

  if(no_data_extended.length === 0) { return {...point, no_data_seconds: 0, no_data_extended: []}}

  if(no_data_extended.length === 1) {
    let one_no_data = no_data_extended[0]
    let from_one_no_data = one_no_data.from > point.start_at ? one_no_data.from : point.start_at
    let to_one_no_data = one_no_data.to > point.end_at ? point.end_at : one_no_data.to
    let dur_one_no_data = to_one_no_data - from_one_no_data
    let no_data_point = {
      from: from_one_no_data, 
      to: to_one_no_data,
      duration: dur_one_no_data
    }
    no_data_extended[0] = no_data_point
  } else {
    let first_no_data = no_data_extended[0]
    let last_no_data = no_data_extended[no_data_extended.length - 1]

    let from_first_no_data = first_no_data.from > point.start_at ? first_no_data.from : point.start_at
    let to_first_no_data = first_no_data.to > point.end_at ? point.end_at : first_no_data.to
    let dur_first_no_data = to_first_no_data - from_first_no_data

    let from_last_no_data = last_no_data.from > point.start_at ? last_no_data.from : point.start_at
    let to_last_no_data = last_no_data.to > point.end_at ? point.end_at : last_no_data.to
    let dur_last_no_data = to_last_no_data - from_last_no_data

    let first = {
      from: from_first_no_data, 
      to: to_first_no_data,
      duration: dur_first_no_data
    }

    let last = {
      from: from_last_no_data, 
      to: to_last_no_data,
      duration: dur_last_no_data
    }

    no_data_extended[0] = first
    no_data_extended[no_data_extended.length - 1] = last
  }
  return {...point, no_data_seconds: _.sum(no_data_extended.map(i => i.duration)), no_data_extended: no_data_extended}
}

  



// Генерация взаимодействий
function getPersonInteractions(interactions: PersonInteractionData[] | undefined, timeline: PersonTimelineData[] | [], threshold: number) {
  let defaultValues: InteractionParameters = {
    interactionsAsDuration: undefined,
    interactionsAsPercent: undefined,
    interactionsAsString: undefined,
    workedInInteractions: undefined,
    workedInInteractionsAsPercent: undefined,
    workedInInteractionsAsString: undefined,
    stoppedInInteractionsAsString: undefined,
    stoppedInInteractions: undefined,
    stoppedInInteractionsAsPercent: undefined,
    turnedOffInInteractionsAsString: undefined,
    turnedOffInInteractions: undefined,
    turnedOffInInteractionsAsPercent: undefined
  }
  if(!interactions) { return defaultValues}
  if(!timeline || timeline.length === 0) {
    return {
      ...defaultValues,
      interactionsAsDuration: _.sum(interactions.map(i => i.duration)),
      interactionsAsString: Duration.fromMillis(_.sum(interactions.map(i => i.duration)) * 1000).toFormat("hh:mm") 
    }
  }
  if(timeline && timeline.length > 0) {
    let extendInteractions = []
    for(let it of interactions) {
      extendInteractions.push(extendPersonInteractionData(it, timeline, threshold))
    }

    let workedInInteractions = _.sum(extendInteractions.map(i => i.worked))
    let stoppedInInteractions = _.sum(extendInteractions.map(i => i.stopped))
    let turnedOffInInteractions = _.sum(extendInteractions.map(i => i.turnedOff))
    
    return {
      ...defaultValues,
      interactionsAsDuration: _.sum(interactions.map(i => i.duration)),
      interactionsAsString: Duration.fromObject({seconds: _.sum(interactions.map(i => i.duration))}).toFormat("hh:mm"),
      workedInInteractions: workedInInteractions,
      workedInInteractionsAsPercent: workedInInteractions > 0 ? (workedInInteractions / (workedInInteractions + stoppedInInteractions + turnedOffInInteractions)) * 100 : undefined,
      workedInInteractionsAsString: Duration.fromObject({seconds: workedInInteractions}).toFormat("hh:mm"),
      stoppedInInteractions: stoppedInInteractions,
      stoppedInInteractionsAsPercent: stoppedInInteractions > 0 ? (stoppedInInteractions / (workedInInteractions + stoppedInInteractions + turnedOffInInteractions)) * 100 : undefined,
      stoppedInInteractionsAsString: Duration.fromObject({seconds: stoppedInInteractions}).toFormat("hh:mm"),
      turnedOffInInteractions: turnedOffInInteractions,
      turnedOffInInteractionsAsPercent: turnedOffInInteractions > 0 ? (turnedOffInInteractions / (workedInInteractions + stoppedInInteractions + turnedOffInInteractions)) * 100 : undefined,
      turnedOffInInteractionsAsString: Duration.fromObject({seconds: turnedOffInInteractions}).toFormat("hh:mm"),
      interaction: extendInteractions
    }
  }
}

function extendPersonInteractionData(interaction: PersonInteractionData, timeline: PersonTimelineData[], threshold: number) {
  let equipmentStart = getPersonStart(
    DateTime.fromSQL(interaction.start),
    DateTime.fromSQL(interaction.finish),
    timeline,
    threshold
  );
  let equipmentEnd = getPersonEnd(
    DateTime.fromSQL(interaction.start),
    DateTime.fromSQL(interaction.finish),
    timeline,
    threshold
  );
  let interactionWorked = getPersonInteractionWorked(
    DateTime.fromSQL(interaction.start),
    DateTime.fromSQL(interaction.finish),
    timeline
  );
  let interactionStopped = getPersonInteractionStopped(
    DateTime.fromSQL(interaction.start),
    DateTime.fromSQL(interaction.finish),
    timeline
  );
  let interactionTurnedOff = getPersonInteractionTurnedOff(
    DateTime.fromSQL(interaction.start),
    DateTime.fromSQL(interaction.finish),
    timeline
  );

  return {
    ...interaction,
    equipmentStart: equipmentStart ? DateTime.fromSQL(equipmentStart?.start!) : undefined,
    equipmentEnd: equipmentEnd ? DateTime.fromSQL(equipmentEnd?.finish!) : undefined,
    worked: interactionWorked,
    stopped: interactionStopped,
    turnedOff: interactionTurnedOff
  }
}

function extendPersonTimelineData(timeline: PersonTimelineData[] | undefined, at: DateTime, shift: FShift, threshold: number) {
  if (!timeline || timeline.length == 0) {
    return {
      timelineIsLoaded: false,
      firstHourWorked: undefined,
      lastHourWorked: undefined,
      firstHourStopped: undefined,
      lastHourStopped: undefined,
      equipmentStartedAt: undefined,
      equipmentStartedAtAsString: undefined,
      equipmentStartViolation: undefined,
      equipmentStartAsStringViolation: undefined,
      equipmentEndedAt: undefined,
      equipmentEndedAtAsString: undefined,
      equipmentEndViolation: undefined,
      equipmentEndViolationAsString: undefined
    }
  }
  let start = at.set({ hour: shift.start_at.hour, minute: shift.start_at.minute, second: shift.start_at.second });
  let end: DateTime;
  if (shift.end_at > shift.start_at) {
    end = at.set({ hour: shift.end_at.hour, minute: shift.end_at.minute, second: shift.end_at.second });
  } else {
    end = at
      .plus({ day: 1 })
      .set({ hour: shift.end_at.hour, minute: shift.end_at.minute, second: shift.end_at.second });
  }

  let equipmentStartedAt = getPersonStart(start, end, timeline, threshold)
  let equipmentEndedAt = getPersonEnd(start, end, timeline, threshold)
  let fhw = getPersonShiftFirstHourWorked(timeline, at, shift)
  let lhw = getPersonShiftLastHourWorked(timeline, at, shift)

  return {
    timelineIsLoaded: true,
    firstHourWorked: fhw ? fhw : 0,
    lastHourWorked: lhw ? lhw : 0,
    firstHourStopped: fhw ? 3600 - fhw : 3600,
    lastHourStopped: lhw ? 3600 - lhw : 3600,
    equipmentStartedAt: equipmentStartedAt ? DateTime.fromSQL(equipmentStartedAt?.start!) : undefined,
    equipmentStartedAtAsString: equipmentStartedAt ? DateTime.fromSQL(equipmentStartedAt?.start!).toFormat("HH:mm:ss") : undefined,
    equipmentEndedAt: equipmentEndedAt ? DateTime.fromSQL(equipmentEndedAt?.finish!) : undefined,
    equipmentEndedAtAsString: equipmentEndedAt ? DateTime.fromSQL(equipmentEndedAt?.finish!).toFormat("HH:mm:ss") : undefined,
    equipmentStartViolation: equipmentStartedAt ? DateTime.fromSQL(equipmentStartedAt?.start!).toSeconds() - start.toSeconds() : undefined,
    equipmentStartAsStringViolation: equipmentStartedAt ? Duration.fromObject({seconds: DateTime.fromSQL(equipmentStartedAt?.start!).toSeconds() - start.toSeconds()}).toFormat("hh:mm:ss") : undefined,
    equipmentEndViolation: equipmentStartedAt ? end.toSeconds() - DateTime.fromSQL(equipmentEndedAt?.finish!).toSeconds() : undefined,
    equipmentEndViolationAsString: equipmentStartedAt ? Duration.fromObject({seconds: end.toSeconds() - DateTime.fromSQL(equipmentEndedAt?.finish!).toSeconds()}).toFormat("hh:mm:ss") : undefined
  }
}

// Функция расчета загрузки первого часа работы
function getPersonShiftFirstHourWorked(timeline: PersonTimelineData[] | undefined, at: DateTime, shift: FShift) {
  if (!timeline || timeline.length === 0) return undefined;
  let start = at.set({ hour: shift.start_at.hour, minute: shift.start_at.minute, second: shift.start_at.second });
  let finish = at.set({ hour: shift.start_at.hour + 1, minute: shift.start_at.minute, second: shift.start_at.second});

  let pt = getPersonCorrectTimeline(start, finish, timeline)
  
  return _.sum(pt!.filter((t) => t.state === "worked").map((t) => t.duration));
}
// Функция расчета загрузки последнего часа работы
function getPersonShiftLastHourWorked(timeline: PersonTimelineData[] | undefined, at: DateTime, shift: FShift) {
  if (!timeline || timeline.length === 0) return undefined;
  let finish: DateTime;
  if (shift.end_at > shift.start_at) {
    finish = at.set({ hour: shift.end_at.hour, minute: shift.end_at.minute, second: shift.end_at.second });
  } else {
    finish = at
      .plus({ day: 1 })
      .set({ hour: shift.end_at.hour, minute: shift.end_at.minute, second: shift.end_at.second });
  }
  let start = finish.minus({seconds: 3600})

  let pt = getPersonCorrectTimeline(start, finish, timeline)

  return _.sum(pt!.filter((t) => t.state === "worked").map((t) => t.duration));
}
// Функция получения времени старта работы оборудования
function getPersonStart(iStart: DateTime, iFinish: DateTime, timeline: PersonTimelineData[] | undefined, threshold: number) {
  console.log(timeline)
  if (!timeline || timeline.length == 0) { return undefined;}

  return timeline.filter(
    (t) => t.state === "worked" && t.start_at >= iStart.toSeconds() && t.end_at <= iFinish.toSeconds() && (t.duration + t.no_card_seconds) > threshold
  )[0];
}
// Функция получения времени окончания работы оборудования
function getPersonEnd(iStart: DateTime, iFinish: DateTime, timeline: PersonTimelineData[] | undefined, threshold: number) {
  if (!timeline || timeline.length == 0) {
    return undefined;
  }
  return timeline
    .filter((t) => t.state === "worked" && t.start_at >= iStart.toSeconds() && t.end_at <= iFinish.toSeconds() && (t.duration + t.no_card_seconds) > threshold)
    .reverse()[0];
}
// Функция получения загрузку оборудования во взоимодействии
function getPersonInteractionWorked(start: DateTime, finish: DateTime, timeline: PersonTimelineData[] | undefined) {
  if (!timeline || timeline.length == 0) {
    return 0;
  }
  return _.sum(
    timeline
      .filter((t) => t.state === "worked" && t.start_at >= start.toSeconds() && t.end_at <= finish.toSeconds())
      .map((t) => t.duration)
  );
}
// Функция получения простоя оборудования во взоимодействии
function getPersonInteractionStopped(start: DateTime, finish: DateTime, timeline: PersonTimelineData[] | undefined) {
  if (!timeline || timeline.length == 0) {
    return 0;
  }
  return _.sum(
    timeline
      .filter((t) => t.state === "stopped" && t.start_at >= start.toSeconds() && t.end_at <= finish.toSeconds())
      .map((t) => t.duration)
  );
}
// Функция получения выключено оборудования во взоимодействии
function getPersonInteractionTurnedOff(start: DateTime, finish: DateTime, timeline: PersonTimelineData[] | undefined) {
  if (!timeline || timeline.length == 0) {
    return 0;
  }
  let to = _.sum(
    timeline
      .filter((t) => t.state === "turned_off" && t.start_at >= start.toSeconds() && t.end_at <= finish.toSeconds())
      .map((t) => t.duration)
  );

  let nd = _.sum(
    timeline
      .filter((t) => t.start_at >= start.toSeconds() && t.end_at <= finish.toSeconds())
      .map((t) =>  t.no_card_seconds)
  );
  return to + nd
}

// Функция корректировки таймлайна для расчетов
function getPersonCorrectTimeline(start: DateTime, finish: DateTime, timeline: PersonTimelineData[] | undefined) {
  if (!timeline || timeline.length == 0) {
    return timeline;
  }
  let startUTC = start.toUTC().toSeconds()
  let endUTC = finish.toUTC().toSeconds()
  
  let preTimeline = timeline.filter(p => p.end_at > startUTC && p.start_at < endUTC)
  
  if(preTimeline.length > 0) {
    if(preTimeline.length === 1) {
      let onePoint = preTimeline[0]
      let newOnePoint = {...onePoint, ...{start_at: startUTC, end_at: endUTC, start: start.toSQL()!, finish: finish.toSQL()!, duration: endUTC - startUTC}}
      preTimeline[0] = correctNoCardSeconds(newOnePoint)
      return preTimeline
    } else {
      let startPoint = preTimeline[0]
      let endPoint = preTimeline[preTimeline.length - 1]
      
      let newStartPoint = {...startPoint, ...{start_at: startUTC, start: start.toSQL()!, duration: startPoint.end_at - startUTC}}
      let newEndPoint = {...endPoint, ...{end_at: endUTC, finish: finish.toSQL()!, duration: endUTC - endPoint.start_at}}
  
  
      preTimeline[0] = correctNoCardSeconds(newStartPoint)
      preTimeline[preTimeline.length - 1] = correctNoCardSeconds(newEndPoint)
      return preTimeline
    }
  } else {
    let bigPoint = timeline.find((p) => p.start_at < startUTC && p.end_at > endUTC)!
    let newBigPoint = {...bigPoint, ...{start_at: startUTC, end_at: endUTC, start: start.toSQL()!, finish: finish.toSQL()!, duration: endUTC - startUTC}}
    return [correctNoCardSeconds(newBigPoint)]
  }
}

// Функция корректировки no_data в таймлайнах
function correctNoCardSeconds(point: PersonTimelineData) {
  if(!point.no_card_extended || point?.no_card_extended?.length === 0) { return {...point, no_card_extended: [], no_card_seconds: 0} }
  let no_card_extended: {from: number, to: number, duration: number}[] = point.no_card_extended
    .map(i => ({from: Number(i.from), to: Number(i.to), duration: Number(i.duration)}))
    .filter(i => i.from <= point.end_at)
    .filter(i => i.to >= point.start_at)

  if(no_card_extended.length === 0) { return {...point, no_card_seconds: 0, no_card_extended: []}}

  if(no_card_extended.length === 1) {
    let one_no_data = no_card_extended[0]
    let from_one_no_data = one_no_data.from > point.start_at ? one_no_data.from : point.start_at
    let to_one_no_data = one_no_data.to > point.end_at ? point.end_at : one_no_data.to
    let dur_one_no_data = to_one_no_data - from_one_no_data
    let no_data_point = {
      from: from_one_no_data, 
      to: to_one_no_data,
      duration: dur_one_no_data
    }
    no_card_extended[0] = no_data_point
  } else {
    let first_no_data = no_card_extended[0]
    let last_no_data = no_card_extended[no_card_extended.length - 1]

    let from_first_no_data = first_no_data.from > point.start_at ? first_no_data.from : point.start_at
    let to_first_no_data = first_no_data.to > point.end_at ? point.end_at : first_no_data.to
    let dur_first_no_data = to_first_no_data - from_first_no_data

    let from_last_no_data = last_no_data.from > point.start_at ? last_no_data.from : point.start_at
    let to_last_no_data = last_no_data.to > point.end_at ? point.end_at : last_no_data.to
    let dur_last_no_data = to_last_no_data - from_last_no_data

    let first = {
      from: from_first_no_data, 
      to: to_first_no_data,
      duration: dur_first_no_data
    }

    let last = {
      from: from_last_no_data, 
      to: to_last_no_data,
      duration: dur_last_no_data
    }

    no_card_extended[0] = first
    no_card_extended[no_card_extended.length - 1] = last
  }
  return {...point, no_card_seconds: _.sum(no_card_extended.map(i => i.duration)), no_card_extended: no_card_extended}
}

function getTimelineByHours(timelines: EquipmentTimelineData[], shiftStart: DateTime, shiftEnd: DateTime, excludes: Exclude[]) {
  const hours = Interval.fromDateTimes(shiftStart, shiftEnd).splitBy({hour: 1})
  let calculatedHours = []
  for(let hour of hours){
    let t = getEquipmentCorrectTimeline(hour.start!, hour.end!, timelines)
    calculatedHours.push({
      hour: hour.start?.hour, 
      duration: _.sum(t?.map(i => i.duration)),
      worked: _.sum(t?.filter(i => i.state === "worked").map(i => i.duration)),  
      stopped: _.sum(t?.filter(i => i.state === "stopped").map(i => i.duration)),  
      turned_off: _.sum(t?.filter(i => i.state === "turned_off").map(i => i.duration)), 
      no_data: _.sum(t?.map(i => i.no_data_seconds))})
  }
  return calculatedHours
}

// Блок разбиения таймлайна по часам 

const plusTime = (
  state: string,
  hourStats: { hour: number; worked: number; stopped: number; turned_off: number; duration: number; no_data: number },
  duration: number,
  no_data: number
) => {
  let roundedDuration = Math.floor(duration);
  switch (state) {
    case "worked":
      const newWorked = hourStats.worked + roundedDuration;
      hourStats.worked = newWorked > 0 ? newWorked : 0;
      break;
    case "stopped":
      const newStopped = hourStats.stopped + roundedDuration;
      hourStats.stopped = newStopped > 0 ? newStopped : 0;
      break;
    case "turned_off":
      const newTurnedOff = hourStats.turned_off + roundedDuration;
      hourStats.turned_off = newTurnedOff ? newTurnedOff : 0;
      break;
  }
  const newDuration = hourStats.duration + roundedDuration;
  hourStats.duration = newDuration > 0 ? newDuration : 0;
  hourStats.no_data = hourStats.no_data + no_data;
  return hourStats;
};

const clearTime = (hourStats: {
  hour: number;
  worked: number;
  stopped: number;
  turned_off: number;
  duration: number;
  no_data: number;
}) => {
  hourStats.worked = 0;
  hourStats.stopped = 0;
  hourStats.turned_off = 0;
  hourStats.duration = 0;
  hourStats.no_data = 0;

  return hourStats;
};


// EVENTS

// 0: "equipment_item_id"
// 1: "event_date"
// 2: "shift_date"
// 3: "start"
// 4: "start_ts"
// 5: "finish"
// 6: "finish_ts"
// 7: "duration"
// 8: "state"
// 9: "equipment_item_status"
// 10: "personnel_id"
// 11: "access_token_id"
// 12: "equipment_item_division_id"
// 13: "personnel_division_id"
// 14: "state_changed"
// 15: "status_changed"
// 16: "person_changed"
// 17: "hour" добавляем

function calculateEvents(events: Event[], duration: number) {
  let totalWorked: number = Math.round(_.sum(events.filter(i => i[8] === "worked" && Number(i[9]) === 0).map(i => i[7])))
  let totalStopped: number = Math.round(_.sum(events.filter(i => i[8] === "stopped" && Number(i[9]) === 0).map(i => i[7])))
  let totalRepair: number = Math.round(_.sum(events.filter(i => i[8] !== "turned_off" && Number(i[9]) === 4).map(i => i[7])))
  let totalBroken: number = Math.round(_.sum(events.filter(i => i[8] !== "turned_off" && Number(i[9]) === 1).map(i => i[7])))
  let totalMaintenance: number = Math.round(_.sum(events.filter(i => i[8] !== "turned_off" && Number(i[9]) === 5).map(i => i[7])))
  let totalOther: number = Math.round(_.sum(events.filter(i => i[8] !== "turned_off" && [2,3,6,7,8,9,10].includes(Number(i[9])) ).map(i => i[7])))
  let totalTurnedOff: number = Math.round(_.sum(events.filter(i => i[8] === "turned_off").map(i => i[7])))

  let totalWorkedPercent: number = (totalWorked / duration) * 100
  let totalStoppedPercent: number = (totalStopped / duration) * 100
  let totalRepairPercent: number = (totalRepair / duration) * 100
  let totalBrokenPercent: number = (totalBroken / duration) * 100
  let totalMaintenancePercent: number = (totalMaintenance / duration) * 100
  let totalOtherPercent: number = (totalOther / duration) * 100
  let totalTurnedOffPercent: number = Math.round((totalTurnedOff / duration) * 100)

  let totalWorkedTime: string = Duration.fromObject({seconds: totalWorked ? totalWorked : 0}).toFormat("hh:mm:ss")
  let totalStoppedTime: string = Duration.fromObject({seconds: totalStopped ? totalStopped : 0}).toFormat("hh:mm:ss")
  let totalRepairTime: string = Duration.fromObject({seconds: totalRepair ? totalRepair : 0}).toFormat("hh:mm:ss")
  let totalBrokenTime: string = Duration.fromObject({seconds: totalBroken ? totalBroken : 0}).toFormat("hh:mm:ss")
  let totalMaintenanceTime: string = Duration.fromObject({seconds: totalMaintenance ? totalMaintenance : 0}).toFormat("hh:mm:ss")
  let totalOtherTime: string = Duration.fromObject({seconds: totalOther ? totalOther : 0}).toFormat("hh:mm:ss")
  let totalTurnedOffTime: string = Duration.fromObject({seconds: totalTurnedOff ? totalTurnedOff : 0}).toFormat("hh:mm:ss")

  return {
    totalWorked,
    totalStopped,
    totalRepair,
    totalBroken,
    totalMaintenance,
    totalOther,
    totalTurnedOff,
    totalWorkedPercent,
    totalStoppedPercent,
    totalRepairPercent,
    totalBrokenPercent,
    totalMaintenancePercent,
    totalOtherPercent,
    totalTurnedOffPercent,
    totalWorkedTime,
    totalStoppedTime,
    totalRepairTime,
    totalBrokenTime,
    totalMaintenanceTime,
    totalOtherTime,
    totalTurnedOffTime
  }
}



function splitEventsByHours(events: Event[], shiftStart: DateTime, shiftEnd: DateTime, correctMaintenanceLoad: boolean) {
  const start_date = DateTime.fromSQL(String(events[0][3]));
  const end_date = DateTime.fromSQL(String(events[events.length - 1][3]));
  let workedStatuses = correctMaintenanceLoad ? [0,5] : [0]
  let hours = Interval.fromDateTimes(shiftStart, shiftEnd).splitBy({ hour: 1 }).map((date: Interval) => date.start?.hour);
  let workedEvents = events.filter(e => workedStatuses.includes(Number(e[9])) && e[8] !== "turned_off")
  let slicedEvents: Event[] = []
  workedEvents.map(e => splitEventByHour(e, slicedEvents))

  let forChart: (number | string)[][] = []

  hours.map((h, hi) => {
      let hourEvents = slicedEvents.filter(e => 
          DateTime.fromSQL(String(e[3])).hour === h
      )
      if(!hourEvents || hourEvents.length === 0) {
          forChart.push([
              hi,
              0,
              0,
              start_date.hour + h! > 24 ? end_date.set({hour: h}).startOf('hour').toFormat("dd.MM.yyyy HH:mm")! : start_date.set({hour: h}).startOf('hour').toFormat("dd.MM.yyyy HH:mm")!,
              0
          ])
          return
      }

      forChart.push([
          hi,
          0,
          Math.round((_.sum(hourEvents.map(he => he[8] === "worked" && Number(he[7]))) / 3600) * 100),
          DateTime.fromSQL(String(hourEvents[0][1])).set({hour: h}).toFormat("dd.MM.yyyy HH:mm")!,
          Math.round((_.sum(hourEvents.map(he => he[8] === "stopped" && Number(he[7]))) / 3600) * 100)
      ])
  })
  return forChart
} 

function splitEventByHour(event: Event, acc: Event[]) {
  if(DateTime.fromSQL(String(event[3])).hour === DateTime.fromSQL(String(event[5])).hour) {
    let eventWithHour = [event[0], event[1], event[2], event[3], event[4], event[5], event[6], event[7], event[8], event[9], event[10], event[11], event[12], event[13], event[14], event[15], event[16], DateTime.fromSQL(String(event[3])).hour, 0]
    acc.push(eventWithHour)
    return 
  } else {
      let start_dt = DateTime.fromSQL(String(event[3]))
      let finish = start_dt.set({hour: start_dt.hour + 1}).startOf('hour')
      let new_duration = finish.toSeconds() - start_dt.toSeconds()
      let finish_st = finish.toFormat("yyyy-MM-dd HH:mm:ss")
      let finish_ts = Number(event[4]) + new_duration

      let next_start = finish_st
      let next_start_ts = finish_ts
      let next_duration = Number(event[7]) - new_duration

      let next_event_date = DateTime.fromSQL(String(event[3])).day === DateTime.fromSQL(String(event[5])).day ? event[1] : DateTime.fromSQL(String(event[5])).toFormat("yyyy-MM-dd")

      let eventOne = [event[0], event[1], event[2], event[3], event[4], finish_st!, finish_ts!, new_duration!, event[8], event[9], event[10], event[11], event[12], event[13], event[14], event[15], event[16], DateTime.fromSQL(String(event[3])).hour, 0]
      let eventNext = [event[0], next_event_date!, event[2], next_start!, next_start_ts!, event[5], event[6], next_duration, event[8], event[9], event[10], event[11], event[12], event[13], event[14], event[15], event[16]]
      
      acc.push(eventOne)
      splitEventByHour(eventNext, acc)
  }
}

// ПЕРЕРЫВЫ
// Проверка находится ли это событие внцтри перерыва или нет,
// если да то мы возможно его нужно будет обрезать 
// Функция возвращает true если время в диапозоне и false если нет

function isTimeInRange(time: DateTime, startTime: DateTime, endTime: DateTime) {
    // Нормализуем все времена к одной дате (сегодня)
    let baseDate = DateTime.fromObject({year: 1970, month: 1, day: 1})
    
    let timeDt = baseDate.set({hour: time.hour, minute: time.minute, second: time.second});
    let startDt = baseDate.set({hour: startTime.hour, minute: startTime.minute, second: startTime.second});
    let endDt = baseDate.set({hour: endTime.hour, minute: endTime.minute, second: endTime.second});
    
    // Если конечное время меньше начального, значит диапазон переходит через полночь
    if (endDt <= startDt) {
        endDt = endDt.plus({ days: 1 });
        // Если проверяемое время меньше начального, считаем что оно на следующий день
        const adjustedTimeDt = timeDt < startDt ? timeDt.plus({ days: 1 }) : timeDt;
        return adjustedTimeDt >= startDt && adjustedTimeDt <= endDt;
    }
    
    return timeDt >= startDt && timeDt <= endDt;
}

function cutEventByBreaks(event: Event, breaks: NormalizedShiftBreak[], acc: Event[]) {
  let eventStart = DateTime.fromSQL(String(event[3]))
  let eventEnd = DateTime.fromSQL(String(event[5]))

  // Находим все перерывы, которые пересекаются с событием
  const overlappingBreaks = breaks.filter(br => 
    isTimeInRange(eventEnd, br.started_at, br.ended_at) || isTimeInRange(eventStart, br.started_at, br.ended_at)
  );
    
  if (overlappingBreaks.length === 0) {
    // Нет пересекающихся перерывов
    let eventWithFlag =  [event[0], event[1], event[2], event[3], event[4], event[5], event[6], event[7], event[8], event[9], event[10], event[11], event[12], event[13], event[14], event[15], event[16], event[17], 0]
    acc.push(eventWithFlag)
    return
  } else {
    cutEventByBreak(event, overlappingBreaks[0], acc)
  }

}

function cutEventByBreak(event: Event, b: NormalizedShiftBreak, acc: Event[]) {
  let eventStart = DateTime.fromSQL(String(event[3]))
  let eventEnd = DateTime.fromSQL(String(event[5]))

  // Событие в перерыве
  if(isTimeInRange(eventStart, b.started_at, b.ended_at) && isTimeInRange(eventEnd, b.started_at, b.ended_at)) {
    let eventOne = [event[0], event[1], event[2], event[3], event[4], event[5], event[6], event[7], event[8], event[9], event[10], event[11], event[12], event[13], event[14], event[15], event[16], event[17], 1]
    acc.push(eventOne)
    return
  }

  // Начало события в перерыве
  if(isTimeInRange(eventStart, b.started_at, b.ended_at) && !isTimeInRange(eventEnd, b.started_at, b.ended_at)) {
    let baseDate = DateTime.fromObject({year: eventStart.year, month: eventStart.month, day: eventStart.day})
    let endEvent = baseDate.set({hour: b.ended_at.hour, minute: b.ended_at.minute, second: 0})

    let new_duration = eventStart > endEvent ? endEvent.plus({ days: 1 }).toSeconds() - eventStart.toSeconds() : endEvent.toSeconds() - eventStart.toSeconds()
    let finish_st = eventStart > endEvent ? endEvent.plus({ days: 1 }).toFormat("yyyy-MM-dd HH:mm:ss") : endEvent.toFormat("yyyy-MM-dd HH:mm:ss")
    let finish_ts = Number(event[4]) + new_duration

    let next_start = finish_st
    let next_start_ts = finish_ts
    let next_duration = Number(event[7]) - new_duration

    let next_event_date = eventStart > endEvent ? event[1] : endEvent.plus({ days: 1 }).toFormat("yyyy-MM-dd")

    let eventOne = [event[0], event[1], event[2], event[3], event[4], finish_st!, finish_ts!, new_duration!, event[8], event[9], event[10], event[11], event[12], event[13], event[14], event[15], event[16], event[17], 1]
    let eventNext = [event[0], next_event_date!, event[2], next_start!, next_start_ts!, event[5], event[6], next_duration, event[8], event[9], event[10], event[11], event[12], event[13], event[14], event[15], event[16], event[17], 0]
    
    acc.push(eventOne)
    acc.push(eventNext)
    return
  }

  // Конец события в перерыве
  if(!isTimeInRange(eventStart, b.started_at, b.ended_at) && isTimeInRange(eventEnd, b.started_at, b.ended_at)) {
    let baseDate = DateTime.fromObject({year: eventStart.year, month: eventStart.month, day: eventStart.day})
    let breakStart = baseDate.set({hour: b.started_at.hour, minute: b.started_at.minute, second: 0})

    let new_duration = eventStart > breakStart ? breakStart.plus({ days: 1 }).toSeconds() - eventStart.toSeconds() : breakStart.toSeconds() - eventStart.toSeconds()
    let finish_st = eventStart > breakStart ? breakStart.plus({ days: 1 }).toFormat("yyyy-MM-dd HH:mm:ss") : breakStart.toFormat("yyyy-MM-dd HH:mm:ss")
    let finish_ts = Number(event[4]) + new_duration

    let next_start = finish_st
    let next_start_ts = finish_ts
    let next_duration = Number(event[7]) - new_duration

    let next_event_date = eventStart > breakStart ? event[1] : breakStart.plus({ days: 1 }).toFormat("yyyy-MM-dd")

    let eventOne = [event[0], event[1], event[2], event[3], event[4], finish_st!, finish_ts!, new_duration!, event[8], event[9], event[10], event[11], event[12], event[13], event[14], event[15], event[16], event[17], 0]
    let eventNext = [event[0], next_event_date!, event[2], next_start!, next_start_ts!, event[5], event[6], next_duration, event[8], event[9], event[10], event[11], event[12], event[13], event[14], event[15], event[16], event[17], 1]
    
    acc.push(eventOne)
    acc.push(eventNext)
    return
  }
  
  // Перерыв внутри события
  if(isTimeInRange(b.started_at, eventStart, eventEnd) && isTimeInRange(b.ended_at, eventStart, eventEnd)) {
    let baseDate = DateTime.fromObject({year: eventStart.year, month: eventStart.month, day: eventStart.day})
    let breakStart = baseDate.set({hour: b.started_at.hour, minute: b.started_at.minute, second: 0})
    let breakEnd = baseDate.set({hour: b.ended_at.hour, minute: b.ended_at.minute, second: 0})
    

    let new_before_event_duration = eventStart > breakStart ? breakStart.plus({ days: 1 }).toSeconds() - eventStart.toSeconds() : breakStart.toSeconds() - eventStart.toSeconds()
    let new_break_event_duration = breakStart > breakEnd ? breakStart.plus({ days: 1 }).toSeconds() - breakEnd.toSeconds() : breakStart.toSeconds() - breakEnd.toSeconds()
    let new_after_event_duration =  Number(event[7]) - (new_before_event_duration + new_break_event_duration)

    let before_event_finish_st = DateTime.fromSeconds(Number(event[4]) + new_before_event_duration).toFormat("yyyy-MM-dd HH:mm:ss")
    let before_event_finish_ts = Number(event[4]) + new_before_event_duration

    let after_event_start_st = DateTime.fromSeconds(Number(event[4]) + new_before_event_duration + new_break_event_duration).toFormat("yyyy-MM-dd HH:mm:ss")
    let after_event_start_ts = Number(event[4]) + new_before_event_duration + new_break_event_duration

    let break_event_date = DateTime.fromSeconds(Number(event[4]) + new_before_event_duration).toFormat("yyyy-MM-dd")
    let after_event_date = DateTime.fromSeconds(Number(event[4]) + new_before_event_duration + new_break_event_duration).toFormat("yyyy-MM-dd")

    let eventBefore = [event[0], event[1], event[2], event[3], event[4], before_event_finish_st!, before_event_finish_ts!, new_before_event_duration!, event[8], event[9], event[10], event[11], event[12], event[13], event[14], event[15], event[16], event[17], 0]
    let eventBreak = [event[0], break_event_date!, event[2], before_event_finish_st!, before_event_finish_ts!, event[5], event[6], new_break_event_duration, event[8], event[9], event[10], event[11], event[12], event[13], event[14], event[15], event[16], event[17], 1]
    let eventAfter = [event[0], after_event_date!, event[2], after_event_start_st!, after_event_start_ts!, event[5], event[6], new_after_event_duration, event[8], event[9], event[10], event[11], event[12], event[13], event[14], event[15], event[16], event[17], 0]
    
    acc.push(eventBefore)
    acc.push(eventBreak)
    acc.push(eventAfter)

    return
  }
  
}



function extendEquipmentDataByEvents(events: Event[], shiftStart: DateTime, shiftEnd: DateTime, correctMaintenanceLoad: boolean) {
  let workedStatuses = correctMaintenanceLoad ? [0,5] : [0]
  let equipmentWorkedEvents = events.filter(e => workedStatuses.includes(Number(e[9])) && e[8] === "worked" && Number(e[7]) > 2)
  let equipmentStartWork = equipmentWorkedEvents && equipmentWorkedEvents.length ? DateTime.fromSQL(String(equipmentWorkedEvents[0][3])) : null
  let equipmentStartWorkDelta = equipmentStartWork ? equipmentStartWork.diff(shiftStart) : null
  let equipmentEndWork = equipmentWorkedEvents && equipmentWorkedEvents.length ? DateTime.fromSQL(String(equipmentWorkedEvents.reverse()[0][5])) : null
  let equipmentEndWorkDelta = equipmentEndWork ? shiftEnd.diff(equipmentEndWork) : null

  return {
    equipmentStartWork,
    equipmentStartWorkDelta,
    equipmentEndWork,
    equipmentEndWorkDelta
  }
}


function calculateUsage(usage: UsageDataset[], duration: number) {
  let totalWorked: number = Math.round(_.sum(usage.filter(i => i.equipment_item_status === 0).map(i => i.worked)))
  let totalStopped: number = Math.round(_.sum(usage.filter(i => i.equipment_item_status === 0).map(i => i.stopped)))
  let totalRepair: number = Math.round(_.sum(usage.filter(i => i.equipment_item_status === 4).map(i => i.worked + i.stopped)))
  let totalBroken: number = Math.round(_.sum(usage.filter(i => i.equipment_item_status === 1).map(i => i.worked + i.stopped)))
  let totalMaintenance: number = Math.round(_.sum(usage.filter(i => i.equipment_item_status === 5).map(i => i.worked + i.stopped)))
  let totalOther: number = Math.round(_.sum(usage.filter(i => [2,3,6,7,8,9,10].includes(i.equipment_item_status)).map(i => i.worked + i.stopped)))
  let totalTurnedOff: number = Math.round(_.sum(usage.map(i => i.turned_off)))

  let totalWorkedPercent: number = (totalWorked / duration) * 100
  let totalStoppedPercent: number = (totalStopped / duration) * 100
  let totalRepairPercent: number = (totalRepair / duration) * 100
  let totalBrokenPercent: number = (totalBroken / duration) * 100
  let totalMaintenancePercent: number = (totalMaintenance / duration) * 100
  let totalOtherPercent: number = (totalOther / duration) * 100
  let totalTurnedOffPercent: number = Math.round((totalTurnedOff / duration) * 100)

  let totalWorkedTime: string = Duration.fromObject({seconds: totalWorked ? totalWorked : 0}).toFormat("hh:mm:ss")
  let totalStoppedTime: string = Duration.fromObject({seconds: totalStopped ? totalStopped : 0}).toFormat("hh:mm:ss")
  let totalRepairTime: string = Duration.fromObject({seconds: totalRepair ? totalRepair : 0}).toFormat("hh:mm:ss")
  let totalBrokenTime: string = Duration.fromObject({seconds: totalBroken ? totalBroken : 0}).toFormat("hh:mm:ss")
  let totalMaintenanceTime: string = Duration.fromObject({seconds: totalMaintenance ? totalMaintenance : 0}).toFormat("hh:mm:ss")
  let totalOtherTime: string = Duration.fromObject({seconds: totalOther ? totalOther : 0}).toFormat("hh:mm:ss")
  let totalTurnedOffTime: string = Duration.fromObject({seconds: totalTurnedOff ? totalTurnedOff : 0}).toFormat("hh:mm:ss")

  return {
    totalWorked,
    totalStopped,
    totalRepair,
    totalBroken,
    totalMaintenance,
    totalOther,
    totalTurnedOff,
    totalWorkedPercent,
    totalStoppedPercent,
    totalRepairPercent,
    totalBrokenPercent,
    totalMaintenancePercent,
    totalOtherPercent,
    totalTurnedOffPercent,
    totalWorkedTime,
    totalStoppedTime,
    totalRepairTime,
    totalBrokenTime,
    totalMaintenanceTime,
    totalOtherTime,
    totalTurnedOffTime
  }
}


function transformEvent(item: Event, equipment: Equipment):Event {
  let state = getCurrentState(item, equipment)
  return [item[0], item[1], item[2], item[3], item[4], item[5], item[6], item[7], state, item[9], item[10], item[11], item[12], item[13], item[14], item[15], item[16]]
}

function getCurrentState(item: Event, equipment: Equipment):string {
  switch (item[9]) {
      case 0: return String(item[8])
      case 4: return "repair"
      case 5: return equipment?.attributes.correct_maintenance_load ? String(item[8]) === "stopped" ? "maintenance" : String(item[8]) : String(item[8]) === "turned_off" ? "turned_off" : "maintenance"
      case 1: return "broken"
      case 2: 
      case 3: 
      case 6: 
      case 7: 
      case 8: return "other"
      case 9: return "service" 
      case 10: return "turned_off"
      default: return "turned_off"
  }
}

function getCurrentStatus(item: Event[]) {
  return item[item.length - 1][7] === 0 && item[item.length - 1][8] === "turned_off" ? String(getStatusType(item[item.length - 2])) : String(getStatusType(item[item.length - 1]))
}

function getStatusType(event: Event) {
  switch (event[9]) {
      case 0: return event[8]
      case 1: return event[8] === "turned_off" ? "turned_off" : "broken"
      case 4: return event[8] === "turned_off" ? "turned_off" : "repair"
      case 5: return event[8] === "turned_off" ? "turned_off" : "maintenance"
      case 9: return event[8] === "turned_off" ? "turned_off" : "service"
      default: return event[8] === "turned_off" ? "turned_off" : "other"
  }
}

function getCurrentStateColor(state: string) {
  switch (state) {
      case "worked": return hexColor.worked
      case "stopped": return hexColor.stopped
      case "turned_off": return hexColor.turned_off
      case "repair": return hexColor.repair
      case "service": return hexColor.service
      case "broken": return hexColor.broken
      case "maintenance": return hexColor.maintenance
      case "other": return hexColor.other
      default: return hexColor.turned_off
  }
}

// Расчет данных для мониторинга
function calculateDataByEquipment(data: GrouppedData, equipments: Equipment[], dateShift: DateShift, persons: Person[]):EventsCalculation {
  let itemsAvailability: {worked: number, stopped: number, on: number, off: number, interaction: number} = {worked: 0, stopped: 0, on: 0, off: 0, interaction: 0}
  let itemsWorks: {worked: number, stopped: number, on: number, off: number, interaction: number} = {worked: 0, stopped: 0, on: 0, off: 0, interaction: 0}
  let itemsMaintenance: {worked: number, stopped: number, on: number, off: number, interaction: number} = {worked: 0, stopped: 0, on: 0, off: 0, interaction: 0}

  let itemsUnavailability: {worked: number, stopped: number, on: number, off: number, interaction: number} = {worked: 0, stopped: 0, on: 0, off: 0, interaction: 0}
  let itemsRepair: {worked: number, stopped: number, on: number, off: number, interaction: number} = {worked: 0, stopped: 0, on: 0, off: 0, interaction: 0}
  let itemsBroken: {worked: number, stopped: number, on: number, off: number, interaction: number} = {worked: 0, stopped: 0, on: 0, off: 0, interaction: 0}
  let itemsService: {worked: number, stopped: number, on: number, off: number, interaction: number} = {worked: 0, stopped: 0, on: 0, off: 0, interaction: 0}
  let itemsOther:{worked: number, stopped: number, on: number, off: number, interaction: number} = {worked: 0, stopped: 0, on: 0, off: 0, interaction: 0}

  let itemsDuration: number = 0
  let itemsData: EventsCalculationItem[] = []

  Object.entries(data).map(([eid, events]:[string, Event[]]) => {
    let equipment = equipments.find(e => Number(e.id) === Number(eid))!
    let dataRow = calculateEquipmentRow(dateShift.start_day.toSQLDate()!, events, {id: dateShift.id, name: dateShift.label, start_at: dateShift.start_time, end_at: dateShift.end_time} as FShift, equipment)

    itemsAvailability.worked = itemsAvailability.worked + dataRow.availability.worked.seconds
    itemsAvailability.stopped = itemsAvailability.stopped + dataRow.availability.stopped.seconds
    itemsAvailability.on = itemsAvailability.on + dataRow.availability.on.seconds
    itemsAvailability.off = itemsAvailability.off + dataRow.availability.off.seconds
    itemsAvailability.interaction = itemsAvailability.interaction + dataRow.availability.interaction.seconds

    itemsWorks.worked = itemsWorks.worked + dataRow.works.worked.seconds
    itemsWorks.stopped = itemsWorks.stopped + dataRow.works.stopped.seconds
    itemsWorks.on = itemsWorks.on + dataRow.works.on.seconds
    itemsWorks.off = itemsWorks.off + dataRow.works.off.seconds
    itemsWorks.interaction = itemsWorks.interaction + dataRow.works.interaction.seconds

    itemsMaintenance.worked = itemsMaintenance.worked + dataRow.maintenance.worked.seconds
    itemsMaintenance.stopped = itemsMaintenance.stopped + dataRow.maintenance.stopped.seconds
    itemsMaintenance.on = itemsMaintenance.on + dataRow.maintenance.on.seconds
    itemsMaintenance.off = itemsMaintenance.off + dataRow.maintenance.off.seconds
    itemsMaintenance.interaction = itemsMaintenance.interaction + dataRow.maintenance.interaction.seconds

    itemsUnavailability.worked = itemsUnavailability.worked + dataRow.unavailability.worked.seconds
    itemsUnavailability.stopped = itemsUnavailability.stopped + dataRow.unavailability.stopped.seconds
    itemsUnavailability.on = itemsUnavailability.on + dataRow.unavailability.on.seconds
    itemsUnavailability.off = itemsUnavailability.off + dataRow.unavailability.off.seconds
    itemsUnavailability.interaction = itemsUnavailability.interaction + dataRow.unavailability.interaction.seconds

    itemsRepair.worked = itemsRepair.worked + dataRow.repair.worked.seconds
    itemsRepair.stopped = itemsRepair.stopped + dataRow.repair.stopped.seconds
    itemsRepair.on = itemsRepair.on + dataRow.repair.on.seconds
    itemsRepair.off = itemsRepair.off + dataRow.repair.off.seconds
    itemsRepair.interaction = itemsRepair.interaction + dataRow.repair.interaction.seconds

    itemsBroken.worked = itemsBroken.worked + dataRow.broken.worked.seconds
    itemsBroken.stopped = itemsBroken.stopped + dataRow.broken.stopped.seconds
    itemsBroken.on = itemsBroken.on + dataRow.broken.on.seconds
    itemsBroken.off = itemsBroken.off + dataRow.broken.off.seconds
    itemsBroken.interaction = itemsBroken.interaction + dataRow.broken.interaction.seconds

    itemsService.worked = itemsService.worked + dataRow.service.worked.seconds
    itemsService.stopped = itemsService.stopped + dataRow.service.stopped.seconds
    itemsService.on = itemsService.on + dataRow.service.on.seconds
    itemsService.off = itemsService.off + dataRow.service.off.seconds
    itemsService.interaction = itemsService.interaction + dataRow.service.interaction.seconds

    itemsOther.worked = itemsOther.worked + dataRow.other.worked.seconds
    itemsOther.stopped = itemsOther.stopped + dataRow.other.stopped.seconds
    itemsOther.on = itemsOther.on + dataRow.other.on.seconds
    itemsOther.off = itemsOther.off + dataRow.other.off.seconds
    itemsOther.interaction = itemsOther.interaction + dataRow.other.interaction.seconds

    itemsDuration = itemsDuration + dataRow.duration.total.seconds

    itemsData.push(dataRow)
  })

  return {
    itemsAvailability: {
      total: {seconds: itemsAvailability.on + itemsAvailability.off, percent: (itemsAvailability.on + itemsAvailability.off) / itemsDuration, time: Duration.fromObject({seconds: (itemsAvailability.on + itemsAvailability.off) ? (itemsAvailability.on + itemsAvailability.off) : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: itemsAvailability.worked, percent: itemsAvailability.worked / itemsDuration, time: Duration.fromObject({seconds: itemsAvailability.worked ? itemsAvailability.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: itemsAvailability.stopped, percent: itemsAvailability.stopped / itemsDuration, time: Duration.fromObject({seconds: itemsAvailability.stopped ? itemsAvailability.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: itemsAvailability.on, percent: itemsAvailability.on / itemsDuration, time: Duration.fromObject({seconds: itemsAvailability.on ? itemsAvailability.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: itemsAvailability.off, percent: itemsAvailability.off / itemsDuration, time: Duration.fromObject({seconds: itemsAvailability.off ? itemsAvailability.off : 0}).toFormat("hh:mm:ss")},
      interaction: {seconds: itemsAvailability.interaction, percent: itemsAvailability.interaction / itemsDuration, time: Duration.fromObject({seconds: itemsAvailability.interaction ? itemsAvailability.interaction : 0}).toFormat("hh:mm:ss")}
    },
    itemsWorks: {
      total: {seconds: itemsWorks.on + itemsWorks.off, percent: (itemsWorks.on + itemsWorks.off) / itemsDuration, time: Duration.fromObject({seconds: (itemsWorks.on + itemsWorks.off) ? (itemsWorks.on + itemsWorks.off) : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: itemsWorks.worked, percent: itemsWorks.worked / itemsDuration, time: Duration.fromObject({seconds: itemsWorks.worked ? itemsWorks.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: itemsWorks.stopped, percent: itemsWorks.stopped / itemsDuration, time: Duration.fromObject({seconds: itemsWorks.stopped ? itemsWorks.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: itemsWorks.on, percent: itemsWorks.on / itemsDuration, time: Duration.fromObject({seconds: itemsWorks.on ? itemsWorks.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: itemsWorks.off, percent: itemsWorks.off / itemsDuration, time: Duration.fromObject({seconds: itemsWorks.off ? itemsWorks.off : 0}).toFormat("hh:mm:ss")},
      interaction: {seconds: itemsWorks.interaction, percent: itemsWorks.interaction / itemsDuration, time: Duration.fromObject({seconds: itemsWorks.interaction ? itemsWorks.interaction : 0}).toFormat("hh:mm:ss")}
    },
    itemsMaintenance: {
      total: {seconds: itemsMaintenance.on + itemsMaintenance.off, percent: (itemsMaintenance.on + itemsMaintenance.off) / itemsDuration, time: Duration.fromObject({seconds: (itemsMaintenance.on + itemsMaintenance.off) ? (itemsMaintenance.on + itemsMaintenance.off) : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: itemsMaintenance.worked, percent: itemsMaintenance.worked / itemsDuration, time: Duration.fromObject({seconds: itemsMaintenance.worked ? itemsMaintenance.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: itemsMaintenance.stopped, percent: itemsMaintenance.stopped / itemsDuration, time: Duration.fromObject({seconds: itemsMaintenance.stopped ? itemsMaintenance.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: itemsMaintenance.on, percent: itemsMaintenance.on / itemsDuration, time: Duration.fromObject({seconds: itemsMaintenance.on ? itemsMaintenance.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: itemsMaintenance.off, percent: itemsMaintenance.off / itemsDuration, time: Duration.fromObject({seconds: itemsMaintenance.off ? itemsMaintenance.off : 0}).toFormat("hh:mm:ss")},
      interaction: {seconds: itemsMaintenance.interaction, percent: itemsMaintenance.interaction / itemsDuration, time: Duration.fromObject({seconds: itemsMaintenance.interaction ? itemsMaintenance.interaction : 0}).toFormat("hh:mm:ss")}
    },
    itemsUnavailability: {
      total: {seconds: itemsUnavailability.on + itemsUnavailability.off, percent: (itemsUnavailability.on + itemsUnavailability.off) / itemsDuration, time: Duration.fromObject({seconds: (itemsUnavailability.on + itemsUnavailability.off) ? (itemsUnavailability.on + itemsUnavailability.off) : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: itemsUnavailability.worked, percent: itemsUnavailability.worked / itemsDuration, time: Duration.fromObject({seconds: itemsUnavailability.worked ? itemsUnavailability.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: itemsUnavailability.stopped, percent: itemsUnavailability.stopped / itemsDuration, time: Duration.fromObject({seconds: itemsUnavailability.stopped ? itemsUnavailability.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: itemsUnavailability.on, percent: itemsUnavailability.on / itemsDuration, time: Duration.fromObject({seconds: itemsUnavailability.on ? itemsUnavailability.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: itemsUnavailability.off, percent: itemsUnavailability.off / itemsDuration, time: Duration.fromObject({seconds: itemsUnavailability.off ? itemsUnavailability.off : 0}).toFormat("hh:mm:ss")},
      interaction: {seconds: itemsUnavailability.interaction, percent: itemsUnavailability.interaction / itemsDuration, time: Duration.fromObject({seconds: itemsUnavailability.interaction ? itemsUnavailability.interaction : 0}).toFormat("hh:mm:ss")}
    },
    itemsBroken: {
      total: {seconds: itemsBroken.on + itemsBroken.off, percent: (itemsBroken.on + itemsBroken.off) / itemsDuration, time: Duration.fromObject({seconds: (itemsBroken.on + itemsBroken.off) ? (itemsBroken.on + itemsBroken.off) : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: itemsBroken.worked, percent: itemsBroken.worked / itemsDuration, time: Duration.fromObject({seconds: itemsBroken.worked ? itemsBroken.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: itemsBroken.stopped, percent: itemsBroken.stopped / itemsDuration, time: Duration.fromObject({seconds: itemsBroken.stopped ? itemsBroken.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: itemsBroken.on, percent: itemsBroken.on / itemsDuration, time: Duration.fromObject({seconds: itemsBroken.on ? itemsBroken.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: itemsBroken.off, percent: itemsBroken.off / itemsDuration, time: Duration.fromObject({seconds: itemsBroken.off ? itemsBroken.off : 0}).toFormat("hh:mm:ss")},
      interaction: {seconds: itemsBroken.interaction, percent: itemsBroken.interaction / itemsDuration, time: Duration.fromObject({seconds: itemsBroken.interaction ? itemsBroken.interaction : 0}).toFormat("hh:mm:ss")}
    },
    itemsRepair: {
      total: {seconds: itemsRepair.on + itemsRepair.off, percent: (itemsRepair.on + itemsRepair.off) / itemsDuration, time: Duration.fromObject({seconds: (itemsRepair.on + itemsRepair.off) ? (itemsRepair.on + itemsRepair.off) : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: itemsRepair.worked, percent: itemsRepair.worked / itemsDuration, time: Duration.fromObject({seconds: itemsRepair.worked ? itemsRepair.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: itemsRepair.stopped, percent: itemsRepair.stopped / itemsDuration, time: Duration.fromObject({seconds: itemsRepair.stopped ? itemsRepair.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: itemsRepair.on, percent: itemsRepair.on / itemsDuration, time: Duration.fromObject({seconds: itemsRepair.on ? itemsRepair.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: itemsRepair.off, percent: itemsRepair.off / itemsDuration, time: Duration.fromObject({seconds: itemsRepair.off ? itemsRepair.off : 0}).toFormat("hh:mm:ss")},
      interaction: {seconds: itemsRepair.interaction, percent: itemsRepair.interaction / itemsDuration, time: Duration.fromObject({seconds: itemsRepair.interaction ? itemsRepair.interaction : 0}).toFormat("hh:mm:ss")}
    },
    itemsService: {
      total: {seconds: itemsService.on + itemsService.off, percent: (itemsService.on + itemsService.off) / itemsDuration, time: Duration.fromObject({seconds: (itemsService.on + itemsService.off) ? (itemsService.on + itemsService.off) : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: itemsService.worked, percent: itemsService.worked / itemsDuration, time: Duration.fromObject({seconds: itemsService.worked ? itemsService.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: itemsService.stopped, percent: itemsService.stopped / itemsDuration, time: Duration.fromObject({seconds: itemsService.stopped ? itemsService.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: itemsService.on, percent: itemsService.on / itemsDuration, time: Duration.fromObject({seconds: itemsService.on ? itemsService.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: itemsService.off, percent: itemsService.off / itemsDuration, time: Duration.fromObject({seconds: itemsService.off ? itemsService.off : 0}).toFormat("hh:mm:ss")},
      interaction: {seconds: itemsService.interaction, percent: itemsService.interaction / itemsDuration, time: Duration.fromObject({seconds: itemsService.interaction ? itemsService.interaction : 0}).toFormat("hh:mm:ss")}
    },
    itemsOther: {
      total: {seconds: itemsOther.on + itemsOther.off, percent: (itemsOther.on + itemsOther.off) / itemsDuration, time: Duration.fromObject({seconds: (itemsOther.on + itemsOther.off) ? (itemsOther.on + itemsOther.off) : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: itemsOther.worked, percent: itemsOther.worked / itemsDuration, time: Duration.fromObject({seconds: itemsOther.worked ? itemsOther.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: itemsOther.stopped, percent: itemsOther.stopped / itemsDuration, time: Duration.fromObject({seconds: itemsOther.stopped ? itemsOther.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: itemsOther.on, percent: itemsOther.on / itemsDuration, time: Duration.fromObject({seconds: itemsOther.on ? itemsOther.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: itemsOther.off, percent: itemsOther.off / itemsDuration, time: Duration.fromObject({seconds: itemsOther.off ? itemsOther.off : 0}).toFormat("hh:mm:ss")},
      interaction: {seconds: itemsOther.interaction, percent: itemsOther.interaction / itemsDuration, time: Duration.fromObject({seconds: itemsOther.interaction ? itemsOther.interaction : 0}).toFormat("hh:mm:ss")}
    },
    itemsDuration: {
      total: {seconds: itemsDuration, percent: 1, time: Duration.fromObject({seconds: itemsDuration ? itemsDuration : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: itemsAvailability.worked + itemsUnavailability.worked, percent: (itemsAvailability.worked + itemsUnavailability.worked) / itemsDuration, time: Duration.fromObject({seconds: (itemsAvailability.worked + itemsUnavailability.worked)}).toFormat("hh:mm:ss")},
      stopped: {seconds: itemsAvailability.stopped + itemsUnavailability.stopped, percent: (itemsAvailability.stopped + itemsUnavailability.stopped) / itemsDuration, time: Duration.fromObject({seconds: (itemsAvailability.stopped + itemsUnavailability.stopped)}).toFormat("hh:mm:ss")},
      on: {seconds: itemsAvailability.on + itemsUnavailability.on, percent: (itemsAvailability.on + itemsUnavailability.on) / itemsDuration, time: Duration.fromObject({seconds: (itemsAvailability.on + itemsUnavailability.on)}).toFormat("hh:mm:ss")},
      off: {seconds: itemsAvailability.off + itemsUnavailability.off, percent: (itemsAvailability.off + itemsUnavailability.off) / itemsDuration, time: Duration.fromObject({seconds: (itemsAvailability.off + itemsUnavailability.off)}).toFormat("hh:mm:ss")},
      interaction: {seconds: itemsAvailability.interaction + itemsUnavailability.interaction, percent: (itemsAvailability.interaction + itemsUnavailability.interaction) / itemsDuration, time: Duration.fromObject({seconds: (itemsAvailability.interaction + itemsUnavailability.interaction)}).toFormat("hh:mm:ss")}
    },
    itemsData: itemsData
  }
}

// Расчет строк для отчета по оборудованию
function calculateEquipmentRow(day: string, events: Event[], shift: FShift, equipment: Equipment) {
  let dateShift = convertFshiftsToDateShifts([shift], DateTime.fromSQL(day))[0]
  let interactionIds: number[] = []
  let itemDuration: number = 0
  let transformedEvents: Event[] = []

  let breaks: any[]
  if(shift.working_shift_breaks && shift.working_shift_breaks.length > 0)
    breaks = shift.working_shift_breaks.map(b => {
      let start = DateTime.fromISO(b.started_at)
      let end =  DateTime.fromISO(b.ended_at)
      return {id: b.id, started_at: start, ended_at: end} as NormalizedShiftBreak
    }).sort((a, b) => 
      a.started_at.valueOf() - b.started_at.valueOf()
    )
  else {
    breaks = []
  }

  let has_breaks = breaks.length > 0

  let itemAvailability: {worked: number, stopped: number, on: number, off: number, interaction: number} = {worked: 0, stopped: 0, on: 0, off: 0, interaction: 0}
  let itemWorks: {worked: number, stopped: number, on: number, off: number, interaction: number} = {worked: 0, stopped: 0, on: 0, off: 0, interaction: 0}
  let itemMaintenance: {worked: number, stopped: number, on: number, off: number, interaction: number} = {worked: 0, stopped: 0, on: 0, off: 0, interaction: 0}

  let itemUnavailability: {worked: number, stopped: number, on: number, off: number, interaction: number} = {worked: 0, stopped: 0, on: 0, off: 0, interaction: 0}
  let itemRepair: {worked: number, stopped: number, on: number, off: number, interaction: number} = {worked: 0, stopped: 0, on: 0, off: 0, interaction: 0}
  let itemBroken: {worked: number, stopped: number, on: number, off: number, interaction: number} = {worked: 0, stopped: 0, on: 0, off: 0, interaction: 0}
  let itemService: {worked: number, stopped: number, on: number, off: number, interaction: number} = {worked: 0, stopped: 0, on: 0, off: 0, interaction: 0}
  let itemOther: {worked: number, stopped: number, on: number, off: number, interaction: number} = {worked: 0, stopped: 0, on: 0, off: 0, interaction: 0}

  events.map((e: Event) => {
    interactionIds.push(Number(e[10]))

    itemWorks.worked = itemWorks.worked + (e[8] === "worked" && Number(e[9]) === 0 ? Number(e[7]) : 0)
    itemWorks.stopped = itemWorks.stopped + ((e[8] === "stopped" && Number(e[9]) === 0) ? Number(e[7]) : 0)
    itemWorks.on = itemWorks.on + ((e[8] !== "turned_off" && Number(e[9]) === 0) ? Number(e[7]) : 0)
    itemWorks.off = itemWorks.off + ((e[8] === "turned_off" && Number(e[9]) === 0) ? Number(e[7]) : 0)
    itemWorks.interaction = itemWorks.interaction + (Number(e[9]) === 0 && Number(e[10]) > 0 ? Number(e[7]) : 0)

    itemMaintenance.worked = itemMaintenance.worked + (e[8] === "worked" && Number(e[9]) === 5 ? Number(e[7]) : 0)
    itemMaintenance.stopped = itemMaintenance.stopped + (e[8] === "stopped" && Number(e[9]) === 5 ? Number(e[7]) : 0)
    itemMaintenance.on = itemMaintenance.on + (e[8] !== "turned_off" && Number(e[9]) === 5 ? Number(e[7]) : 0)
    itemMaintenance.off = itemMaintenance.off + (e[8] === "turned_off" && Number(e[9]) === 5 ? Number(e[7]) : 0)
    itemMaintenance.interaction = itemMaintenance.interaction + (Number(e[9]) === 5 && Number(e[10]) > 0 ? Number(e[7]) : 0)

    itemAvailability.worked = itemAvailability.worked + (Number(e[9]) === 0 && e[8] === "worked" ? Number(e[7]) : 0) + (Number(e[9]) === 5 && e[8] === "worked" ? equipment?.attributes.correct_maintenance_load ? Number(e[7]) : 0 : 0)
    itemAvailability.stopped = itemAvailability.stopped + (Number(e[9]) === 0 && e[8] === "stopped" ? Number(e[7]) : 0) + (Number(e[9]) === 5 ? equipment?.attributes.correct_maintenance_load ? e[8] === "stopped" ? Number(e[7]) : 0 : e[8] !== "turned_off" ? Number(e[7]) : 0 : 0)
    itemAvailability.on = itemAvailability.on + ([0,5].includes(Number(e[9]))  && e[8] !== "turned_off" ? Number(e[7]) : 0)
    itemAvailability.off = itemAvailability.off + ([0,5].includes(Number(e[9])) && e[8] === "turned_off" ? Number(e[7]) : 0)
    itemAvailability.interaction = itemAvailability.interaction + ([0,5].includes(Number(e[9])) && Number(e[10]) > 0 ? Number(e[7]) : 0)

    itemBroken.worked = itemBroken.worked + (Number(e[9]) === 1 && e[8] === "worked" ? Number(e[7]) : 0)
    itemBroken.stopped = itemBroken.stopped + (Number(e[9]) === 1 && e[8] === "stopped"  ? Number(e[7]) : 0)
    itemBroken.on = itemBroken.on + (Number(e[9]) === 1 && e[8] !== "turned_off"  ? Number(e[7]) : 0)
    itemBroken.off = itemBroken.off + (Number(e[9]) === 1 && e[8] === "turned_off"  ? Number(e[7]) : 0)
    itemBroken.interaction = itemBroken.interaction + (Number(e[9]) === 1 && Number(e[10]) > 0 ? Number(e[7]) : 0)

    itemRepair.worked = itemRepair.worked + (Number(e[9]) === 4 && e[8] === "worked" ? Number(e[7]) : 0)
    itemRepair.stopped = itemRepair.stopped + (Number(e[9]) === 4 && e[8] === "stopped"  ? Number(e[7]) : 0)
    itemRepair.on = itemRepair.on + (Number(e[9]) === 4 && e[8] !== "turned_off"  ? Number(e[7]) : 0)
    itemRepair.off = itemRepair.off + (Number(e[9]) === 4 && e[8] === "turned_off"  ? Number(e[7]) : 0)
    itemRepair.interaction = itemRepair.interaction + (Number(e[9]) === 4 && Number(e[10]) > 0 ? Number(e[7]) : 0)

    itemService.worked = itemService.worked + (Number(e[9]) === 9 && e[8] === "worked" ? Number(e[7]) : 0)
    itemService.stopped = itemService.stopped + (Number(e[9]) === 9 && e[8] === "stopped"  ? Number(e[7]) : 0)
    itemService.on = itemService.on + (Number(e[9]) === 9 && e[8] !== "turned_off"  ? Number(e[7]) : 0)
    itemService.off = itemService.off + (Number(e[9]) === 9 && e[8] === "turned_off"  ? Number(e[7]) : 0)
    itemService.interaction = itemService.interaction + (Number(e[9]) === 9 && Number(e[10]) > 0  ? Number(e[7]) : 0)

    itemOther.worked = itemOther.worked + ([2,3,6,7,8,10].includes(Number(e[9])) && e[8] === "worked" ? Number(e[7]) : 0)
    itemOther.stopped = itemOther.stopped + ([2,3,6,7,8,10].includes(Number(e[9])) && e[8] === "stopped"  ? Number(e[7]) : 0)
    itemOther.on = itemOther.on + ([2,3,6,7,8,10].includes(Number(e[9])) && e[8] !== "turned_off"  ? Number(e[7]) : 0)
    itemOther.off = itemOther.off + ([2,3,6,7,8,10].includes(Number(e[9])) && e[8] === "turned_off"  ? Number(e[7]) : 0)
    itemOther.interaction = itemOther.interaction + ([2,3,5,7,8,10].includes(Number(e[9])) && Number(e[10]) > 0 ? Number(e[7]) : 0)

    itemUnavailability.worked = itemUnavailability.worked + ([1,2,3,4,6,7,8,9,10].includes(Number(e[9])) && e[8] === "worked" ? Number(e[7]) : 0)
    itemUnavailability.stopped = itemUnavailability.stopped + ([1,2,3,4,6,7,8,9,10].includes(Number(e[9])) && e[8] === "stopped" ? Number(e[7]) : 0)
    itemUnavailability.on = itemUnavailability.on + ([1,2,3,4,6,7,8,9,10].includes(Number(e[9])) && e[8] !== "turned_off" ? Number(e[7]) : 0)
    itemUnavailability.off = itemUnavailability.off + ([1,2,3,4,6,7,8,9,10].includes(Number(e[9])) && e[8] === "turned_off" ? Number(e[7]) : 0)
    itemUnavailability.interaction = itemUnavailability.interaction + ([1,2,3,4,6,7,8,9,10].includes(Number(e[9])) && Number(e[10]) > 0 ? Number(e[7]) : 0)

    itemDuration = equipment?.attributes.is_main ? dateShift.duration : itemDuration + ((e[8] !== "turned_off" && Number(e[9]) === 0) || (equipment?.attributes.correct_maintenance_load && e[8] !== "turned_off" && Number(e[9]) === 5) ? Number(e[7]) : 0)

    let trasnformedEvent = transformEvent(e, equipment!)
    let splitedEventByHour: Event[] = []
    splitEventByHour(trasnformedEvent, splitedEventByHour)
    // После разбивки на часы маркируем перерывы
    let preparedEvents: Event[] = []
    has_breaks ? splitedEventByHour.map(ev => cutEventByBreaks(ev, breaks, preparedEvents)) : preparedEvents = splitedEventByHour
    transformedEvents = [...transformedEvents, ...preparedEvents]
  })

  let workedStatuses = equipment.attributes.correct_maintenance_load ? [0,5] : [0]
  let activeEvents = events.filter((e: Event) => e[8] !== "turned_off" && [0,5].includes(Number(e[9])))
  let activeEventsInteraction = activeEvents.filter((e: Event) => Number(e[10]) > 0)
  let activeEventsWork = activeEvents.filter((e: Event) => e[8] === "worked" && workedStatuses.includes(Number(e[9])) && Number(e[7]) > (equipment.attributes.stop_threshold ? equipment.attributes.stop_threshold : 0))

  let violations = {
    start: {
      eventTime: activeEvents[0] ? String(activeEvents[0][3]) : null, 
      seconds: equipment.attributes.is_main && activeEvents[0] ? Number(activeEvents[0][4]) - dateShift.start_time.toSeconds() : 0, 
      time: equipment.attributes.is_main && activeEvents[0] ? Duration.fromObject({seconds: Number(activeEvents[0][4]) - dateShift.start_time.toSeconds()}).toFormat("hh:mm:ss") : "00:00:00", 
      percent: equipment.attributes.is_main && activeEvents[0] ? (Number(activeEvents[0][4]) - dateShift.start_time.toSeconds()) / dateShift.duration : 0
    },
    startInteraction: {
      eventTime: activeEventsInteraction[0] ? String(activeEventsInteraction[0][3]) : null, 
      seconds: equipment.attributes.is_main && activeEventsInteraction[0] ? Number(activeEventsInteraction[0][4]) - dateShift.start_time.toSeconds() : 0, 
      time: equipment.attributes.is_main && activeEventsInteraction[0] ? Duration.fromObject({seconds: Number(activeEventsInteraction[0][4]) -  dateShift.start_time.toSeconds()}).toFormat("hh:mm:ss") : "00:00:00", 
      percent: equipment.attributes.is_main && activeEventsInteraction[0] ? (Number(activeEventsInteraction[0][4]) - dateShift.start_time.toSeconds()) / dateShift.duration : 0
    },
    startWork: {
      eventTime: activeEventsWork[0] ? String(activeEventsWork[0][3]) : null, 
      seconds: equipment.attributes.is_main && activeEventsWork[0] ? Number(activeEventsWork[0][4]) - dateShift.start_time.toSeconds() : 0, 
      time: equipment.attributes.is_main && activeEventsWork[0] ? Duration.fromObject({seconds: Number(activeEventsWork[0][4]) - dateShift.start_time.toSeconds()}).toFormat("hh:mm:ss") : "00:00:00", 
      percent: equipment.attributes.is_main && activeEventsWork[0] ? (Number(activeEventsWork[0][4]) - dateShift.start_time.toSeconds()) / dateShift.duration : 0
    },
    end: {
      eventTime: activeEvents[activeEvents.length - 1] ? String(activeEvents[activeEvents.length - 1][5]) : null, 
      seconds: equipment.attributes.is_main && activeEvents[activeEvents.length - 1] ? dateShift.end_time.toSeconds() - Number(activeEvents[activeEvents.length - 1][6]) : 0, 
      time: equipment.attributes.is_main &&  activeEvents[activeEvents.length - 1] ? Duration.fromObject({seconds: dateShift.end_time.toSeconds() - Number(activeEvents[activeEvents.length - 1][6])}).toFormat("hh:mm:ss") : "00:00:00", 
      percent: equipment.attributes.is_main &&  activeEvents[activeEvents.length - 1] ? (dateShift.end_time.toSeconds() - Number(activeEvents[activeEvents.length - 1][6])) / dateShift.duration : 0
    },
    endInteraction: {
      eventTime: activeEventsInteraction[activeEventsInteraction.length - 1] ? String(activeEventsInteraction[activeEventsInteraction.length - 1][5]) : null, 
      seconds: equipment.attributes.is_main && activeEventsInteraction[activeEventsInteraction.length - 1] ? dateShift.end_time.toSeconds() - Number(activeEventsInteraction[activeEventsInteraction.length - 1][6]) : 0, 
      time: equipment.attributes.is_main && activeEventsInteraction[activeEventsInteraction.length - 1] ? Duration.fromObject({seconds: dateShift.end_time.toSeconds() - Number(activeEventsInteraction[activeEventsInteraction.length - 1][6])}).toFormat("hh:mm:ss") : "00:00:00", 
      percent: equipment.attributes.is_main && activeEventsInteraction[activeEventsInteraction.length - 1] ? (dateShift.end_time.toSeconds() - Number(activeEventsInteraction[activeEventsInteraction.length - 1][6])) / dateShift.duration : 0
    },
    endWork: {
      eventTime: activeEventsWork[activeEventsWork.length - 1] ? String(activeEventsWork[activeEventsWork.length - 1][5]) : null, 
      seconds: equipment.attributes.is_main && activeEventsWork[activeEventsWork.length - 1] ? dateShift.end_time.toSeconds() - Number(activeEventsWork[activeEventsWork.length - 1][6]) : 0, 
      time: equipment.attributes.is_main && activeEventsWork[activeEventsWork.length - 1] ? Duration.fromObject({seconds: dateShift.end_time.toSeconds() - Number(activeEventsWork[activeEventsWork.length - 1][6])}).toFormat("hh:mm:ss") : "00:00:00", 
      percent: equipment.attributes.is_main && activeEventsWork[activeEventsWork.length - 1] ? (dateShift.end_time.toSeconds() - Number(activeEventsWork[activeEventsWork.length - 1][6])) / dateShift.duration : 0
    }
  }

  interactionIds = _.uniq(interactionIds)

  return {
    dateShift: dateShift,
    equipment: equipment!,
    interactionIds: interactionIds,
    availability: {
      total: {seconds: itemAvailability.on + itemAvailability.off, percent: (itemAvailability.on + itemAvailability.off) / itemDuration, time: Duration.fromObject({seconds: (itemAvailability.on + itemAvailability.off) ? (itemAvailability.on + itemAvailability.off) : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: itemAvailability.worked, percent: itemAvailability.worked / itemDuration, time: Duration.fromObject({seconds: itemAvailability.worked ? itemAvailability.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: itemAvailability.stopped, percent: itemAvailability.stopped / itemDuration, time: Duration.fromObject({seconds: itemAvailability.stopped ? itemAvailability.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: itemAvailability.on, percent: itemAvailability.on / itemDuration, time: Duration.fromObject({seconds: itemAvailability.on ? itemAvailability.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: itemAvailability.off, percent: itemAvailability.off / itemDuration, time: Duration.fromObject({seconds: itemAvailability.off ? itemAvailability.off : 0}).toFormat("hh:mm:ss")},
      interaction: {seconds: itemAvailability.interaction, percent: itemAvailability.interaction / itemDuration, time: Duration.fromObject({seconds: itemAvailability.interaction ? itemAvailability.interaction : 0}).toFormat("hh:mm:ss")}
    },
    works: {
      total: {seconds: itemWorks.on + itemWorks.off, percent: (itemWorks.on + itemWorks.off) / itemDuration, time: Duration.fromObject({seconds: (itemWorks.on + itemWorks.off) ? (itemWorks.on + itemWorks.off) : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: itemWorks.worked, percent: itemWorks.worked / itemDuration, time: Duration.fromObject({seconds: itemWorks.worked ? itemWorks.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: itemWorks.stopped, percent: itemWorks.stopped / itemDuration, time: Duration.fromObject({seconds: itemWorks.stopped ? itemWorks.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: itemWorks.on, percent: itemWorks.on / itemDuration, time: Duration.fromObject({seconds: itemWorks.on ? itemWorks.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: itemWorks.off, percent: itemWorks.off / itemDuration, time: Duration.fromObject({seconds: itemWorks.off ? itemWorks.off : 0}).toFormat("hh:mm:ss")},
      interaction: {seconds: itemWorks.interaction, percent: itemWorks.interaction / itemDuration, time: Duration.fromObject({seconds: itemWorks.interaction ? itemWorks.interaction : 0}).toFormat("hh:mm:ss")}
    },
    maintenance: {
      total: {seconds: itemMaintenance.on + itemMaintenance.off, percent: (itemMaintenance.on + itemMaintenance.off) / itemDuration, time: Duration.fromObject({seconds: (itemMaintenance.on + itemMaintenance.off) ? (itemMaintenance.on + itemMaintenance.off) : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: itemMaintenance.worked, percent: itemMaintenance.worked / itemDuration, time: Duration.fromObject({seconds: itemMaintenance.worked ? itemMaintenance.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: itemMaintenance.stopped, percent: itemMaintenance.stopped / itemDuration, time: Duration.fromObject({seconds: itemMaintenance.stopped ? itemMaintenance.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: itemMaintenance.on, percent: itemMaintenance.on / itemDuration, time: Duration.fromObject({seconds: itemMaintenance.on ? itemMaintenance.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: itemMaintenance.off, percent: itemMaintenance.off / itemDuration, time: Duration.fromObject({seconds: itemMaintenance.off ? itemMaintenance.off : 0}).toFormat("hh:mm:ss")},
      interaction: {seconds: itemMaintenance.interaction, percent: itemMaintenance.interaction / itemDuration, time: Duration.fromObject({seconds: itemMaintenance.interaction ? itemMaintenance.interaction : 0}).toFormat("hh:mm:ss")}
    },
    unavailability: {
      total: {seconds: itemUnavailability.on + itemUnavailability.off, percent: (itemUnavailability.on + itemUnavailability.off) / itemDuration, time: Duration.fromObject({seconds: (itemUnavailability.on + itemUnavailability.off) ? (itemUnavailability.on + itemUnavailability.off) : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: itemUnavailability.worked, percent: itemUnavailability.worked / itemDuration, time: Duration.fromObject({seconds: itemUnavailability.worked ? itemUnavailability.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: itemUnavailability.stopped, percent: itemUnavailability.stopped / itemDuration, time: Duration.fromObject({seconds: itemUnavailability.stopped ? itemUnavailability.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: itemUnavailability.on, percent: itemUnavailability.on / itemDuration, time: Duration.fromObject({seconds: itemUnavailability.on ? itemUnavailability.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: itemUnavailability.off, percent: itemUnavailability.off / itemDuration, time: Duration.fromObject({seconds: itemUnavailability.off ? itemUnavailability.off : 0}).toFormat("hh:mm:ss")},
      interaction: {seconds: itemUnavailability.interaction, percent: itemUnavailability.interaction / itemDuration, time: Duration.fromObject({seconds: itemUnavailability.interaction ? itemUnavailability.interaction : 0}).toFormat("hh:mm:ss")}
    },
    broken: {
      total: {seconds: itemBroken.on + itemBroken.off, percent: (itemBroken.on + itemBroken.off) / itemDuration, time: Duration.fromObject({seconds: (itemBroken.on + itemBroken.off) ? (itemBroken.on + itemBroken.off) : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: itemBroken.worked, percent: itemBroken.worked / itemDuration, time: Duration.fromObject({seconds: itemBroken.worked ? itemBroken.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: itemBroken.stopped, percent: itemBroken.stopped / itemDuration, time: Duration.fromObject({seconds: itemBroken.stopped ? itemBroken.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: itemBroken.on, percent: itemBroken.on / itemDuration, time: Duration.fromObject({seconds: itemBroken.on ? itemBroken.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: itemBroken.off, percent: itemBroken.off / itemDuration, time: Duration.fromObject({seconds: itemBroken.off ? itemBroken.off : 0}).toFormat("hh:mm:ss")},
      interaction: {seconds: itemBroken.interaction, percent: itemBroken.interaction / itemDuration, time: Duration.fromObject({seconds: itemBroken.interaction ? itemBroken.interaction : 0}).toFormat("hh:mm:ss")}
    },
    repair: {
      total: {seconds: itemRepair.on + itemRepair.off, percent: (itemRepair.on + itemRepair.off) / itemDuration, time: Duration.fromObject({seconds: (itemRepair.on + itemRepair.off) ? (itemRepair.on + itemRepair.off) : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: itemRepair.worked, percent: itemRepair.worked / itemDuration, time: Duration.fromObject({seconds: itemRepair.worked ? itemRepair.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: itemRepair.stopped, percent: itemRepair.stopped / itemDuration, time: Duration.fromObject({seconds: itemRepair.stopped ? itemRepair.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: itemRepair.on, percent: itemRepair.on / itemDuration, time: Duration.fromObject({seconds: itemRepair.on ? itemRepair.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: itemRepair.off, percent: itemRepair.off / itemDuration, time: Duration.fromObject({seconds: itemRepair.off ? itemRepair.off : 0}).toFormat("hh:mm:ss")},
      interaction: {seconds: itemRepair.interaction, percent: itemRepair.interaction / itemDuration, time: Duration.fromObject({seconds: itemRepair.interaction ? itemRepair.interaction : 0}).toFormat("hh:mm:ss")}
    },
    service: {
      total: {seconds: itemService.on + itemService.off, percent: (itemService.on + itemService.off) / itemDuration, time: Duration.fromObject({seconds: (itemService.on + itemService.off) ? (itemService.on + itemService.off) : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: itemService.worked, percent: itemService.worked / itemDuration, time: Duration.fromObject({seconds: itemService.worked ? itemService.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: itemService.stopped, percent: itemService.stopped / itemDuration, time: Duration.fromObject({seconds: itemService.stopped ? itemService.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: itemService.on, percent: itemService.on / itemDuration, time: Duration.fromObject({seconds: itemService.on ? itemService.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: itemService.off, percent: itemService.off / itemDuration, time: Duration.fromObject({seconds: itemService.off ? itemService.off : 0}).toFormat("hh:mm:ss")},
      interaction: {seconds: itemService.interaction, percent: itemService.interaction / itemDuration, time: Duration.fromObject({seconds: itemService.interaction ? itemService.interaction : 0}).toFormat("hh:mm:ss")}
    },
    other: {
      total: {seconds: itemOther.on + itemOther.off, percent: (itemOther.on + itemOther.off) / itemDuration, time: Duration.fromObject({seconds: (itemOther.on + itemOther.off) ? (itemOther.on + itemOther.off) : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: itemOther.worked, percent: itemOther.worked / itemDuration, time: Duration.fromObject({seconds: itemOther.worked ? itemOther.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: itemOther.stopped, percent: itemOther.stopped / itemDuration, time: Duration.fromObject({seconds: itemOther.stopped ? itemOther.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: itemOther.on, percent: itemOther.on / itemDuration, time: Duration.fromObject({seconds: itemOther.on ? itemOther.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: itemOther.off, percent: itemOther.off / itemDuration, time: Duration.fromObject({seconds: itemOther.off ? itemOther.off : 0}).toFormat("hh:mm:ss")},
      interaction: {seconds: itemOther.interaction, percent: itemOther.interaction / itemDuration, time: Duration.fromObject({seconds: itemOther.interaction ? itemOther.interaction : 0}).toFormat("hh:mm:ss")}
    },
    duration: {
      total: {seconds: itemDuration, percent: 1, time: Duration.fromObject({seconds: itemDuration ? itemDuration : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: itemAvailability.worked + itemUnavailability.worked, percent: (itemAvailability.worked + itemUnavailability.worked) / itemDuration, time: Duration.fromObject({seconds: itemAvailability.worked + itemUnavailability.worked}).toFormat("hh:mm:ss")},
      stopped: {seconds: itemAvailability.stopped + itemUnavailability.stopped, percent: (itemAvailability.stopped + itemUnavailability.stopped) / itemDuration, time: Duration.fromObject({seconds: itemAvailability.stopped + itemUnavailability.stopped}).toFormat("hh:mm:ss")},
      on: {seconds: itemAvailability.on + itemUnavailability.on, percent: (itemAvailability.on + itemUnavailability.on) / itemDuration, time: Duration.fromObject({seconds: itemAvailability.on + itemUnavailability.on}).toFormat("hh:mm:ss")},
      off: {seconds: itemAvailability.off + itemUnavailability.off, percent: (itemAvailability.off + itemUnavailability.off) / itemDuration, time: Duration.fromObject({seconds: itemAvailability.off + itemUnavailability.off}).toFormat("hh:mm:ss")},
      interaction: {seconds: itemAvailability.interaction + itemUnavailability.interaction, percent: (itemAvailability.interaction + itemUnavailability.interaction) / itemDuration, time: Duration.fromObject({seconds: itemAvailability.interaction + itemUnavailability.interaction}).toFormat("hh:mm:ss")}
    },
    originalEvents: events,
    events: transformedEvents,
    interactions: calculateInteractions(events, dateShift),
    violations: violations
  }
}

function calculateEquipmentParameter(events: Event[], parameter: WidgetParameter): number {
  let param = parameter.split(".")

  switch (param[0]) {
    case "availability":
      if(param[1] === "worked") {
        return _.sum(events.map(e => 
          (Number(e[9]) === 0 && e[8] === "worked" ? Number(e[7]) : 0) + (Number(e[9]) === 5 && e[8] === "worked" ? Number(e[7]) : 0)
        ))
      } else if(param[1] === "stopped") {
        return _.sum(events.map(e => 
          (Number(e[9]) === 0 && e[8] === "stopped" ? Number(e[7]) : 0) + (Number(e[9]) === 5 && e[8] === "stopped" ? Number(e[7]) : 0)
        ))
      } else if(param[1] === "on") {
        return _.sum(events.map(e => 
          ([0,5].includes(Number(e[9]))  && e[8] !== "turned_off" ? Number(e[7]) : 0)
        ))
      } else if(param[1] === "off") {
        return _.sum(events.map(e => 
          ([0,5].includes(Number(e[9])) && e[8] === "turned_off" ? Number(e[7]) : 0)
        ))
      } else if(param[1] === "total") {
        return _.sum(events.map(e => 
          ([0,5].includes(Number(e[9])) ? Number(e[7]) : 0)
        ))
      } else if(param[1] === "interaction") {
        return _.sum(events.map(e => 
          ([0,5].includes(Number(e[9])) && Number(e[10]) > 0 ? Number(e[7]) : 0)
        ))
      } else {
        return 0
      }
    case "works": 
    if(param[1] === "worked") {
      return _.sum(events.map(e => 
        (e[8] === "worked" && Number(e[9]) === 0 ? Number(e[7]) : 0)
      ))
    } else if(param[1] === "stopped") {
      return _.sum(events.map(e => 
        ((e[8] === "stopped" && Number(e[9]) === 0) ? Number(e[7]) : 0)
      ))
    } else if(param[1] === "on") {
      return _.sum(events.map(e => 
        ((e[8] !== "turned_off" && Number(e[9]) === 0) ? Number(e[7]) : 0)
      ))
    } else if(param[1] === "off") {
      return _.sum(events.map(e => 
        ((e[8] === "turned_off" && Number(e[9]) === 0) ? Number(e[7]) : 0)
      ))
    } else if(param[1] === "total") {
      return _.sum(events.map(e => 
        (Number(e[9]) === 0 ? Number(e[7]) : 0)
      ))
    } else if(param[1] === "interaction") {
      return _.sum(events.map(e => 
        (Number(e[9]) === 0 && Number(e[10]) > 0 ? Number(e[7]) : 0)
      ))
    } else {
      return 0
    }
    case "maintenance":
      if(param[1] === "worked") {
        return _.sum(events.map(e => 
          (e[8] === "worked" && Number(e[9]) === 5 ? Number(e[7]) : 0)
        ))
      } else if(param[1] === "stopped") {
        return _.sum(events.map(e => 
          ((e[8] === "stopped" && Number(e[9]) === 5) ? Number(e[7]) : 0)
        ))
      } else if(param[1] === "on") {
        return _.sum(events.map(e => 
          ((e[8] !== "turned_off" && Number(e[9]) === 5) ? Number(e[7]) : 0)
        ))
      } else if(param[1] === "off") {
        return _.sum(events.map(e => 
          ((e[8] === "turned_off" && Number(e[9]) === 5) ? Number(e[7]) : 0)
        ))
      } else if(param[1] === "total") {
        return _.sum(events.map(e => 
          (Number(e[9]) === 5 ? Number(e[7]) : 0)
        ))
      } else if(param[1] === "interaction") {
        return _.sum(events.map(e => 
          (Number(e[9]) === 5 && Number(e[10]) > 0 ? Number(e[7]) : 0)
        ))
      } else {
        return 0
      }
    case "unavailability":
      if(param[1] === "worked") {
        return _.sum(events.map(e => 
          ([1,2,3,4,5,7,8,9,10].includes(Number(e[9])) && e[8] === "worked" ? Number(e[7]) : 0)
        ))
      } else if(param[1] === "stopped") {
        return _.sum(events.map(e => 
          ([1,2,3,4,5,7,8,9,10].includes(Number(e[9])) && e[8] === "stopped" ? Number(e[7]) : 0)
        ))
      } else if(param[1] === "on") {
        return _.sum(events.map(e => 
          ([1,2,3,4,5,7,8,9,10].includes(Number(e[9])) && e[8] !== "turned_off" ? Number(e[7]) : 0)
        ))
      } else if(param[1] === "off") {
        return _.sum(events.map(e => 
          ([1,2,3,4,5,7,8,9,10].includes(Number(e[9])) && e[8] === "turned_off" ? Number(e[7]) : 0)
        ))
      } else if(param[1] === "total") {
        return _.sum(events.map(e => 
          ([1,2,3,4,5,7,8,9,10].includes(Number(e[9])) ? Number(e[7]) : 0)
        ))
      } else if(param[1] === "interaction") {
        return _.sum(events.map(e => 
          ([1,2,3,4,5,7,8,9,10].includes(Number(e[9])) && Number(e[10]) > 0 ? Number(e[7]) : 0)
        ))
      } else {
        return 0
      }
    case "broken": 
      if(param[1] === "worked") {
        return _.sum(events.map(e => 
          ([1].includes(Number(e[9])) && e[8] === "worked" ? Number(e[7]) : 0)
        ))
      } else if(param[1] === "stopped") {
        return _.sum(events.map(e => 
          ([1].includes(Number(e[9])) && e[8] === "stopped" ? Number(e[7]) : 0)
        ))
      } else if(param[1] === "on") {
        return _.sum(events.map(e => 
          ([1].includes(Number(e[9])) && e[8] !== "turned_off" ? Number(e[7]) : 0)
        ))
      } else if(param[1] === "off") {
        return _.sum(events.map(e => 
          ([1].includes(Number(e[9])) && e[8] === "turned_off" ? Number(e[7]) : 0)
        ))
      } else if(param[1] === "total") {
        return _.sum(events.map(e => 
          ([1].includes(Number(e[9])) ? Number(e[7]) : 0)
        ))
      } else if(param[1] === "interaction") {
        return _.sum(events.map(e => 
          ([1].includes(Number(e[9])) && Number(e[10]) > 0 ? Number(e[7]) : 0)
        ))
      } else {
        return 0
      }
    case "repair":
      if(param[1] === "worked") {
        return _.sum(events.map(e => 
          ([4].includes(Number(e[9])) && e[8] === "worked" ? Number(e[7]) : 0)
        ))
      } else if(param[1] === "stopped") {
        return _.sum(events.map(e => 
          ([4].includes(Number(e[9])) && e[8] === "stopped" ? Number(e[7]) : 0)
        ))
      } else if(param[1] === "on") {
        return _.sum(events.map(e => 
          ([4].includes(Number(e[9])) && e[8] !== "turned_off" ? Number(e[7]) : 0)
        ))
      } else if(param[1] === "off") {
        return _.sum(events.map(e => 
          ([4].includes(Number(e[9])) && e[8] === "turned_off" ? Number(e[7]) : 0)
        ))
      } else if(param[1] === "total") {
        return _.sum(events.map(e => 
          ([4].includes(Number(e[9])) ? Number(e[7]) : 0)
        ))
      } else if(param[1] === "interaction") {
        return _.sum(events.map(e => 
          ([4].includes(Number(e[9])) && Number(e[10]) > 0 ? Number(e[7]) : 0)
        ))
      } else {
        return 0
      }
    case "service": 
      if(param[1] === "worked") {
        return _.sum(events.map(e => 
          ([9].includes(Number(e[9])) && e[8] === "worked" ? Number(e[7]) : 0)
        ))
      } else if(param[1] === "stopped") {
        return _.sum(events.map(e => 
          ([9].includes(Number(e[9])) && e[8] === "stopped" ? Number(e[7]) : 0)
        ))
      } else if(param[1] === "on") {
        return _.sum(events.map(e => 
          ([9].includes(Number(e[9])) && e[8] !== "turned_off" ? Number(e[7]) : 0)
        ))
      } else if(param[1] === "off") {
        return _.sum(events.map(e => 
          ([9].includes(Number(e[9])) && e[8] === "turned_off" ? Number(e[7]) : 0)
        ))
      } else if(param[1] === "total") {
        return _.sum(events.map(e => 
          ([9].includes(Number(e[9])) ? Number(e[7]) : 0)
        ))
      } else if(param[1] === "interaction") {
        return _.sum(events.map(e => 
          ([9].includes(Number(e[9])) && Number(e[10]) > 0 ? Number(e[7]) : 0)
        ))
      } else {
        return 0
      }
    case "other": 
      if(param[1] === "worked") {
        return _.sum(events.map(e => 
          ([2,3,5,7,8,10].includes(Number(e[9])) && e[8] === "worked" ? Number(e[7]) : 0)
        ))
      } else if(param[1] === "stopped") {
        return _.sum(events.map(e => 
          ([2,3,5,7,8,10].includes(Number(e[9])) && e[8] === "stopped" ? Number(e[7]) : 0)
        ))
      } else if(param[1] === "on") {
        return _.sum(events.map(e => 
          ([2,3,5,7,8,10].includes(Number(e[9])) && e[8] !== "turned_off" ? Number(e[7]) : 0)
        ))
      } else if(param[1] === "off") {
        return _.sum(events.map(e => 
          ([2,3,5,7,8,10].includes(Number(e[9])) && e[8] === "turned_off" ? Number(e[7]) : 0)
        ))
      } else if(param[1] === "total") {
        return _.sum(events.map(e => 
          ([2,3,5,7,8,10].includes(Number(e[9])) ? Number(e[7]) : 0)
        ))
      } else if(param[1] === "interaction") {
        return _.sum(events.map(e => 
          ([2,3,5,7,8,10].includes(Number(e[9])) && Number(e[10]) > 0 ? Number(e[7]) : 0)
        ))
      } else {
        return 0
      }
    case "duration": 
      if(param[1] === "worked") {
        return _.sum(events.map(e => 
          (e[8] === "worked" ? Number(e[7]) : 0)
        ))
      } else if(param[1] === "stopped") {
        return _.sum(events.map(e => 
          (e[8] === "stopped" ? Number(e[7]) : 0)
        ))
      } else if(param[1] === "on") {
        return _.sum(events.map(e => 
          (e[8] !== "turned_off" ? Number(e[7]) : 0)
        ))
      } else if(param[1] === "off") {
        return _.sum(events.map(e => 
          (e[8] === "turned_off" ? Number(e[7]) : 0)
        ))
      } else if(param[1] === "total") {
        return _.sum(events.map(e => 
          Number(e[7])
        ))
      } else if(param[1] === "interaction") {
        return _.sum(events.map(e => 
          (Number(e[10]) > 0 ? Number(e[7]) : 0)
        ))
      } else {
        return 0
      }
    default:
      return 0
  }
}

function calculatePersonRow(day: string, events: Event[], shift: FShift, equipment: Equipment, person: Person) {
  let dateShift = convertFshiftsToDateShifts([shift], DateTime.fromSQL(day))[0]
  let interactionIds: number[] = []
  let itemDuration: number = 0
  let transformedEvents: Event[] = []

  let itemAvailability: {worked: number, stopped: number, on: number, off: number, interaction: number} = {worked: 0, stopped: 0, on: 0, off: 0, interaction: 0}
  let itemWorks: {worked: number, stopped: number, on: number, off: number, interaction: number} = {worked: 0, stopped: 0, on: 0, off: 0, interaction: 0}
  let itemMaintenance: {worked: number, stopped: number, on: number, off: number, interaction: number} = {worked: 0, stopped: 0, on: 0, off: 0, interaction: 0}

  let itemUnavailability: {worked: number, stopped: number, on: number, off: number, interaction: number} = {worked: 0, stopped: 0, on: 0, off: 0, interaction: 0}
  let itemRepair: {worked: number, stopped: number, on: number, off: number, interaction: number} = {worked: 0, stopped: 0, on: 0, off: 0, interaction: 0}
  let itemBroken: {worked: number, stopped: number, on: number, off: number, interaction: number} = {worked: 0, stopped: 0, on: 0, off: 0, interaction: 0}
  let itemService: {worked: number, stopped: number, on: number, off: number, interaction: number} = {worked: 0, stopped: 0, on: 0, off: 0, interaction: 0}
  let itemOther:{worked: number, stopped: number, on: number, off: number, interaction: number} = {worked: 0, stopped: 0, on: 0, off: 0, interaction: 0}

  events.map((e: Event) => {
    interactionIds.push(Number(e[11]))

    itemWorks.worked = itemWorks.worked + (e[8] === "worked" && Number(e[9]) === 0 ? Number(e[7]) : 0)
    itemWorks.stopped = itemWorks.stopped + ((e[8] === "stopped" && Number(e[9]) === 0) ? Number(e[7]) : 0)
    itemWorks.on = itemWorks.on + ((e[8] !== "turned_off" && Number(e[9]) === 0) ? Number(e[7]) : 0)
    itemWorks.off = itemWorks.off + ((e[8] === "turned_off" && Number(e[9]) === 0) ? Number(e[7]) : 0)
    itemWorks.interaction = itemWorks.interaction + (Number(e[9]) === 0 && Number(e[10]) > 0 ? Number(e[7]) : 0)

    itemMaintenance.worked = itemMaintenance.worked + (e[8] === "worked" && Number(e[9]) === 5 ? Number(e[7]) : 0)
    itemMaintenance.stopped = itemMaintenance.stopped + (e[8] === "stopped" && Number(e[9]) === 5 ? Number(e[7]) : 0)
    itemMaintenance.on = itemMaintenance.on + (e[8] !== "turned_off" && Number(e[9]) === 5 ? Number(e[7]) : 0)
    itemMaintenance.off = itemMaintenance.off + (e[8] === "turned_off" && Number(e[9]) === 5 ? Number(e[7]) : 0)
    itemMaintenance.interaction = itemMaintenance.interaction + (Number(e[9]) === 5 && Number(e[10]) > 0 ? Number(e[7]) : 0)

    itemAvailability.worked = itemAvailability.worked + (Number(e[9]) === 0 && e[8] === "worked" ? Number(e[7]) : 0) + (Number(e[9]) === 5 && e[8] === "worked" ? equipment?.attributes.correct_maintenance_load ? Number(e[7]) : 0 : 0)
    itemAvailability.stopped = itemAvailability.stopped + (Number(e[9]) === 0 && e[8] === "stopped" ? Number(e[7]) : 0) + (Number(e[9]) === 5 ? equipment?.attributes.correct_maintenance_load ? e[8] === "stopped" ? Number(e[7]) : 0 : e[8] !== "turned_off" ? Number(e[7]) : 0 : 0)
    itemAvailability.on = itemAvailability.on + ([0,5].includes(Number(e[9]))  && e[8] !== "turned_off" ? Number(e[7]) : 0)
    itemAvailability.off = itemAvailability.off + ([0,5].includes(Number(e[9])) && e[8] === "turned_off" ? Number(e[7]) : 0)
    itemAvailability.interaction = itemAvailability.interaction + ([0,5].includes(Number(e[9])) && Number(e[10]) > 0 ? Number(e[7]) : 0)

    itemBroken.worked = itemBroken.worked + (Number(e[9]) === 1 && e[8] === "worked" ? Number(e[7]) : 0)
    itemBroken.stopped = itemBroken.stopped + (Number(e[9]) === 1 && e[8] === "stopped"  ? Number(e[7]) : 0)
    itemBroken.on = itemBroken.on + (Number(e[9]) === 1 && e[8] !== "turned_off"  ? Number(e[7]) : 0)
    itemBroken.off = itemBroken.off + (Number(e[9]) === 1 && e[8] === "turned_off"  ? Number(e[7]) : 0)
    itemBroken.interaction = itemBroken.interaction + (Number(e[9]) === 1 && Number(e[10]) > 0 ? Number(e[7]) : 0)

    itemRepair.worked = itemRepair.worked + (Number(e[9]) === 4 && e[8] === "worked" ? Number(e[7]) : 0)
    itemRepair.stopped = itemRepair.stopped + (Number(e[9]) === 4 && e[8] === "stopped"  ? Number(e[7]) : 0)
    itemRepair.on = itemRepair.on + (Number(e[9]) === 4 && e[8] !== "turned_off"  ? Number(e[7]) : 0)
    itemRepair.off = itemRepair.off + (Number(e[9]) === 4 && e[8] === "turned_off"  ? Number(e[7]) : 0)
    itemRepair.interaction = itemRepair.interaction + (Number(e[9]) === 4 && Number(e[10]) > 0 ? Number(e[7]) : 0)

    itemService.worked = itemService.worked + (Number(e[9]) === 9 && e[8] === "worked" ? Number(e[7]) : 0)
    itemService.stopped = itemService.stopped + (Number(e[9]) === 9 && e[8] === "stopped"  ? Number(e[7]) : 0)
    itemService.on = itemService.on + (Number(e[9]) === 9 && e[8] !== "turned_off"  ? Number(e[7]) : 0)
    itemService.off = itemService.off + (Number(e[9]) === 9 && e[8] === "turned_off"  ? Number(e[7]) : 0)
    itemService.interaction = itemService.interaction + (Number(e[9]) === 9 && Number(e[10]) > 0  ? Number(e[7]) : 0)

    itemOther.worked = itemOther.worked + ([2,3,6,7,8,10].includes(Number(e[9])) && e[8] === "worked" ? Number(e[7]) : 0)
    itemOther.stopped = itemOther.stopped + ([2,3,6,7,8,10].includes(Number(e[9])) && e[8] === "stopped"  ? Number(e[7]) : 0)
    itemOther.on = itemOther.on + ([2,3,6,7,8,10].includes(Number(e[9])) && e[8] !== "turned_off"  ? Number(e[7]) : 0)
    itemOther.off = itemOther.off + ([2,3,6,7,8,10].includes(Number(e[9])) && e[8] === "turned_off"  ? Number(e[7]) : 0)
    itemOther.interaction = itemOther.interaction + ([2,3,5,7,8,10].includes(Number(e[9])) && Number(e[10]) > 0 ? Number(e[7]) : 0)

    itemUnavailability.worked = itemUnavailability.worked + ([1,2,3,4,6,7,8,9,10].includes(Number(e[9])) && e[8] === "worked" ? Number(e[7]) : 0)
    itemUnavailability.stopped = itemUnavailability.stopped + ([1,2,3,4,6,7,8,9,10].includes(Number(e[9])) && e[8] === "stopped" ? Number(e[7]) : 0)
    itemUnavailability.on = itemUnavailability.on + ([1,2,3,4,6,7,8,9,10].includes(Number(e[9])) && e[8] !== "turned_off" ? Number(e[7]) : 0)
    itemUnavailability.off = itemUnavailability.off + ([1,2,3,4,6,7,8,9,10].includes(Number(e[9])) && e[8] === "turned_off" ? Number(e[7]) : 0)
    itemUnavailability.interaction = itemUnavailability.interaction + ([1,2,3,4,6,7,8,9,10].includes(Number(e[9])) && Number(e[10]) > 0 ? Number(e[7]) : 0)

    itemDuration = equipment?.attributes.is_main ? itemDuration + Number(e[7]) : itemDuration + ((e[8] !== "turned_off" && Number(e[9]) === 0) || (equipment?.attributes.correct_maintenance_load && e[8] !== "turned_off" && Number(e[9]) === 5) ? Number(e[7]) : 0)

    let trasnformedEvent = transformEvent(e, equipment!)
    let splitedEventByHour: Event[] = []
    splitEventByHour(trasnformedEvent, splitedEventByHour)
    transformedEvents = [...transformedEvents, ...splitedEventByHour]
  })

  let workedStatuses = equipment.attributes.correct_maintenance_load ? [0,5] : [0]
  let activeEvents = events.filter((e: Event) => e[8] !== "turned_off" && [0,5].includes(Number(e[9])))
  let activeEventsInteraction = activeEvents.filter((e: Event) => Number(e[10]) > 0)
  let activeEventsWork = activeEvents.filter((e: Event) => e[8] === "worked" && workedStatuses.includes(Number(e[9])) && Number(e[7]) > (equipment.attributes.stop_threshold ? equipment.attributes.stop_threshold : 0))

  let violations = {
    start: {
      eventTime: activeEvents[0] ? String(activeEvents[0][3]) : null, 
      seconds: equipment.attributes.is_main && activeEvents[0] ? Number(activeEvents[0][4]) - dateShift.start_time.toSeconds() : 0, 
      time: equipment.attributes.is_main && activeEvents[0] ? Duration.fromObject({seconds: Number(activeEvents[0][4]) - dateShift.start_time.toSeconds()}).toFormat("hh:mm:ss") : "00:00:00", 
      percent: equipment.attributes.is_main && activeEvents[0] ? (Number(activeEvents[0][4]) - dateShift.start_time.toSeconds()) / dateShift.duration : 0
    },
    startInteraction: {
      eventTime: activeEventsInteraction[0] ? String(activeEventsInteraction[0][3]) : null, 
      seconds: equipment.attributes.is_main && activeEventsInteraction[0] ? Number(activeEventsInteraction[0][4]) - dateShift.start_time.toSeconds() : 0, 
      time: equipment.attributes.is_main && activeEventsInteraction[0] ? Duration.fromObject({seconds: Number(activeEventsInteraction[0][4]) -  dateShift.start_time.toSeconds()}).toFormat("hh:mm:ss") : "00:00:00", 
      percent: equipment.attributes.is_main && activeEventsInteraction[0] ? (Number(activeEventsInteraction[0][4]) - dateShift.start_time.toSeconds()) / dateShift.duration : 0
    },
    startWork: {
      eventTime: activeEventsWork[0] ? String(activeEventsWork[0][3]) : null, 
      seconds: equipment.attributes.is_main && activeEventsWork[0] ? Number(activeEventsWork[0][4]) - Number(activeEventsInteraction[0][4]) : 0, 
      time: equipment.attributes.is_main && activeEventsWork[0] ? Duration.fromObject({seconds: Number(activeEventsWork[0][4]) - Number(activeEventsInteraction[0][4])}).toFormat("hh:mm:ss") : "00:00:00", 
      percent: equipment.attributes.is_main && activeEventsWork[0] ? (Number(activeEventsWork[0][4]) - Number(activeEventsInteraction[0][4])) / dateShift.duration : 0
    },
    end: {
      eventTime: activeEvents[activeEvents.length - 1] ? String(activeEvents[activeEvents.length - 1][5]) : null, 
      seconds: equipment.attributes.is_main && activeEvents[activeEvents.length - 1] ? dateShift.end_time.toSeconds() - Number(activeEvents[activeEvents.length - 1][6]) : 0, 
      time: equipment.attributes.is_main &&  activeEvents[activeEvents.length - 1] ? Duration.fromObject({seconds: dateShift.end_time.toSeconds() - Number(activeEvents[activeEvents.length - 1][6])}).toFormat("hh:mm:ss") : "00:00:00", 
      percent: equipment.attributes.is_main &&  activeEvents[activeEvents.length - 1] ? (dateShift.end_time.toSeconds() - Number(activeEvents[activeEvents.length - 1][6])) / dateShift.duration : 0
    },
    endInteraction: {
      eventTime: activeEventsInteraction[activeEventsInteraction.length - 1] ? String(activeEventsInteraction[activeEventsInteraction.length - 1][5]) : null, 
      seconds: equipment.attributes.is_main && activeEventsInteraction[activeEventsInteraction.length - 1] ? dateShift.end_time.toSeconds() - Number(activeEventsInteraction[activeEventsInteraction.length - 1][6]) : 0, 
      time: equipment.attributes.is_main && activeEventsInteraction[activeEventsInteraction.length - 1] ? Duration.fromObject({seconds: dateShift.end_time.toSeconds() - Number(activeEventsInteraction[activeEventsInteraction.length - 1][6])}).toFormat("hh:mm:ss") : "00:00:00", 
      percent: equipment.attributes.is_main && activeEventsInteraction[activeEventsInteraction.length - 1] ? (dateShift.end_time.toSeconds() - Number(activeEventsInteraction[activeEventsInteraction.length - 1][6])) / dateShift.duration : 0
    },
    endWork: {
      eventTime: activeEventsWork[activeEventsWork.length - 1] ? String(activeEventsWork[activeEventsWork.length - 1][5]) : null, 
      seconds: equipment.attributes.is_main && activeEventsWork[activeEventsWork.length - 1] ? Number(activeEventsInteraction[activeEventsInteraction.length - 1][6]) - Number(activeEventsWork[activeEventsWork.length - 1][6]) : 0, 
      time: equipment.attributes.is_main && activeEventsWork[activeEventsWork.length - 1] ? Duration.fromObject({seconds: Number(activeEventsInteraction[activeEventsInteraction.length - 1][6]) - Number(activeEventsWork[activeEventsWork.length - 1][6])}).toFormat("hh:mm:ss") : "00:00:00", 
      percent: equipment.attributes.is_main && activeEventsWork[activeEventsWork.length - 1] ? (Number(activeEventsInteraction[activeEventsInteraction.length - 1][6]) - Number(activeEventsWork[activeEventsWork.length - 1][6])) / dateShift.duration : 0
    }
  }

  interactionIds = _.uniq(interactionIds)

  return {
    dateShift: dateShift,
    equipment: equipment!,
    person: person,
    interactionIds: interactionIds,
    availability: {
      total: {seconds: itemAvailability.on + itemAvailability.off, percent: (itemAvailability.on + itemAvailability.off) / itemDuration, time: Duration.fromObject({seconds: (itemAvailability.on + itemAvailability.off) ? (itemAvailability.on + itemAvailability.off) : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: itemAvailability.worked, percent: itemAvailability.worked / itemDuration, time: Duration.fromObject({seconds: itemAvailability.worked ? itemAvailability.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: itemAvailability.stopped, percent: itemAvailability.stopped / itemDuration, time: Duration.fromObject({seconds: itemAvailability.stopped ? itemAvailability.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: itemAvailability.on, percent: itemAvailability.on / itemDuration, time: Duration.fromObject({seconds: itemAvailability.on ? itemAvailability.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: itemAvailability.off, percent: itemAvailability.off / itemDuration, time: Duration.fromObject({seconds: itemAvailability.off ? itemAvailability.off : 0}).toFormat("hh:mm:ss")},
      interaction: {seconds: itemAvailability.interaction, percent: itemAvailability.interaction / itemDuration, time: Duration.fromObject({seconds: itemAvailability.interaction ? itemAvailability.interaction : 0}).toFormat("hh:mm:ss")}
    },
    works: {
      total: {seconds: itemWorks.on + itemWorks.off, percent: (itemWorks.on + itemWorks.off) / itemDuration, time: Duration.fromObject({seconds: (itemWorks.on + itemWorks.off) ? (itemWorks.on + itemWorks.off) : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: itemWorks.worked, percent: itemWorks.worked / itemDuration, time: Duration.fromObject({seconds: itemWorks.worked ? itemWorks.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: itemWorks.stopped, percent: itemWorks.stopped / itemDuration, time: Duration.fromObject({seconds: itemWorks.stopped ? itemWorks.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: itemWorks.on, percent: itemWorks.on / itemDuration, time: Duration.fromObject({seconds: itemWorks.on ? itemWorks.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: itemWorks.off, percent: itemWorks.off / itemDuration, time: Duration.fromObject({seconds: itemWorks.off ? itemWorks.off : 0}).toFormat("hh:mm:ss")},
      interaction: {seconds: itemWorks.interaction, percent: itemWorks.interaction / itemDuration, time: Duration.fromObject({seconds: itemWorks.interaction ? itemWorks.interaction : 0}).toFormat("hh:mm:ss")}
    },
    maintenance: {
      total: {seconds: itemMaintenance.on + itemMaintenance.off, percent: (itemMaintenance.on + itemMaintenance.off) / itemDuration, time: Duration.fromObject({seconds: (itemMaintenance.on + itemMaintenance.off) ? (itemMaintenance.on + itemMaintenance.off) : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: itemMaintenance.worked, percent: itemMaintenance.worked / itemDuration, time: Duration.fromObject({seconds: itemMaintenance.worked ? itemMaintenance.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: itemMaintenance.stopped, percent: itemMaintenance.stopped / itemDuration, time: Duration.fromObject({seconds: itemMaintenance.stopped ? itemMaintenance.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: itemMaintenance.on, percent: itemMaintenance.on / itemDuration, time: Duration.fromObject({seconds: itemMaintenance.on ? itemMaintenance.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: itemMaintenance.off, percent: itemMaintenance.off / itemDuration, time: Duration.fromObject({seconds: itemMaintenance.off ? itemMaintenance.off : 0}).toFormat("hh:mm:ss")},
      interaction: {seconds: itemMaintenance.interaction, percent: itemMaintenance.interaction / itemDuration, time: Duration.fromObject({seconds: itemMaintenance.interaction ? itemMaintenance.interaction : 0}).toFormat("hh:mm:ss")}
    },
    unavailability: {
      total: {seconds: itemUnavailability.on + itemUnavailability.off, percent: (itemUnavailability.on + itemUnavailability.off) / itemDuration, time: Duration.fromObject({seconds: (itemUnavailability.on + itemUnavailability.off) ? (itemUnavailability.on + itemUnavailability.off) : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: itemUnavailability.worked, percent: itemUnavailability.worked / itemDuration, time: Duration.fromObject({seconds: itemUnavailability.worked ? itemUnavailability.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: itemUnavailability.stopped, percent: itemUnavailability.stopped / itemDuration, time: Duration.fromObject({seconds: itemUnavailability.stopped ? itemUnavailability.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: itemUnavailability.on, percent: itemUnavailability.on / itemDuration, time: Duration.fromObject({seconds: itemUnavailability.on ? itemUnavailability.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: itemUnavailability.off, percent: itemUnavailability.off / itemDuration, time: Duration.fromObject({seconds: itemUnavailability.off ? itemUnavailability.off : 0}).toFormat("hh:mm:ss")},
      interaction: {seconds: itemUnavailability.interaction, percent: itemUnavailability.interaction / itemDuration, time: Duration.fromObject({seconds: itemUnavailability.interaction ? itemUnavailability.interaction : 0}).toFormat("hh:mm:ss")}
    },
    broken: {
      total: {seconds: itemBroken.on + itemBroken.off, percent: (itemBroken.on + itemBroken.off) / itemDuration, time: Duration.fromObject({seconds: (itemBroken.on + itemBroken.off) ? (itemBroken.on + itemBroken.off) : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: itemBroken.worked, percent: itemBroken.worked / itemDuration, time: Duration.fromObject({seconds: itemBroken.worked ? itemBroken.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: itemBroken.stopped, percent: itemBroken.stopped / itemDuration, time: Duration.fromObject({seconds: itemBroken.stopped ? itemBroken.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: itemBroken.on, percent: itemBroken.on / itemDuration, time: Duration.fromObject({seconds: itemBroken.on ? itemBroken.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: itemBroken.off, percent: itemBroken.off / itemDuration, time: Duration.fromObject({seconds: itemBroken.off ? itemBroken.off : 0}).toFormat("hh:mm:ss")},
      interaction: {seconds: itemBroken.interaction, percent: itemBroken.interaction / itemDuration, time: Duration.fromObject({seconds: itemBroken.interaction ? itemBroken.interaction : 0}).toFormat("hh:mm:ss")}
    },
    repair: {
      total: {seconds: itemRepair.on + itemRepair.off, percent: (itemRepair.on + itemRepair.off) / itemDuration, time: Duration.fromObject({seconds: (itemRepair.on + itemRepair.off) ? (itemRepair.on + itemRepair.off) : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: itemRepair.worked, percent: itemRepair.worked / itemDuration, time: Duration.fromObject({seconds: itemRepair.worked ? itemRepair.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: itemRepair.stopped, percent: itemRepair.stopped / itemDuration, time: Duration.fromObject({seconds: itemRepair.stopped ? itemRepair.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: itemRepair.on, percent: itemRepair.on / itemDuration, time: Duration.fromObject({seconds: itemRepair.on ? itemRepair.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: itemRepair.off, percent: itemRepair.off / itemDuration, time: Duration.fromObject({seconds: itemRepair.off ? itemRepair.off : 0}).toFormat("hh:mm:ss")},
      interaction: {seconds: itemRepair.interaction, percent: itemRepair.interaction / itemDuration, time: Duration.fromObject({seconds: itemRepair.interaction ? itemRepair.interaction : 0}).toFormat("hh:mm:ss")}
    },
    service: {
      total: {seconds: itemService.on + itemService.off, percent: (itemService.on + itemService.off) / itemDuration, time: Duration.fromObject({seconds: (itemService.on + itemService.off) ? (itemService.on + itemService.off) : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: itemService.worked, percent: itemService.worked / itemDuration, time: Duration.fromObject({seconds: itemService.worked ? itemService.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: itemService.stopped, percent: itemService.stopped / itemDuration, time: Duration.fromObject({seconds: itemService.stopped ? itemService.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: itemService.on, percent: itemService.on / itemDuration, time: Duration.fromObject({seconds: itemService.on ? itemService.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: itemService.off, percent: itemService.off / itemDuration, time: Duration.fromObject({seconds: itemService.off ? itemService.off : 0}).toFormat("hh:mm:ss")},
      interaction: {seconds: itemService.interaction, percent: itemService.interaction / itemDuration, time: Duration.fromObject({seconds: itemService.interaction ? itemService.interaction : 0}).toFormat("hh:mm:ss")}
    },
    other: {
      total: {seconds: itemOther.on + itemOther.off, percent: (itemOther.on + itemOther.off) / itemDuration, time: Duration.fromObject({seconds: (itemOther.on + itemOther.off) ? (itemOther.on + itemOther.off) : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: itemOther.worked, percent: itemOther.worked / itemDuration, time: Duration.fromObject({seconds: itemOther.worked ? itemOther.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: itemOther.stopped, percent: itemOther.stopped / itemDuration, time: Duration.fromObject({seconds: itemOther.stopped ? itemOther.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: itemOther.on, percent: itemOther.on / itemDuration, time: Duration.fromObject({seconds: itemOther.on ? itemOther.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: itemOther.off, percent: itemOther.off / itemDuration, time: Duration.fromObject({seconds: itemOther.off ? itemOther.off : 0}).toFormat("hh:mm:ss")},
      interaction: {seconds: itemOther.interaction, percent: itemOther.interaction / itemDuration, time: Duration.fromObject({seconds: itemOther.interaction ? itemOther.interaction : 0}).toFormat("hh:mm:ss")}
    },
    duration: {
      total: {seconds: itemDuration, percent: 1, time: Duration.fromObject({seconds: itemDuration ? itemDuration : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: itemAvailability.worked + itemUnavailability.worked, percent: (itemAvailability.worked + itemUnavailability.worked) / itemDuration, time: Duration.fromObject({seconds: itemAvailability.worked + itemUnavailability.worked}).toFormat("hh:mm:ss")},
      stopped: {seconds: itemAvailability.stopped + itemUnavailability.stopped, percent: (itemAvailability.stopped + itemUnavailability.stopped) / itemDuration, time: Duration.fromObject({seconds: itemAvailability.stopped + itemUnavailability.stopped}).toFormat("hh:mm:ss")},
      on: {seconds: itemAvailability.on + itemUnavailability.on, percent: (itemAvailability.on + itemUnavailability.on) / itemDuration, time: Duration.fromObject({seconds: itemAvailability.on + itemUnavailability.on}).toFormat("hh:mm:ss")},
      off: {seconds: itemAvailability.off + itemUnavailability.off, percent: (itemAvailability.off + itemUnavailability.off) / itemDuration, time: Duration.fromObject({seconds: itemAvailability.off + itemUnavailability.off}).toFormat("hh:mm:ss")},
      interaction: {seconds: itemAvailability.interaction + itemUnavailability.interaction, percent: (itemAvailability.interaction + itemUnavailability.interaction) / itemDuration, time: Duration.fromObject({seconds: itemAvailability.interaction + itemUnavailability.interaction}).toFormat("hh:mm:ss")}
    },
    originalEvents: events,
    events: transformedEvents,
    interactions: calculateInteractions(events, dateShift),
    violations: violations
  }
}

function calculateInteractions(events: Event[], shiftDay: DateShift) {
  let eventsDayData: Event[] = []
  let interactions: InteractionFromEvents[] = []
  let dayEvents = events.filter(de => de[8] !== "turned_off").sort((a, b) => Number(a[4]) - Number(b[4]))
  let turnedOffs: Event[] = []

  dayEvents.map((e, index, array) => {
    if(index === 0 && Number(e[4]) > (shiftDay.start_day.set({hour: shiftDay.start_time.hour, minute: shiftDay.start_time.minute}).toMillis() / 1000)) {
      turnedOffs.push([
        e[0], 
        shiftDay.start_day.toSQLDate()!, 
        shiftDay.start_day.toSQLDate()!, 
        shiftDay.start_day.set({hour: shiftDay.start_time.hour, minute: shiftDay.start_time.minute}).toFormat("yyyy-MM-dd HH:mm:ss")!, 
        Math.round(shiftDay.start_day.set({hour: shiftDay.start_time.hour, minute: shiftDay.start_time.minute}).toMillis() / 1000),
        e[3],
        e[4],
        Number(e[4]) - Math.round(shiftDay.start_day.set({hour: shiftDay.start_time.hour, minute: shiftDay.start_time.minute + (shiftDay.id === "full" ? 1 : 0)}).toMillis() / 1000),
        "turned_off",
        0,
        0,
        0,
        0,
        0,
        0,
        1
      ])
    }

    if(index === array.length - 1 && Number(e[6]) < (shiftDay.end_day.set({hour: shiftDay.end_time.hour, minute: shiftDay.end_time.minute + (shiftDay.id === "full" ? 1 : 0)}).toMillis() / 1000)) {
      turnedOffs.push([
        e[0], 
        e[1], 
        shiftDay.start_day.toSQLDate()!, 
        e[5], 
        e[6],
        shiftDay.end_day.set({hour: shiftDay.end_time.hour, minute: shiftDay.end_time.minute + (shiftDay.id === "full" ? 1 : 0)}).toFormat("yyyy-MM-dd HH:mm:ss")!,
        shiftDay.start_day.set({hour: shiftDay.end_time.hour, minute: shiftDay.end_time.minute + (shiftDay.id === "full" ? 1 : 0)}).toMillis() / 1000,
        Math.round(shiftDay.start_day.set({hour: shiftDay.end_time.hour, minute: shiftDay.end_time.minute + (shiftDay.id === "full" ? 1 : 0)}).toMillis() / 1000) - Number(e[6]),
        "turned_off",
        0,
        0,
        0,
        0,
        0,
        0,
        1
      ])
    }

    if(isUndefined(array[index + 1]) || array[index + 1][4] === e[6]) { return }
    else {
      Number(array[index + 1][4]) - Number(e[6]) > 0 &&
      turnedOffs.push([
        e[0], 
        e[1], 
        e[2], 
        e[5], 
        e[6],
        array[index + 1][3],
        array[index + 1][4],
        Number(array[index + 1][4]) - Number(e[6]),
        "turned_off",
        0,
        0,
        0,
        0,
        0,
        0,
        1
      ])
    }
  })

  eventsDayData = [...dayEvents, ...turnedOffs].sort((a, b) => Number(a[4]) - Number(b[4]))

  turnedOffs.map((tOff, ind, arr) => {
    interactions.push({
      at: String(tOff[1]),
      equipment_item_id: 0, 
      personnel_id: 0,
      worked: 0, 
      stopped: 0, 
      duration: Number(tOff[7]),
      from: String(tOff[3]), 
      from_ts: Number(tOff[4]), 
      to: String(tOff[5]),
      to_ts: Number(tOff[6])
    })

    if(isUndefined(arr[ind + 1])) { return }

    
    let eventsBetween = eventsDayData.filter(edd => Number(edd[4]) >= Number(tOff[6]) && Number(edd[6]) <= Number(arr[ind + 1][4]))
    interactions = [...interactions, ...splitBetweenEventsOnInteractions(eventsBetween)]
  })

  return interactions
}

function splitBetweenEventsOnInteractions(eventsBetween: Event[]) {
  if(eventsBetween.length === 0) {return []}

  let interactions: {at: string, equipment_item_id: number, personnel_id: number, worked: number, stopped: number, duration: number, from: string, from_ts: number, to: string, to_ts: number}[] = [
    {
      at: String(eventsBetween[0][1]),
      equipment_item_id: Number(eventsBetween[0][0]),
      personnel_id: Number(eventsBetween[0][10]),
      worked: Number(eventsBetween[0][8] === "worked" ? eventsBetween[0][7] : 0),
      stopped: Number(eventsBetween[0][8] === "stopped" ? eventsBetween[0][7] : 0),
      duration: Number(eventsBetween[0][6]) - Number(eventsBetween[0][4]),
      from: String(eventsBetween[0][3]),
      from_ts: Number(eventsBetween[0][4]),
      to: String(eventsBetween[0][5]),
      to_ts: Number(eventsBetween[0][6])
    }
  ]

  for (let i = 1; i < eventsBetween.length; i++) { 
    if(eventsBetween[i][0] === eventsBetween[i - 1][0] && eventsBetween[i][10] === eventsBetween[i - 1][10]) {
      interactions[interactions.length - 1] = {
        ...interactions[interactions.length - 1], 
        worked: interactions[interactions.length - 1].worked + Number(eventsBetween[i][8] === "worked" ? eventsBetween[i][7] : 0),
        stopped: interactions[interactions.length - 1].stopped + Number(eventsBetween[i][8] === "stopped" ? eventsBetween[i][7] : 0),
        duration: interactions[interactions.length - 1].duration + Number(eventsBetween[i][6]) - Number(eventsBetween[i][4]),
        from: interactions[interactions.length - 1].from,
        from_ts: interactions[interactions.length - 1].from_ts,
        to: String(eventsBetween[i][5]),
        to_ts: Number(eventsBetween[i][6])
      }
    } else {
      interactions.push(
        {
          at: String(eventsBetween[i][1]),
          equipment_item_id: Number(eventsBetween[i][0]),
          personnel_id: Number(eventsBetween[i][10]),
          worked: Number(eventsBetween[i][8] === "worked" ? eventsBetween[i][7] : 0),
          stopped: Number(eventsBetween[i][8] === "stopped" ? eventsBetween[i][7] : 0),
          duration: Number(eventsBetween[i][6]) - Number(eventsBetween[i][4]),
          from: String(eventsBetween[i][3]),
          from_ts: Number(eventsBetween[i][4]),
          to: String(eventsBetween[i][5]),
          to_ts: Number(eventsBetween[i][6])
        }
      )
    }
  }
  return interactions
}

// Расчет строк и саммари для оборудования на основе ответа usage_dataset
function calculateSummaryUsage(data: UsageDataset[], shift: FShift, equipment: Equipment, persons: Person[]):SummaryUsageRow {
  let itemsInteractionIds: number[] = []
  let daysData: UsageRow[] = []

  let eqTotal: {total: number, worked: number, stopped: number, on: number, off: number} = {total: 0, worked: 0, stopped: 0, on: 0, off: 0}
  let eqAvailability: {total: number, worked: number, stopped: number, on: number, off: number} = {total: 0, worked: 0, stopped: 0, on: 0, off: 0}
  let eqWorks: {total: number, worked: number, stopped: number, on: number, off: number} = {total: 0, worked: 0, stopped: 0, on: 0, off: 0}
  let eqMaintenance: {total: number, worked: number, stopped: number, on: number, off: number} = {total: 0, worked: 0, stopped: 0, on: 0, off: 0}
  let eqUnavailability: {total: number, worked: number, stopped: number, on: number, off: number} = {total: 0, worked: 0, stopped: 0, on: 0, off: 0}
  let eqBroken: {total: number, worked: number, stopped: number, on: number, off: number} = {total: 0, worked: 0, stopped: 0, on: 0, off: 0}
  let eqRepair: {total: number, worked: number, stopped: number, on: number, off: number} = {total: 0, worked: 0, stopped: 0, on: 0, off: 0}
  let eqService: {total: number, worked: number, stopped: number, on: number, off: number} = {total: 0, worked: 0, stopped: 0, on: 0, off: 0}
  let eqOther: {total: number, worked: number, stopped: number, on: number, off: number} = {total: 0, worked: 0, stopped: 0, on: 0, off: 0}

  Object.entries(_.groupBy(data, "at")).map(([day, ddata]:[string, UsageDataset[]]) => {
    let row = calculateUsageRow(day, ddata, shift, equipment, persons)

    eqTotal.total += row.total.total.seconds
    eqTotal.worked += row.total.worked.seconds
    eqTotal.stopped += row.total.stopped.seconds
    eqTotal.on += row.total.on.seconds
    eqTotal.off += row.total.off.seconds

    eqAvailability.total += row.availability.total.seconds
    eqAvailability.worked += row.availability.worked.seconds
    eqAvailability.stopped += row.availability.stopped.seconds
    eqAvailability.on += row.availability.on.seconds
    eqAvailability.off += row.availability.off.seconds

    eqWorks.total += row.works.total.seconds
    eqWorks.worked += row.works.worked.seconds
    eqWorks.stopped += row.works.stopped.seconds
    eqWorks.on += row.works.on.seconds
    eqWorks.off += row.works.off.seconds

    eqMaintenance.total += row.maintenance.total.seconds
    eqMaintenance.worked += row.maintenance.worked.seconds
    eqMaintenance.stopped += row.maintenance.stopped.seconds
    eqMaintenance.on += row.maintenance.on.seconds
    eqMaintenance.off += row.maintenance.off.seconds

    eqUnavailability.total += row.unavailability.total.seconds
    eqUnavailability.worked += row.unavailability.worked.seconds
    eqUnavailability.stopped += row.unavailability.stopped.seconds
    eqUnavailability.on += row.unavailability.on.seconds
    eqUnavailability.off += row.unavailability.off.seconds

    eqBroken.total += row.broken.total.seconds
    eqBroken.worked += row.broken.worked.seconds
    eqBroken.stopped += row.broken.stopped.seconds
    eqBroken.on += row.broken.on.seconds
    eqBroken.off += row.broken.off.seconds

    eqRepair.total += row.repair.total.seconds
    eqRepair.worked += row.repair.worked.seconds
    eqRepair.stopped += row.repair.stopped.seconds
    eqRepair.on += row.repair.on.seconds
    eqRepair.off += row.repair.off.seconds

    eqService.total += row.service.total.seconds
    eqService.worked += row.service.worked.seconds
    eqService.stopped += row.service.stopped.seconds
    eqService.on += row.service.on.seconds
    eqService.off += row.service.off.seconds

    eqOther.total += row.other.total.seconds
    eqOther.worked += row.other.worked.seconds
    eqOther.stopped += row.other.stopped.seconds
    eqOther.on += row.other.on.seconds
    eqOther.off += row.other.off.seconds

    itemsInteractionIds = [...itemsInteractionIds, ...row.interactionIds]

    daysData.push(row)
  })

  return {
    total: {
      total: {seconds: eqTotal.total, percent: 1, time: Duration.fromObject({seconds: eqTotal.total ? eqTotal.total : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: eqTotal.worked, percent: eqTotal.worked / eqTotal.total, time: Duration.fromObject({seconds: eqTotal.worked ? eqTotal.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: eqTotal.stopped, percent: eqTotal.stopped / eqTotal.total, time: Duration.fromObject({seconds: eqTotal.stopped ? eqTotal.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: eqTotal.on, percent: eqTotal.on / eqTotal.total, time: Duration.fromObject({seconds: eqTotal.on ? eqTotal.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: eqTotal.off, percent: eqTotal.off / eqTotal.total, time: Duration.fromObject({seconds: eqTotal.off ? eqTotal.off : 0}).toFormat("hh:mm:ss")},
    },
    availability: {
      total: {seconds: eqAvailability.total, percent: eqAvailability.total / eqTotal.total, time: Duration.fromObject({seconds: eqAvailability.total ? eqAvailability.total : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: eqAvailability.worked, percent: eqAvailability.worked / eqTotal.total, time: Duration.fromObject({seconds: eqAvailability.worked ? eqAvailability.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: eqAvailability.stopped, percent: eqAvailability.stopped / eqTotal.total, time: Duration.fromObject({seconds: eqAvailability.stopped ? eqAvailability.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: eqAvailability.on, percent: eqAvailability.on / eqTotal.total, time: Duration.fromObject({seconds: eqAvailability.on ? eqAvailability.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: eqAvailability.off, percent: eqAvailability.off / eqTotal.total, time: Duration.fromObject({seconds: eqAvailability.off ? eqAvailability.off : 0}).toFormat("hh:mm:ss")},
    },
    works: {
      total: {seconds: eqWorks.total, percent: eqWorks.total / eqTotal.total, time: Duration.fromObject({seconds: eqWorks.total ? eqWorks.total : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: eqWorks.worked, percent: eqWorks.worked / eqTotal.total, time: Duration.fromObject({seconds: eqWorks.worked ? eqWorks.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: eqWorks.stopped, percent: eqWorks.stopped / eqTotal.total, time: Duration.fromObject({seconds: eqWorks.stopped ? eqWorks.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: eqWorks.on, percent: eqWorks.on / eqTotal.total, time: Duration.fromObject({seconds: eqWorks.on ? eqWorks.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: eqWorks.off, percent: eqWorks.off / eqTotal.total, time: Duration.fromObject({seconds: eqWorks.off ? eqWorks.off : 0}).toFormat("hh:mm:ss")},
    },
    maintenance: {
      total: {seconds: eqMaintenance.total, percent: eqMaintenance.total / eqTotal.total, time: Duration.fromObject({seconds: eqMaintenance.total ? eqMaintenance.total : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: eqMaintenance.worked, percent: eqMaintenance.worked / eqTotal.total, time: Duration.fromObject({seconds: eqMaintenance.worked ? eqMaintenance.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: eqMaintenance.stopped, percent: eqMaintenance.stopped / eqTotal.total, time: Duration.fromObject({seconds: eqMaintenance.stopped ? eqMaintenance.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: eqMaintenance.on, percent: eqMaintenance.on / eqTotal.total, time: Duration.fromObject({seconds: eqMaintenance.on ? eqMaintenance.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: eqMaintenance.off, percent: eqMaintenance.off / eqTotal.total, time: Duration.fromObject({seconds: eqMaintenance.off ? eqMaintenance.off : 0}).toFormat("hh:mm:ss")},
    },
    unavailability: {
      total: {seconds: eqUnavailability.total, percent: eqUnavailability.total / eqTotal.total, time: Duration.fromObject({seconds: eqUnavailability.total ? eqUnavailability.total : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: eqUnavailability.worked, percent: eqUnavailability.worked / eqTotal.total, time: Duration.fromObject({seconds: eqUnavailability.worked ? eqUnavailability.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: eqUnavailability.stopped, percent: eqUnavailability.stopped / eqTotal.total, time: Duration.fromObject({seconds: eqUnavailability.stopped ? eqUnavailability.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: eqUnavailability.on, percent: eqUnavailability.on / eqTotal.total, time: Duration.fromObject({seconds: eqUnavailability.on ? eqUnavailability.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: eqUnavailability.off, percent: eqUnavailability.off / eqTotal.total, time: Duration.fromObject({seconds: eqUnavailability.off ? eqUnavailability.off : 0}).toFormat("hh:mm:ss")},
    },
    broken: {
      total: {seconds: eqBroken.total, percent: eqBroken.total / eqTotal.total, time: Duration.fromObject({seconds: eqBroken.total ? eqBroken.total : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: eqBroken.worked, percent: eqBroken.worked / eqTotal.total, time: Duration.fromObject({seconds: eqBroken.worked ? eqBroken.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: eqBroken.stopped, percent: eqBroken.stopped / eqTotal.total, time: Duration.fromObject({seconds: eqBroken.stopped ? eqBroken.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: eqBroken.on, percent: eqBroken.on / eqTotal.total, time: Duration.fromObject({seconds: eqBroken.on ? eqBroken.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: eqBroken.off, percent: eqBroken.off / eqTotal.total, time: Duration.fromObject({seconds: eqBroken.off ? eqBroken.off : 0}).toFormat("hh:mm:ss")},
    },
    repair: {
      total: {seconds: eqRepair.total, percent: eqRepair.total / eqTotal.total, time: Duration.fromObject({seconds: eqRepair.total ? eqRepair.total : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: eqRepair.worked, percent: eqRepair.worked / eqTotal.total, time: Duration.fromObject({seconds: eqRepair.worked ? eqRepair.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: eqRepair.stopped, percent: eqRepair.stopped / eqTotal.total, time: Duration.fromObject({seconds: eqRepair.stopped ? eqRepair.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: eqRepair.on, percent: eqRepair.on / eqTotal.total, time: Duration.fromObject({seconds: eqRepair.on ? eqRepair.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: eqRepair.off, percent: eqRepair.off / eqTotal.total, time: Duration.fromObject({seconds: eqRepair.off ? eqRepair.off : 0}).toFormat("hh:mm:ss")},
    },
    service: {
      total: {seconds: eqService.total, percent: eqService.total / eqTotal.total, time: Duration.fromObject({seconds: eqService.total ? eqService.total : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: eqService.worked, percent: eqService.worked / eqTotal.total, time: Duration.fromObject({seconds: eqService.worked ? eqService.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: eqService.stopped, percent: eqService.stopped / eqTotal.total, time: Duration.fromObject({seconds: eqService.stopped ? eqService.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: eqService.on, percent: eqService.on / eqTotal.total, time: Duration.fromObject({seconds: eqService.on ? eqService.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: eqService.off, percent: eqService.off / eqTotal.total, time: Duration.fromObject({seconds: eqService.off ? eqService.off : 0}).toFormat("hh:mm:ss")},
    },
    other: {
      total: {seconds: eqOther.total, percent: eqOther.total / eqTotal.total, time: Duration.fromObject({seconds: eqOther.total ? eqOther.total : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: eqOther.worked, percent: eqOther.worked / eqTotal.total, time: Duration.fromObject({seconds: eqOther.worked ? eqOther.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: eqOther.stopped, percent: eqOther.stopped / eqTotal.total, time: Duration.fromObject({seconds: eqOther.stopped ? eqOther.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: eqOther.on, percent: eqOther.on / eqTotal.total, time: Duration.fromObject({seconds: eqOther.on ? eqOther.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: eqOther.off, percent: eqOther.off / eqTotal.total, time: Duration.fromObject({seconds: eqOther.off ? eqOther.off : 0}).toFormat("hh:mm:ss")},
    },
    interactionIds: _.uniq(itemsInteractionIds),
    data: daysData
  }
}
// Расчет строк для оборудования на основе usage_dataset
function calculateUsageRow(day: string, data: UsageDataset[], shift: FShift, equipment: Equipment, persons: Person[]):UsageRow {
  let dateShift = convertFshiftsToDateShifts([shift], DateTime.fromSQL(day))[0]
  let interactions: UsageRowInteraction[] = []

  data.map(d => d.persons.map(p => {
    if(p[0] === 0) { return }
    let person = persons.find(person => Number(person.id) === Number(p[0]))!
    interactions.push({
      person: person,
      card: person?.attributes.access_tokens.find(t => t.id === p[1])!
    })
  }))

  let interactionIds: number[] = _.uniq(data.map(dd => dd.persons.map(p => p[0])).flat())
  let eqTotal: {total: number, worked: number, stopped: number, on: number, off: number} = {total: 0, worked: 0, stopped: 0, on: 0, off: 0}
  let eqAvailability: {total: number, worked: number, stopped: number, on: number, off: number} = {total: 0, worked: 0, stopped: 0, on: 0, off: 0}
  let eqWorks: {total: number, worked: number, stopped: number, on: number, off: number} = {total: 0, worked: 0, stopped: 0, on: 0, off: 0}
  let eqMaintenance: {total: number, worked: number, stopped: number, on: number, off: number} = {total: 0, worked: 0, stopped: 0, on: 0, off: 0}
  let eqUnavailability: {total: number, worked: number, stopped: number, on: number, off: number} = {total: 0, worked: 0, stopped: 0, on: 0, off: 0}
  let eqBroken: {total: number, worked: number, stopped: number, on: number, off: number} = {total: 0, worked: 0, stopped: 0, on: 0, off: 0}
  let eqRepair: {total: number, worked: number, stopped: number, on: number, off: number} = {total: 0, worked: 0, stopped: 0, on: 0, off: 0}
  let eqService: {total: number, worked: number, stopped: number, on: number, off: number} = {total: 0, worked: 0, stopped: 0, on: 0, off: 0}
  let eqOther: {total: number, worked: number, stopped: number, on: number, off: number} = {total: 0, worked: 0, stopped: 0, on: 0, off: 0}
  
  let eqStart: DateTime = _.min(data.map(dd => DateTime.fromSQL(dd.start_at)))!
  let eqStartWork: DateTime = _.min(data.map(dd => DateTime.fromSQL(dd.start_at_worked)))!
  let eqEnd: DateTime = _.max(data.map(dd => DateTime.fromSQL(dd.end_at)))!
  let eqEndWork: DateTime = _.max(data.map(dd => DateTime.fromSQL(dd.end_at_worked)))!

  data.map(dd => {
    eqTotal.total += equipment.attributes.is_main ? (dd.worked + dd.stopped + dd.turned_off) : (dd.worked + dd.stopped)
    eqTotal.worked += dd.worked
    eqTotal.stopped += dd.stopped
    eqTotal.on += (dd.worked + dd.stopped)
    eqTotal.off += dd.turned_off

    let eqStatus = dd.equipment_item_status

    if(eqStatus === 0) {
      eqAvailability.total += equipment.attributes.is_main ? (dd.worked + dd.stopped + dd.turned_off) : (dd.worked + dd.stopped)
      eqAvailability.worked += dd.worked
      eqAvailability.stopped += dd.stopped
      eqAvailability.on += (dd.worked + dd.stopped)
      eqAvailability.off += dd.turned_off

      eqWorks.total += (dd.worked + dd.stopped + dd.turned_off)
      eqWorks.worked += dd.worked
      eqWorks.stopped += dd.stopped
      eqWorks.on += (dd.worked + dd.stopped)
      eqWorks.off += dd.turned_off
    } else if(eqStatus === 5) {
      eqAvailability.total += equipment.attributes.is_main ? (dd.worked + dd.stopped + dd.turned_off) : (dd.worked + dd.stopped)
      eqAvailability.worked += equipment.attributes.correct_maintenance_load ? dd.worked : 0
      eqAvailability.stopped += equipment.attributes.correct_maintenance_load ? dd.stopped : dd.worked + dd.stopped
      eqAvailability.on += (dd.worked + dd.stopped)
      eqAvailability.off += dd.turned_off

      eqMaintenance.total += (dd.worked + dd.stopped + dd.turned_off)
      eqMaintenance.worked += dd.worked
      eqMaintenance.stopped += dd.stopped
      eqMaintenance.on += (dd.worked + dd.stopped)
      eqMaintenance.off += dd.turned_off
    } else if(eqStatus === 1) {
      eqUnavailability.total += (dd.worked + dd.stopped + dd.turned_off)
      eqUnavailability.worked += dd.worked
      eqUnavailability.stopped += dd.stopped
      eqUnavailability.on += (dd.worked + dd.stopped)
      eqUnavailability.off += dd.turned_off

      eqBroken.total += (dd.worked + dd.stopped + dd.turned_off)
      eqBroken.worked += dd.worked
      eqBroken.stopped += dd.stopped
      eqBroken.on += (dd.worked + dd.stopped)
      eqBroken.off += dd.turned_off
    } else if(eqStatus === 4) {
      eqUnavailability.total += (dd.worked + dd.stopped + dd.turned_off)
      eqUnavailability.worked += dd.worked
      eqUnavailability.stopped += dd.stopped
      eqUnavailability.on += (dd.worked + dd.stopped)
      eqUnavailability.off += dd.turned_off

      eqRepair.total += (dd.worked + dd.stopped + dd.turned_off)
      eqRepair.worked += dd.worked
      eqRepair.stopped += dd.stopped
      eqRepair.on += (dd.worked + dd.stopped)
      eqRepair.off += dd.turned_off
    } else if(eqStatus === 9) {
      eqUnavailability.total += (dd.worked + dd.stopped + dd.turned_off)
      eqUnavailability.worked += dd.worked
      eqUnavailability.stopped += dd.stopped
      eqUnavailability.on += (dd.worked + dd.stopped)
      eqUnavailability.off += dd.turned_off

      eqService.total += (dd.worked + dd.stopped + dd.turned_off)
      eqService.worked += dd.worked
      eqService.stopped += dd.stopped
      eqService.on += (dd.worked + dd.stopped)
      eqService.off += dd.turned_off
    } else {
      eqUnavailability.total += (dd.worked + dd.stopped + dd.turned_off)
      eqUnavailability.worked += dd.worked
      eqUnavailability.stopped += dd.stopped
      eqUnavailability.on += (dd.worked + dd.stopped)
      eqUnavailability.off += dd.turned_off

      eqOther.total += (dd.worked + dd.stopped + dd.turned_off)
      eqOther.worked += dd.worked
      eqOther.stopped += dd.stopped
      eqOther.on += (dd.worked + dd.stopped)
      eqOther.off += dd.turned_off
    }
  })

  return {
    dateShift: dateShift,
    interactionIds: interactionIds,
    interactions: interactions,
    start: eqStart,
    startWork: eqStartWork,
    end: eqEnd,
    endWork: eqEndWork,
    total: {
      total: {seconds: eqTotal.total, percent: eqTotal.total / eqTotal.total, time: Duration.fromObject({seconds: eqTotal.total ? eqTotal.total : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: eqTotal.worked, percent: eqTotal.worked / eqTotal.total, time: Duration.fromObject({seconds: eqTotal.worked ? eqTotal.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: eqTotal.stopped, percent: eqTotal.stopped / eqTotal.total, time: Duration.fromObject({seconds: eqTotal.stopped ? eqTotal.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: eqTotal.on, percent: eqTotal.on / eqTotal.total, time: Duration.fromObject({seconds: eqTotal.on ? eqTotal.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: eqTotal.off, percent: eqTotal.off / eqTotal.total, time: Duration.fromObject({seconds: eqTotal.off ? eqTotal.off : 0}).toFormat("hh:mm:ss")},
    },
    availability: {
      total: {seconds: eqAvailability.total, percent: eqAvailability.total / eqTotal.total, time: Duration.fromObject({seconds: eqAvailability.total ? eqAvailability.total : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: eqAvailability.worked, percent: eqAvailability.worked / eqTotal.total, time: Duration.fromObject({seconds: eqAvailability.worked ? eqAvailability.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: eqAvailability.stopped, percent: eqAvailability.stopped / eqTotal.total, time: Duration.fromObject({seconds: eqAvailability.stopped ? eqAvailability.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: eqAvailability.on, percent: eqAvailability.on / eqTotal.total, time: Duration.fromObject({seconds: eqAvailability.on ? eqAvailability.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: eqAvailability.off, percent: eqAvailability.off / eqTotal.total, time: Duration.fromObject({seconds: eqAvailability.off ? eqAvailability.off : 0}).toFormat("hh:mm:ss")},
    },
    works: {
      total: {seconds: eqWorks.total, percent: eqWorks.total / eqTotal.total, time: Duration.fromObject({seconds: eqWorks.total ? eqWorks.total : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: eqWorks.worked, percent: eqWorks.worked / eqTotal.total, time: Duration.fromObject({seconds: eqWorks.worked ? eqWorks.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: eqWorks.stopped, percent: eqWorks.stopped / eqTotal.total, time: Duration.fromObject({seconds: eqWorks.stopped ? eqWorks.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: eqWorks.on, percent: eqWorks.on / eqTotal.total, time: Duration.fromObject({seconds: eqWorks.on ? eqWorks.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: eqWorks.off, percent: eqWorks.off / eqTotal.total, time: Duration.fromObject({seconds: eqWorks.off ? eqWorks.off : 0}).toFormat("hh:mm:ss")},
    },
    maintenance: {
      total: {seconds: eqMaintenance.total, percent: eqMaintenance.total / eqTotal.total, time: Duration.fromObject({seconds: eqMaintenance.total ? eqMaintenance.total : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: eqMaintenance.worked, percent: eqMaintenance.worked / eqTotal.total, time: Duration.fromObject({seconds: eqMaintenance.worked ? eqMaintenance.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: eqMaintenance.stopped, percent: eqMaintenance.stopped / eqTotal.total, time: Duration.fromObject({seconds: eqMaintenance.stopped ? eqMaintenance.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: eqMaintenance.on, percent: eqMaintenance.on / eqTotal.total, time: Duration.fromObject({seconds: eqMaintenance.on ? eqMaintenance.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: eqMaintenance.off, percent: eqMaintenance.off / eqTotal.total, time: Duration.fromObject({seconds: eqMaintenance.off ? eqMaintenance.off : 0}).toFormat("hh:mm:ss")},
    },
    unavailability: {
      total: {seconds: eqUnavailability.total, percent: eqUnavailability.total / eqTotal.total, time: Duration.fromObject({seconds: eqUnavailability.total ? eqUnavailability.total : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: eqUnavailability.worked, percent: eqUnavailability.worked / eqTotal.total, time: Duration.fromObject({seconds: eqUnavailability.worked ? eqUnavailability.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: eqUnavailability.stopped, percent: eqUnavailability.stopped / eqTotal.total, time: Duration.fromObject({seconds: eqUnavailability.stopped ? eqUnavailability.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: eqUnavailability.on, percent: eqUnavailability.on / eqTotal.total, time: Duration.fromObject({seconds: eqUnavailability.on ? eqUnavailability.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: eqUnavailability.off, percent: eqUnavailability.off / eqTotal.total, time: Duration.fromObject({seconds: eqUnavailability.off ? eqUnavailability.off : 0}).toFormat("hh:mm:ss")},
    },
    broken: {
      total: {seconds: eqBroken.total, percent: eqBroken.total / eqTotal.total, time: Duration.fromObject({seconds: eqBroken.total ? eqBroken.total : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: eqBroken.worked, percent: eqBroken.worked / eqTotal.total, time: Duration.fromObject({seconds: eqBroken.worked ? eqBroken.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: eqBroken.stopped, percent: eqBroken.stopped / eqTotal.total, time: Duration.fromObject({seconds: eqBroken.stopped ? eqBroken.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: eqBroken.on, percent: eqBroken.on / eqTotal.total, time: Duration.fromObject({seconds: eqBroken.on ? eqBroken.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: eqBroken.off, percent: eqBroken.off / eqTotal.total, time: Duration.fromObject({seconds: eqBroken.off ? eqBroken.off : 0}).toFormat("hh:mm:ss")},
    },
    repair: {
      total: {seconds: eqRepair.total, percent: eqRepair.total / eqTotal.total, time: Duration.fromObject({seconds: eqRepair.total ? eqRepair.total : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: eqRepair.worked, percent: eqRepair.worked / eqTotal.total, time: Duration.fromObject({seconds: eqRepair.worked ? eqRepair.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: eqRepair.stopped, percent: eqRepair.stopped / eqTotal.total, time: Duration.fromObject({seconds: eqRepair.stopped ? eqRepair.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: eqRepair.on, percent: eqRepair.on / eqTotal.total, time: Duration.fromObject({seconds: eqRepair.on ? eqRepair.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: eqRepair.off, percent: eqRepair.off / eqTotal.total, time: Duration.fromObject({seconds: eqRepair.off ? eqRepair.off : 0}).toFormat("hh:mm:ss")},
    },
    service: {
      total: {seconds: eqService.total, percent: eqService.total / eqTotal.total, time: Duration.fromObject({seconds: eqService.total ? eqService.total : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: eqService.worked, percent: eqService.worked / eqTotal.total, time: Duration.fromObject({seconds: eqService.worked ? eqService.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: eqService.stopped, percent: eqService.stopped / eqTotal.total, time: Duration.fromObject({seconds: eqService.stopped ? eqService.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: eqService.on, percent: eqService.on / eqTotal.total, time: Duration.fromObject({seconds: eqService.on ? eqService.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: eqService.off, percent: eqService.off / eqTotal.total, time: Duration.fromObject({seconds: eqService.off ? eqService.off : 0}).toFormat("hh:mm:ss")},
    },
    other: {
      total: {seconds: eqOther.total, percent: eqOther.total / eqTotal.total, time: Duration.fromObject({seconds: eqOther.total ? eqOther.total : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: eqOther.worked, percent: eqOther.worked / eqTotal.total, time: Duration.fromObject({seconds: eqOther.worked ? eqOther.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: eqOther.stopped, percent: eqOther.stopped / eqTotal.total, time: Duration.fromObject({seconds: eqOther.stopped ? eqOther.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: eqOther.on, percent: eqOther.on / eqTotal.total, time: Duration.fromObject({seconds: eqOther.on ? eqOther.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: eqOther.off, percent: eqOther.off / eqTotal.total, time: Duration.fromObject({seconds: eqOther.off ? eqOther.off : 0}).toFormat("hh:mm:ss")},
    }
  }
}

// Расчет строк и саммари для по персоналу на основе ответа usage_dataset_by_personnels
function calculateSummaryPersonUsage(data: UsageDatasetByPersonnels[], shift: FShift, equipments: Equipment[], person: Person):SummaryPersonUsageRow {
  let itemsInteractionIds: number[] = []
  let itemsCardIds: number[] = []
  let daysData: PersonUsageRow[] = []
  let interactions: {
    equipment: Equipment, 
    availability: {total: STP, worked: STP, stopped: STP, on: STP, off: STP},
    works: {total: STP, worked: STP, stopped: STP, on: STP, off: STP},
    maintenance: {total: STP, worked: STP, stopped: STP, on: STP, off: STP},
    unavailability: {total: STP, worked: STP, stopped: STP, on: STP, off: STP},
    broken: {total: STP, worked: STP, stopped: STP, on: STP, off: STP},
    repair: {total: STP, worked: STP, stopped: STP, on: STP, off: STP},
    service: {total: STP, worked: STP, stopped: STP, on: STP, off: STP},
    other: {total: STP, worked: STP, stopped: STP, on: STP, off: STP},
  }[] = []

  let personInteraction: {total: number, worked: number, stopped: number, on: number, off: number} = {total: 0, worked: 0, stopped: 0, on: 0, off: 0}
  let personManual: {total: number, worked: number, stopped: number, on: number, off: number} = {total: 0, worked: 0, stopped: 0, on: 0, off: 0}
  let personMaintenance: {total: number, worked: number, stopped: number, on: number, off: number} = {total: 0, worked: 0, stopped: 0, on: 0, off: 0}
  let personRepair: {total: number, worked: number, stopped: number, on: number, off: number} = {total: 0, worked: 0, stopped: 0, on: 0, off: 0}
  let personService: {total: number, worked: number, stopped: number, on: number, off: number} = {total: 0, worked: 0, stopped: 0, on: 0, off: 0}

  Object.entries(_.groupBy(data, "equipment_item_id")).map(([eq_id, edata]:[string, UsageDatasetByPersonnels[]]) => {
    let equipment = equipments.find(e => e.id === eq_id)
    itemsInteractionIds.push(Number(eq_id))

    let eqInteraction: {total: number, worked: number, stopped: number, on: number, off: number} = {total: 0, worked: 0, stopped: 0, on: 0, off: 0}
    let eqAvailability: {total: number, worked: number, stopped: number, on: number, off: number} = {total: 0, worked: 0, stopped: 0, on: 0, off: 0}
    let eqWorks: {total: number, worked: number, stopped: number, on: number, off: number} = {total: 0, worked: 0, stopped: 0, on: 0, off: 0}
    let eqMaintenance: {total: number, worked: number, stopped: number, on: number, off: number} = {total: 0, worked: 0, stopped: 0, on: 0, off: 0}
    let eqUnavailability: {total: number, worked: number, stopped: number, on: number, off: number} = {total: 0, worked: 0, stopped: 0, on: 0, off: 0}
    let eqRepair: {total: number, worked: number, stopped: number, on: number, off: number} = {total: 0, worked: 0, stopped: 0, on: 0, off: 0}
    let eqBroken: {total: number, worked: number, stopped: number, on: number, off: number} = {total: 0, worked: 0, stopped: 0, on: 0, off: 0}
    let eqService: {total: number, worked: number, stopped: number, on: number, off: number} = {total: 0, worked: 0, stopped: 0, on: 0, off: 0}
    let eqOther: {total: number, worked: number, stopped: number, on: number, off: number} = {total: 0, worked: 0, stopped: 0, on: 0, off: 0}

    edata.map(ed => {
      personInteraction.total += ed.total
      personInteraction.on += (ed.worked + ed.stopped)
      personInteraction.off += ed.turned_off
      itemsCardIds.push(ed.access_token_id)

      let cardType = person.attributes.access_tokens.filter(at => at.status === "issued")?.find(c => c.id === ed.access_token_id)?.access_token_type ? 
        person.attributes.access_tokens.filter(at => at.status === "issued")?.find(c => c.id === ed.access_token_id)?.access_token_type 
        :
        person.attributes.access_token_type

      if(cardType === "manual") {
        personManual.total += ed.total
        personManual.worked += ed.worked
        personManual.stopped += ed.stopped
        personManual.on += (ed.worked + ed.stopped)
        personManual.off += ed.turned_off

        personInteraction.worked += ed.worked
        personInteraction.stopped += ed.stopped
      } else if(cardType === "adjustment") {
        personMaintenance.total += ed.total
        personMaintenance.worked += (equipment?.attributes.correct_maintenance_load ? ed.worked : 0)
        personMaintenance.stopped += (equipment?.attributes.correct_maintenance_load ? ed.stopped : ed.stopped + ed.worked)
        personMaintenance.on += (ed.worked + ed.stopped)
        personMaintenance.off += ed.turned_off

        personInteraction.worked += (equipment?.attributes.correct_maintenance_load ? ed.worked : 0)
        personInteraction.stopped += (equipment?.attributes.correct_maintenance_load ? ed.stopped : ed.stopped + ed.worked)
      } else if(cardType === "repair") {
        personRepair.total += ed.total
        personRepair.worked += ed.worked
        personRepair.stopped += ed.stopped
        personRepair.on += (ed.worked + ed.stopped)
        personRepair.off += ed.turned_off

        personInteraction.worked += ed.worked
        personInteraction.stopped += ed.stopped
      } else if(cardType === "service") {
        personService.total += ed.total
        personService.worked += ed.worked
        personService.stopped += ed.stopped
        personService.on += (ed.worked + ed.stopped)
        personService.off += ed.turned_off

        personInteraction.worked += ed.worked
        personInteraction.stopped += ed.stopped
      }


      if(ed.equipment_item_status === 0) {
        eqWorks.total += ed.total
        eqWorks.worked += ed.worked
        eqWorks.stopped += ed.stopped
        eqWorks.on += ed.worked + ed.stopped
        eqWorks.off += ed.turned_off

        eqAvailability.total += ed.total
        eqAvailability.worked += ed.worked
        eqAvailability.stopped += ed.stopped
        eqAvailability.on += ed.worked + ed.stopped
        eqAvailability.off += ed.turned_off

        eqInteraction.total += ed.total
        eqInteraction.worked += ed.worked
        eqInteraction.stopped += ed.stopped
        eqInteraction.on += ed.worked + ed.stopped
        eqInteraction.off += ed.turned_off

      } else if(ed.equipment_item_status === 5) {
        eqMaintenance.total += ed.total
        eqMaintenance.worked += equipment?.attributes.correct_maintenance_load ? ed.worked : 0
        eqMaintenance.stopped += equipment?.attributes.correct_maintenance_load ? ed.stopped : ed.stopped + ed.worked
        eqMaintenance.on += ed.worked + ed.stopped
        eqMaintenance.off += ed.turned_off

        eqAvailability.total += ed.total
        eqAvailability.worked += equipment?.attributes.correct_maintenance_load ? ed.worked : 0
        eqAvailability.stopped += equipment?.attributes.correct_maintenance_load ? ed.stopped : ed.stopped + ed.worked
        eqAvailability.on += ed.worked + ed.stopped
        eqAvailability.off += ed.turned_off

        eqInteraction.total += ed.total
        eqInteraction.worked += equipment?.attributes.correct_maintenance_load ? ed.worked : 0
        eqInteraction.stopped += equipment?.attributes.correct_maintenance_load ? ed.stopped : ed.stopped + ed.worked
        eqInteraction.on += ed.worked + ed.stopped
        eqInteraction.off += ed.turned_off

      } else if(ed.equipment_item_status === 1) {
        eqBroken.total += ed.total
        eqBroken.worked += ed.worked
        eqBroken.stopped += ed.stopped
        eqBroken.on += ed.worked + ed.stopped
        eqBroken.off += ed.turned_off

        eqUnavailability.total += ed.total
        eqUnavailability.worked += ed.worked
        eqUnavailability.stopped += ed.stopped
        eqUnavailability.on += ed.worked + ed.stopped
        eqUnavailability.off += ed.turned_off

        eqInteraction.total += ed.total
        eqInteraction.worked += ed.worked
        eqInteraction.stopped += ed.stopped
        eqInteraction.on += ed.worked + ed.stopped
        eqInteraction.off += ed.turned_off

      } else if(ed.equipment_item_status === 4) {
        eqRepair.total += ed.total
        eqRepair.worked += ed.worked
        eqRepair.stopped += ed.stopped
        eqRepair.on += ed.worked + ed.stopped
        eqRepair.off += ed.turned_off

        eqUnavailability.total += ed.total
        eqUnavailability.worked += ed.worked
        eqUnavailability.stopped += ed.stopped
        eqUnavailability.on += ed.worked + ed.stopped
        eqUnavailability.off += ed.turned_off

        eqInteraction.total += ed.total
        eqInteraction.worked += ed.worked
        eqInteraction.stopped += ed.stopped
        eqInteraction.on += ed.worked + ed.stopped
        eqInteraction.off += ed.turned_off

      } else if(ed.equipment_item_status === 9) {
        eqService.total += ed.total
        eqService.worked += ed.worked
        eqService.stopped += ed.stopped
        eqService.on += ed.worked + ed.stopped
        eqService.off += ed.turned_off

        eqUnavailability.total += ed.total
        eqUnavailability.worked += ed.worked
        eqUnavailability.stopped += ed.stopped
        eqUnavailability.on += ed.worked + ed.stopped
        eqUnavailability.off += ed.turned_off

        eqInteraction.total += ed.total
        eqInteraction.worked += ed.worked
        eqInteraction.stopped += ed.stopped
        eqInteraction.on += ed.worked + ed.stopped
        eqInteraction.off += ed.turned_off

      } else {
        eqOther.total += ed.total
        eqOther.worked += ed.worked
        eqOther.stopped += ed.stopped
        eqOther.on += ed.worked + ed.stopped
        eqOther.off += ed.turned_off

        eqUnavailability.total += ed.total
        eqUnavailability.worked += ed.worked
        eqUnavailability.stopped += ed.stopped
        eqUnavailability.on += ed.worked + ed.stopped
        eqUnavailability.off += ed.turned_off

        eqInteraction.total += ed.total
        eqInteraction.worked += ed.worked
        eqInteraction.stopped += ed.stopped
        eqInteraction.on += ed.worked + ed.stopped
        eqInteraction.off += ed.turned_off
      }
    })
    interactions.push({
      equipment: equipment!,
      availability: {
        total: {seconds: eqAvailability.total, percent: (eqAvailability.on + eqAvailability.off) / personInteraction.total, time: Duration.fromObject({seconds: eqAvailability.total}).toFormat("hh:mm:ss")},
        worked: {seconds: eqAvailability.worked, percent: eqAvailability.worked / personInteraction.total, time: Duration.fromObject({seconds: eqAvailability.worked ? eqAvailability.worked : 0}).toFormat("hh:mm:ss")},
        stopped: {seconds: eqAvailability.stopped, percent: eqAvailability.stopped / personInteraction.total, time: Duration.fromObject({seconds: eqAvailability.stopped ? eqAvailability.stopped : 0}).toFormat("hh:mm:ss")},
        on: {seconds: eqAvailability.on, percent: eqAvailability.on / personInteraction.total, time: Duration.fromObject({seconds: eqAvailability.on ? eqAvailability.on : 0}).toFormat("hh:mm:ss")},
        off: {seconds: eqAvailability.off, percent: eqAvailability.off / personInteraction.total, time: Duration.fromObject({seconds: eqAvailability.off ? eqAvailability.off : 0}).toFormat("hh:mm:ss")},
      },
      works: {
        total: {seconds: eqWorks.on + eqWorks.off, percent: (eqWorks.on + eqWorks.off) / personInteraction.total, time: Duration.fromObject({seconds: (eqWorks.on + eqWorks.off) ? (eqWorks.on + eqWorks.off) : 0}).toFormat("hh:mm:ss")},
        worked: {seconds: eqWorks.worked, percent: eqWorks.worked / personInteraction.total, time: Duration.fromObject({seconds: eqWorks.worked ? eqWorks.worked : 0}).toFormat("hh:mm:ss")},
        stopped: {seconds: eqWorks.stopped, percent: eqWorks.stopped / personInteraction.total, time: Duration.fromObject({seconds: eqWorks.stopped ? eqWorks.stopped : 0}).toFormat("hh:mm:ss")},
        on: {seconds: eqWorks.on, percent: eqWorks.on / personInteraction.total, time: Duration.fromObject({seconds: eqWorks.on ? eqWorks.on : 0}).toFormat("hh:mm:ss")},
        off: {seconds: eqWorks.off, percent: eqWorks.off / personInteraction.total, time: Duration.fromObject({seconds: eqWorks.off ? eqWorks.off : 0}).toFormat("hh:mm:ss")},
      },
      maintenance: {
        total: {seconds: eqMaintenance.on + eqMaintenance.off, percent: (eqMaintenance.on + eqMaintenance.off) / personInteraction.total, time: Duration.fromObject({seconds: (eqMaintenance.on + eqMaintenance.off) ? (eqMaintenance.on + eqMaintenance.off) : 0}).toFormat("hh:mm:ss")},
        worked: {seconds: eqMaintenance.worked, percent: eqMaintenance.worked / personInteraction.total, time: Duration.fromObject({seconds: eqMaintenance.worked ? eqMaintenance.worked : 0}).toFormat("hh:mm:ss")},
        stopped: {seconds: eqMaintenance.stopped, percent: eqMaintenance.stopped / personInteraction.total, time: Duration.fromObject({seconds: eqMaintenance.stopped ? eqMaintenance.stopped : 0}).toFormat("hh:mm:ss")},
        on: {seconds: eqMaintenance.on, percent: eqMaintenance.on / personInteraction.total, time: Duration.fromObject({seconds: eqMaintenance.on ? eqMaintenance.on : 0}).toFormat("hh:mm:ss")},
        off: {seconds: eqMaintenance.off, percent: eqMaintenance.off / personInteraction.total, time: Duration.fromObject({seconds: eqMaintenance.off ? eqMaintenance.off : 0}).toFormat("hh:mm:ss")},
      },
      unavailability: {
        total: {seconds: eqUnavailability.on + eqUnavailability.off, percent: (eqUnavailability.on + eqUnavailability.off) / personInteraction.total, time: Duration.fromObject({seconds: (eqUnavailability.on + eqUnavailability.off) ? (eqUnavailability.on + eqUnavailability.off) : 0}).toFormat("hh:mm:ss")},
        worked: {seconds: eqUnavailability.worked, percent: eqUnavailability.worked / personInteraction.total, time: Duration.fromObject({seconds: eqUnavailability.worked ? eqUnavailability.worked : 0}).toFormat("hh:mm:ss")},
        stopped: {seconds: eqUnavailability.stopped, percent: eqUnavailability.stopped / personInteraction.total, time: Duration.fromObject({seconds: eqUnavailability.stopped ? eqUnavailability.stopped : 0}).toFormat("hh:mm:ss")},
        on: {seconds: eqUnavailability.on, percent: eqUnavailability.on / personInteraction.total, time: Duration.fromObject({seconds: eqUnavailability.on ? eqUnavailability.on : 0}).toFormat("hh:mm:ss")},
        off: {seconds: eqUnavailability.off, percent: eqUnavailability.off / personInteraction.total, time: Duration.fromObject({seconds: eqUnavailability.off ? eqUnavailability.off : 0}).toFormat("hh:mm:ss")},
      },
      broken: {
        total: {seconds: eqBroken.on + eqBroken.off, percent: (eqBroken.on + eqBroken.off) / personInteraction.total, time: Duration.fromObject({seconds: (eqBroken.on + eqBroken.off) ? (eqBroken.on + eqBroken.off) : 0}).toFormat("hh:mm:ss")},
        worked: {seconds: eqBroken.worked, percent: eqBroken.worked / personInteraction.total, time: Duration.fromObject({seconds: eqBroken.worked ? eqBroken.worked : 0}).toFormat("hh:mm:ss")},
        stopped: {seconds: eqBroken.stopped, percent: eqBroken.stopped / personInteraction.total, time: Duration.fromObject({seconds: eqBroken.stopped ? eqBroken.stopped : 0}).toFormat("hh:mm:ss")},
        on: {seconds: eqBroken.on, percent: eqBroken.on / personInteraction.total, time: Duration.fromObject({seconds: eqBroken.on ? eqBroken.on : 0}).toFormat("hh:mm:ss")},
        off: {seconds: eqBroken.off, percent: eqBroken.off / personInteraction.total, time: Duration.fromObject({seconds: eqBroken.off ? eqBroken.off : 0}).toFormat("hh:mm:ss")},
      },
      repair: {
        total: {seconds: eqRepair.on + eqRepair.off, percent: (eqRepair.on + eqRepair.off) / personInteraction.total, time: Duration.fromObject({seconds: (eqRepair.on + eqRepair.off) ? (eqRepair.on + eqRepair.off) : 0}).toFormat("hh:mm:ss")},
        worked: {seconds: eqRepair.worked, percent: eqRepair.worked / personInteraction.total, time: Duration.fromObject({seconds: eqRepair.worked ? eqRepair.worked : 0}).toFormat("hh:mm:ss")},
        stopped: {seconds: eqRepair.stopped, percent: eqRepair.stopped / personInteraction.total, time: Duration.fromObject({seconds: eqRepair.stopped ? eqRepair.stopped : 0}).toFormat("hh:mm:ss")},
        on: {seconds: eqRepair.on, percent: eqRepair.on / personInteraction.total, time: Duration.fromObject({seconds: eqRepair.on ? eqRepair.on : 0}).toFormat("hh:mm:ss")},
        off: {seconds: eqRepair.off, percent: eqRepair.off / personInteraction.total, time: Duration.fromObject({seconds: eqRepair.off ? eqRepair.off : 0}).toFormat("hh:mm:ss")},
      },
      service: {
        total: {seconds: eqService.on + eqService.off, percent: (eqService.on + eqService.off) / personInteraction.total, time: Duration.fromObject({seconds: (eqService.on + eqService.off) ? (eqService.on + eqService.off) : 0}).toFormat("hh:mm:ss")},
        worked: {seconds: eqService.worked, percent: eqService.worked / personInteraction.total, time: Duration.fromObject({seconds: eqService.worked ? eqService.worked : 0}).toFormat("hh:mm:ss")},
        stopped: {seconds: eqService.stopped, percent: eqService.stopped / personInteraction.total, time: Duration.fromObject({seconds: eqService.stopped ? eqService.stopped : 0}).toFormat("hh:mm:ss")},
        on: {seconds: eqService.on, percent: eqService.on / personInteraction.total, time: Duration.fromObject({seconds: eqService.on ? eqService.on : 0}).toFormat("hh:mm:ss")},
        off: {seconds: eqService.off, percent: eqService.off / personInteraction.total, time: Duration.fromObject({seconds: eqService.off ? eqService.off : 0}).toFormat("hh:mm:ss")},
      },
      other: {
        total: {seconds: eqOther.on + eqOther.off, percent: (eqOther.on + eqOther.off) / personInteraction.total, time: Duration.fromObject({seconds: (eqOther.on + eqOther.off) ? (eqOther.on + eqOther.off) : 0}).toFormat("hh:mm:ss")},
        worked: {seconds: eqOther.worked, percent: eqOther.worked / personInteraction.total, time: Duration.fromObject({seconds: eqOther.worked ? eqOther.worked : 0}).toFormat("hh:mm:ss")},
        stopped: {seconds: eqOther.stopped, percent: eqOther.stopped / personInteraction.total, time: Duration.fromObject({seconds: eqOther.stopped ? eqOther.stopped : 0}).toFormat("hh:mm:ss")},
        on: {seconds: eqOther.on, percent: eqOther.on / personInteraction.total, time: Duration.fromObject({seconds: eqOther.on ? eqOther.on : 0}).toFormat("hh:mm:ss")},
        off: {seconds: eqOther.off, percent: eqOther.off / personInteraction.total, time: Duration.fromObject({seconds: eqOther.off ? eqOther.off : 0}).toFormat("hh:mm:ss")},
      },
    })
  })

  Object.entries(_.groupBy(data, "at")).map(([day, ddata]:[string, UsageDatasetByPersonnels[]]) => {
    daysData.push(calculatePersonUsageRow(day, ddata, shift, equipments, person))
  })

  return {
    interaction: {
      total: {seconds: personInteraction.total, percent: 1, time: Duration.fromObject({seconds: personInteraction.total ? personInteraction.total : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: personInteraction.worked, percent: personInteraction.worked / personInteraction.total, time: Duration.fromObject({seconds: personInteraction.worked ? personInteraction.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: personInteraction.stopped, percent: personInteraction.stopped / personInteraction.total, time: Duration.fromObject({seconds: personInteraction.stopped ? personInteraction.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: personInteraction.on, percent: personInteraction.on / personInteraction.total, time: Duration.fromObject({seconds: personInteraction.on ? personInteraction.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: personInteraction.off, percent: personInteraction.off / personInteraction.total, time: Duration.fromObject({seconds: personInteraction.off ? personInteraction.off : 0}).toFormat("hh:mm:ss")},
    },
    manual: {
      total: {seconds: personManual.total, percent: personManual.total / personInteraction.total, time: Duration.fromObject({seconds: personManual.total ? personManual.total : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: personManual.worked, percent: personManual.worked / personInteraction.total, time: Duration.fromObject({seconds: personManual.worked ? personManual.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: personManual.stopped, percent: personManual.stopped / personInteraction.total, time: Duration.fromObject({seconds: personManual.stopped ? personManual.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: personManual.on, percent: personManual.on / personInteraction.total, time: Duration.fromObject({seconds: personManual.on ? personManual.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: personManual.off, percent: personManual.off / personInteraction.total, time: Duration.fromObject({seconds: personManual.off ? personManual.off : 0}).toFormat("hh:mm:ss")},
    },
    maintenance: {
      total: {seconds: personMaintenance.total, percent: personMaintenance.total / personInteraction.total, time: Duration.fromObject({seconds: personMaintenance.total ? personMaintenance.total : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: personMaintenance.worked, percent: personMaintenance.worked / personInteraction.total, time: Duration.fromObject({seconds: personMaintenance.worked ? personMaintenance.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: personMaintenance.stopped, percent: personMaintenance.stopped / personInteraction.total, time: Duration.fromObject({seconds: personMaintenance.stopped ? personMaintenance.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: personMaintenance.on, percent: personMaintenance.on / personInteraction.total, time: Duration.fromObject({seconds: personMaintenance.on ? personMaintenance.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: personMaintenance.off, percent: personMaintenance.off / personInteraction.total, time: Duration.fromObject({seconds: personMaintenance.off ? personMaintenance.off : 0}).toFormat("hh:mm:ss")},
    },
    repair: {
      total: {seconds: personRepair.total, percent: personRepair.total / personInteraction.total, time: Duration.fromObject({seconds: personRepair.total ? personRepair.total : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: personRepair.worked, percent: personRepair.worked / personInteraction.total, time: Duration.fromObject({seconds: personRepair.worked ? personRepair.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: personRepair.stopped, percent: personRepair.stopped / personInteraction.total, time: Duration.fromObject({seconds: personRepair.stopped ? personRepair.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: personRepair.on, percent: personRepair.on / personInteraction.total, time: Duration.fromObject({seconds: personRepair.on ? personRepair.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: personRepair.off, percent: personRepair.off / personInteraction.total, time: Duration.fromObject({seconds: personRepair.off ? personRepair.off : 0}).toFormat("hh:mm:ss")},
    },
    service: {
      total: {seconds: personService.total, percent: personService.total / personInteraction.total, time: Duration.fromObject({seconds: personService.total ? personService.total : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: personService.worked, percent: personService.worked / personInteraction.total, time: Duration.fromObject({seconds: personService.worked ? personService.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: personService.stopped, percent: personService.stopped / personInteraction.total, time: Duration.fromObject({seconds: personService.stopped ? personService.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: personService.on, percent: personService.on / personInteraction.total, time: Duration.fromObject({seconds: personService.on ? personService.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: personService.off, percent: personService.off / personInteraction.total, time: Duration.fromObject({seconds: personService.off ? personService.off : 0}).toFormat("hh:mm:ss")},
    },
    interactionIds: itemsInteractionIds,
    cardIds: _.uniq(itemsCardIds),
    interactions: interactions,
    data: daysData
  }
}
// Расчет строк для по персоналу на основе ответв usage_dataset_by_personnels
function calculatePersonUsageRow(day: string, data: UsageDatasetByPersonnels[], shift: FShift, equipments: Equipment[], person: Person):PersonUsageRow {
  let dateShift = convertFshiftsToDateShifts([shift], DateTime.fromSQL(day))[0]
  let interactionIds: number[] = _.uniq(data.map(dd => dd.equipment_item_id))
  let cardIds: number[] = _.uniq(data.map(dd => dd.access_token_id))
  let personInteraction: {total: number, worked: number, stopped: number, on: number, off: number} = {total: 0, worked: 0, stopped: 0, on: 0, off: 0}
  let personManual: {total: number, worked: number, stopped: number, on: number, off: number} = {total: 0, worked: 0, stopped: 0, on: 0, off: 0}
  let personMaintenance: {total: number, worked: number, stopped: number, on: number, off: number} = {total: 0, worked: 0, stopped: 0, on: 0, off: 0}
  let personRepair: {total: number, worked: number, stopped: number, on: number, off: number} = {total: 0, worked: 0, stopped: 0, on: 0, off: 0}
  let personService: {total: number, worked: number, stopped: number, on: number, off: number} = {total: 0, worked: 0, stopped: 0, on: 0, off: 0}
  let personStart: DateTime = _.min(data.map(dd => DateTime.fromSQL(dd.start_at)))!
  let personStartWork: DateTime = _.min(data.map(dd => DateTime.fromSQL(dd.start_at_worked)))!
  let personEnd: DateTime = _.max(data.map(dd => DateTime.fromSQL(dd.end_at)))!
  let personEndWork: DateTime = _.max(data.map(dd => DateTime.fromSQL(dd.end_at_worked)))!

  data.map(dd => {
    let equipment = equipments.find(e => e.id === String(dd.equipment_item_id))

    personInteraction.total += dd.total
    personInteraction.on += (dd.worked + dd.stopped)
    personInteraction.off += dd.turned_off

    let cardType = person.attributes.access_tokens.filter(at => at.status === "issued")?.find(c => c.id === dd.access_token_id)?.access_token_type ? 
      person.attributes.access_tokens.filter(at => at.status === "issued")?.find(c => c.id === dd.access_token_id)?.access_token_type 
      :
      person.attributes.access_token_type

    if(cardType === "manual") {
      personManual.total += dd.total
      personManual.worked += dd.worked
      personManual.stopped += dd.stopped
      personManual.on += (dd.worked + dd.stopped)
      personManual.off += dd.turned_off

      personInteraction.worked += dd.worked
      personInteraction.stopped += dd.stopped
    } else if(cardType === "adjustment") {
      personMaintenance.total += dd.total
      personMaintenance.worked += (equipment?.attributes.correct_maintenance_load ? dd.worked : 0)
      personMaintenance.stopped += (equipment?.attributes.correct_maintenance_load ? dd.stopped : dd.stopped + dd.worked)
      personMaintenance.on += (dd.worked + dd.stopped)
      personMaintenance.off += dd.turned_off

      personInteraction.worked += equipment?.attributes.correct_maintenance_load ? dd.worked : 0
      personInteraction.stopped += equipment?.attributes.correct_maintenance_load ? dd.stopped : dd.stopped + dd.worked
    } else if(cardType === "repair") {
      personRepair.total += dd.total
      personRepair.worked += dd.worked
      personRepair.stopped += dd.stopped
      personRepair.on += (dd.worked + dd.stopped)
      personRepair.off += dd.turned_off

      personInteraction.worked += dd.worked
      personInteraction.stopped += dd.stopped
    } else if(cardType === "service") {
      personService.total += dd.total
      personService.worked += dd.worked
      personService.stopped += dd.stopped
      personService.on += (dd.worked + dd.stopped)
      personService.off += dd.turned_off

      personInteraction.worked += dd.worked
      personInteraction.stopped += dd.stopped
    } else {
      return
    }
  })

  let interactions: {
    equipment: Equipment, 
    start: DateTime,
    startWork: DateTime,
    end: DateTime,
    endWork: DateTime,
    availability: {total: STP, worked: STP, stopped: STP, on: STP, off: STP},
    works: {total: STP, worked: STP, stopped: STP, on: STP, off: STP},
    maintenance: {total: STP, worked: STP, stopped: STP, on: STP, off: STP},
    unavailability: {total: STP, worked: STP, stopped: STP, on: STP, off: STP},
    broken: {total: STP, worked: STP, stopped: STP, on: STP, off: STP},
    repair: {total: STP, worked: STP, stopped: STP, on: STP, off: STP},
    service: {total: STP, worked: STP, stopped: STP, on: STP, off: STP},
    other: {total: STP, worked: STP, stopped: STP, on: STP, off: STP},
  }[] = []

  Object.entries(_.groupBy(data, "equipment_item_id")).map(([eq_id, eqData]:[string, UsageDatasetByPersonnels[]]) => { 
    let equipment = equipments.find(e => e.id === eq_id)
    let eqInteraction: {total: number, worked: number, stopped: number, on: number, off: number} = {total: 0, worked: 0, stopped: 0, on: 0, off: 0}
    let eqAvailability: {total: number, worked: number, stopped: number, on: number, off: number} = {total: 0, worked: 0, stopped: 0, on: 0, off: 0}
    let eqWorks: {total: number, worked: number, stopped: number, on: number, off: number} = {total: 0, worked: 0, stopped: 0, on: 0, off: 0}
    let eqMaintenance: {total: number, worked: number, stopped: number, on: number, off: number} = {total: 0, worked: 0, stopped: 0, on: 0, off: 0}
    let eqUnavailability: {total: number, worked: number, stopped: number, on: number, off: number} = {total: 0, worked: 0, stopped: 0, on: 0, off: 0}
    let eqRepair: {total: number, worked: number, stopped: number, on: number, off: number} = {total: 0, worked: 0, stopped: 0, on: 0, off: 0}
    let eqBroken: {total: number, worked: number, stopped: number, on: number, off: number} = {total: 0, worked: 0, stopped: 0, on: 0, off: 0}
    let eqService: {total: number, worked: number, stopped: number, on: number, off: number} = {total: 0, worked: 0, stopped: 0, on: 0, off: 0}
    let eqOther: {total: number, worked: number, stopped: number, on: number, off: number} = {total: 0, worked: 0, stopped: 0, on: 0, off: 0}

    eqData.map(edd => {
      if(edd.equipment_item_status === 0) {
        eqWorks.total += edd.total
        eqWorks.worked += edd.worked
        eqWorks.stopped += edd.stopped
        eqWorks.on += edd.worked + edd.stopped
        eqWorks.off += edd.turned_off

        eqAvailability.total += edd.total
        eqAvailability.worked += edd.worked
        eqAvailability.stopped += edd.stopped
        eqAvailability.on += edd.worked + edd.stopped
        eqAvailability.off += edd.turned_off

        eqInteraction.total += edd.total
        eqInteraction.worked += edd.worked
        eqInteraction.stopped += edd.stopped
        eqInteraction.on += edd.worked + edd.stopped
        eqInteraction.off += edd.turned_off

      } else if(edd.equipment_item_status === 5) {
        eqMaintenance.total += edd.total
        eqMaintenance.worked += equipment?.attributes.correct_maintenance_load ? edd.worked : 0
        eqMaintenance.stopped += equipment?.attributes.correct_maintenance_load ? edd.stopped : edd.stopped + edd.worked
        eqMaintenance.on += edd.worked + edd.stopped
        eqMaintenance.off += edd.turned_off

        eqAvailability.total += edd.total
        eqAvailability.worked += equipment?.attributes.correct_maintenance_load ? edd.worked : 0
        eqAvailability.stopped += equipment?.attributes.correct_maintenance_load ? edd.stopped : edd.stopped + edd.worked
        eqAvailability.on += edd.worked + edd.stopped
        eqAvailability.off += edd.turned_off

        eqInteraction.total += edd.total
        eqInteraction.worked += equipment?.attributes.correct_maintenance_load ? edd.worked : 0
        eqInteraction.stopped += equipment?.attributes.correct_maintenance_load ? edd.stopped : edd.stopped + edd.worked
        eqInteraction.on += edd.worked + edd.stopped
        eqInteraction.off += edd.turned_off

      } else if(edd.equipment_item_status === 1) {
        eqBroken.total += edd.total
        eqBroken.worked += edd.worked
        eqBroken.stopped += edd.stopped
        eqBroken.on += edd.worked + edd.stopped
        eqBroken.off += edd.turned_off

        eqUnavailability.total += edd.total
        eqUnavailability.worked += edd.worked
        eqUnavailability.stopped += edd.stopped
        eqUnavailability.on += edd.worked + edd.stopped
        eqUnavailability.off += edd.turned_off

        eqInteraction.total += edd.total
        eqInteraction.worked += edd.worked
        eqInteraction.stopped += edd.stopped
        eqInteraction.on += edd.worked + edd.stopped
        eqInteraction.off += edd.turned_off

      } else if(edd.equipment_item_status === 4) {
        eqRepair.total += edd.total
        eqRepair.worked += edd.worked
        eqRepair.stopped += edd.stopped
        eqRepair.on += edd.worked + edd.stopped
        eqRepair.off += edd.turned_off

        eqUnavailability.total += edd.total
        eqUnavailability.worked += edd.worked
        eqUnavailability.stopped += edd.stopped
        eqUnavailability.on += edd.worked + edd.stopped
        eqUnavailability.off += edd.turned_off

        eqInteraction.total += edd.total
        eqInteraction.worked += edd.worked
        eqInteraction.stopped += edd.stopped
        eqInteraction.on += edd.worked + edd.stopped
        eqInteraction.off += edd.turned_off

      } else if(edd.equipment_item_status === 9) {
        eqService.total += edd.total
        eqService.worked += edd.worked
        eqService.stopped += edd.stopped
        eqService.on += edd.worked + edd.stopped
        eqService.off += edd.turned_off

        eqUnavailability.total += edd.total
        eqUnavailability.worked += edd.worked
        eqUnavailability.stopped += edd.stopped
        eqUnavailability.on += edd.worked + edd.stopped
        eqUnavailability.off += edd.turned_off

        eqInteraction.total += edd.total
        eqInteraction.worked += edd.worked
        eqInteraction.stopped += edd.stopped
        eqInteraction.on += edd.worked + edd.stopped
        eqInteraction.off += edd.turned_off

      } else {
        eqOther.total += edd.total
        eqOther.worked += edd.worked
        eqOther.stopped += edd.stopped
        eqOther.on += edd.worked + edd.stopped
        eqOther.off += edd.turned_off

        eqUnavailability.total += edd.total
        eqUnavailability.worked += edd.worked
        eqUnavailability.stopped += edd.stopped
        eqUnavailability.on += edd.worked + edd.stopped
        eqUnavailability.off += edd.turned_off

        eqInteraction.total += edd.total
        eqInteraction.worked += edd.worked
        eqInteraction.stopped += edd.stopped
        eqInteraction.on += edd.worked + edd.stopped
        eqInteraction.off += edd.turned_off
      }
    })

    interactions.push({
      equipment: equipment!,
      start: _.min(eqData.map(e => DateTime.fromSQL(e.start_at)))!,
      startWork: _.min(eqData.map(e => DateTime.fromSQL(e.start_at_worked)))!,
      end: _.max(eqData.map(e => DateTime.fromSQL(e.end_at)))!,
      endWork: _.max(eqData.map(e => DateTime.fromSQL(e.end_at_worked)))!,
      availability: {
        total: {seconds: eqAvailability.total, percent: (eqAvailability.on + eqAvailability.off) / personInteraction.total, time: Duration.fromObject({seconds: eqAvailability.total}).toFormat("hh:mm:ss")},
        worked: {seconds: eqAvailability.worked, percent: eqAvailability.worked / personInteraction.total, time: Duration.fromObject({seconds: eqAvailability.worked ? eqAvailability.worked : 0}).toFormat("hh:mm:ss")},
        stopped: {seconds: eqAvailability.stopped, percent: eqAvailability.stopped / personInteraction.total, time: Duration.fromObject({seconds: eqAvailability.stopped ? eqAvailability.stopped : 0}).toFormat("hh:mm:ss")},
        on: {seconds: eqAvailability.on, percent: eqAvailability.on / personInteraction.total, time: Duration.fromObject({seconds: eqAvailability.on ? eqAvailability.on : 0}).toFormat("hh:mm:ss")},
        off: {seconds: eqAvailability.off, percent: eqAvailability.off / personInteraction.total, time: Duration.fromObject({seconds: eqAvailability.off ? eqAvailability.off : 0}).toFormat("hh:mm:ss")},
      },
      works: {
        total: {seconds: eqWorks.on + eqWorks.off, percent: (eqWorks.on + eqWorks.off) / personInteraction.total, time: Duration.fromObject({seconds: (eqWorks.on + eqWorks.off) ? (eqWorks.on + eqWorks.off) : 0}).toFormat("hh:mm:ss")},
        worked: {seconds: eqWorks.worked, percent: eqWorks.worked / personInteraction.total, time: Duration.fromObject({seconds: eqWorks.worked ? eqWorks.worked : 0}).toFormat("hh:mm:ss")},
        stopped: {seconds: eqWorks.stopped, percent: eqWorks.stopped / personInteraction.total, time: Duration.fromObject({seconds: eqWorks.stopped ? eqWorks.stopped : 0}).toFormat("hh:mm:ss")},
        on: {seconds: eqWorks.on, percent: eqWorks.on / personInteraction.total, time: Duration.fromObject({seconds: eqWorks.on ? eqWorks.on : 0}).toFormat("hh:mm:ss")},
        off: {seconds: eqWorks.off, percent: eqWorks.off / personInteraction.total, time: Duration.fromObject({seconds: eqWorks.off ? eqWorks.off : 0}).toFormat("hh:mm:ss")},
      },
      maintenance: {
        total: {seconds: eqMaintenance.on + eqMaintenance.off, percent: (eqMaintenance.on + eqMaintenance.off) / personInteraction.total, time: Duration.fromObject({seconds: (eqMaintenance.on + eqMaintenance.off) ? (eqMaintenance.on + eqMaintenance.off) : 0}).toFormat("hh:mm:ss")},
        worked: {seconds: eqMaintenance.worked, percent: eqMaintenance.worked / personInteraction.total, time: Duration.fromObject({seconds: eqMaintenance.worked ? eqMaintenance.worked : 0}).toFormat("hh:mm:ss")},
        stopped: {seconds: eqMaintenance.stopped, percent: eqMaintenance.stopped / personInteraction.total, time: Duration.fromObject({seconds: eqMaintenance.stopped ? eqMaintenance.stopped : 0}).toFormat("hh:mm:ss")},
        on: {seconds: eqMaintenance.on, percent: eqMaintenance.on / personInteraction.total, time: Duration.fromObject({seconds: eqMaintenance.on ? eqMaintenance.on : 0}).toFormat("hh:mm:ss")},
        off: {seconds: eqMaintenance.off, percent: eqMaintenance.off / personInteraction.total, time: Duration.fromObject({seconds: eqMaintenance.off ? eqMaintenance.off : 0}).toFormat("hh:mm:ss")},
      },
      unavailability: {
        total: {seconds: eqUnavailability.on + eqUnavailability.off, percent: (eqUnavailability.on + eqUnavailability.off) / personInteraction.total, time: Duration.fromObject({seconds: (eqUnavailability.on + eqUnavailability.off) ? (eqUnavailability.on + eqUnavailability.off) : 0}).toFormat("hh:mm:ss")},
        worked: {seconds: eqUnavailability.worked, percent: eqUnavailability.worked / personInteraction.total, time: Duration.fromObject({seconds: eqUnavailability.worked ? eqUnavailability.worked : 0}).toFormat("hh:mm:ss")},
        stopped: {seconds: eqUnavailability.stopped, percent: eqUnavailability.stopped / personInteraction.total, time: Duration.fromObject({seconds: eqUnavailability.stopped ? eqUnavailability.stopped : 0}).toFormat("hh:mm:ss")},
        on: {seconds: eqUnavailability.on, percent: eqUnavailability.on / personInteraction.total, time: Duration.fromObject({seconds: eqUnavailability.on ? eqUnavailability.on : 0}).toFormat("hh:mm:ss")},
        off: {seconds: eqUnavailability.off, percent: eqUnavailability.off / personInteraction.total, time: Duration.fromObject({seconds: eqUnavailability.off ? eqUnavailability.off : 0}).toFormat("hh:mm:ss")},
      },
      broken: {
        total: {seconds: eqBroken.on + eqBroken.off, percent: (eqBroken.on + eqBroken.off) / personInteraction.total, time: Duration.fromObject({seconds: (eqBroken.on + eqBroken.off) ? (eqBroken.on + eqBroken.off) : 0}).toFormat("hh:mm:ss")},
        worked: {seconds: eqBroken.worked, percent: eqBroken.worked / personInteraction.total, time: Duration.fromObject({seconds: eqBroken.worked ? eqBroken.worked : 0}).toFormat("hh:mm:ss")},
        stopped: {seconds: eqBroken.stopped, percent: eqBroken.stopped / personInteraction.total, time: Duration.fromObject({seconds: eqBroken.stopped ? eqBroken.stopped : 0}).toFormat("hh:mm:ss")},
        on: {seconds: eqBroken.on, percent: eqBroken.on / personInteraction.total, time: Duration.fromObject({seconds: eqBroken.on ? eqBroken.on : 0}).toFormat("hh:mm:ss")},
        off: {seconds: eqBroken.off, percent: eqBroken.off / personInteraction.total, time: Duration.fromObject({seconds: eqBroken.off ? eqBroken.off : 0}).toFormat("hh:mm:ss")},
      },
      repair: {
        total: {seconds: eqRepair.on + eqRepair.off, percent: (eqRepair.on + eqRepair.off) / personInteraction.total, time: Duration.fromObject({seconds: (eqRepair.on + eqRepair.off) ? (eqRepair.on + eqRepair.off) : 0}).toFormat("hh:mm:ss")},
        worked: {seconds: eqRepair.worked, percent: eqRepair.worked / personInteraction.total, time: Duration.fromObject({seconds: eqRepair.worked ? eqRepair.worked : 0}).toFormat("hh:mm:ss")},
        stopped: {seconds: eqRepair.stopped, percent: eqRepair.stopped / personInteraction.total, time: Duration.fromObject({seconds: eqRepair.stopped ? eqRepair.stopped : 0}).toFormat("hh:mm:ss")},
        on: {seconds: eqRepair.on, percent: eqRepair.on / personInteraction.total, time: Duration.fromObject({seconds: eqRepair.on ? eqRepair.on : 0}).toFormat("hh:mm:ss")},
        off: {seconds: eqRepair.off, percent: eqRepair.off / personInteraction.total, time: Duration.fromObject({seconds: eqRepair.off ? eqRepair.off : 0}).toFormat("hh:mm:ss")},
      },
      service: {
        total: {seconds: eqService.on + eqService.off, percent: (eqService.on + eqService.off) / personInteraction.total, time: Duration.fromObject({seconds: (eqService.on + eqService.off) ? (eqService.on + eqService.off) : 0}).toFormat("hh:mm:ss")},
        worked: {seconds: eqService.worked, percent: eqService.worked / personInteraction.total, time: Duration.fromObject({seconds: eqService.worked ? eqService.worked : 0}).toFormat("hh:mm:ss")},
        stopped: {seconds: eqService.stopped, percent: eqService.stopped / personInteraction.total, time: Duration.fromObject({seconds: eqService.stopped ? eqService.stopped : 0}).toFormat("hh:mm:ss")},
        on: {seconds: eqService.on, percent: eqService.on / personInteraction.total, time: Duration.fromObject({seconds: eqService.on ? eqService.on : 0}).toFormat("hh:mm:ss")},
        off: {seconds: eqService.off, percent: eqService.off / personInteraction.total, time: Duration.fromObject({seconds: eqService.off ? eqService.off : 0}).toFormat("hh:mm:ss")},
      },
      other: {
        total: {seconds: eqOther.on + eqOther.off, percent: (eqOther.on + eqOther.off) / personInteraction.total, time: Duration.fromObject({seconds: (eqOther.on + eqOther.off) ? (eqOther.on + eqOther.off) : 0}).toFormat("hh:mm:ss")},
        worked: {seconds: eqOther.worked, percent: eqOther.worked / personInteraction.total, time: Duration.fromObject({seconds: eqOther.worked ? eqOther.worked : 0}).toFormat("hh:mm:ss")},
        stopped: {seconds: eqOther.stopped, percent: eqOther.stopped / personInteraction.total, time: Duration.fromObject({seconds: eqOther.stopped ? eqOther.stopped : 0}).toFormat("hh:mm:ss")},
        on: {seconds: eqOther.on, percent: eqOther.on / personInteraction.total, time: Duration.fromObject({seconds: eqOther.on ? eqOther.on : 0}).toFormat("hh:mm:ss")},
        off: {seconds: eqOther.off, percent: eqOther.off / personInteraction.total, time: Duration.fromObject({seconds: eqOther.off ? eqOther.off : 0}).toFormat("hh:mm:ss")},
      },
    })
  })

  return {
    dateShift: dateShift,
    interactionIds: interactionIds,
    cardIds: _.uniq(cardIds),
    start: personStart,
    startWork: personStartWork,
    end: personEnd,
    endWork: personEndWork,
    interaction: {
      total: {seconds: personInteraction.total, percent: 1, time: Duration.fromObject({seconds: personInteraction.total ? personInteraction.total : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: personInteraction.worked, percent: personInteraction.worked / personInteraction.total, time: Duration.fromObject({seconds: personInteraction.worked ? personInteraction.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: personInteraction.stopped, percent: personInteraction.stopped / personInteraction.total, time: Duration.fromObject({seconds: personInteraction.stopped ? personInteraction.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: personInteraction.on, percent: personInteraction.on / personInteraction.total, time: Duration.fromObject({seconds: personInteraction.on ? personInteraction.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: personInteraction.off, percent: personInteraction.off / personInteraction.total, time: Duration.fromObject({seconds: personInteraction.off ? personInteraction.off : 0}).toFormat("hh:mm:ss")},
    },
    manual: {
      total: {seconds: personManual.total, percent: 1, time: Duration.fromObject({seconds: personManual.total ? personManual.total : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: personManual.worked, percent: personManual.worked / personInteraction.total, time: Duration.fromObject({seconds: personManual.worked ? personManual.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: personManual.stopped, percent: personManual.stopped / personInteraction.total, time: Duration.fromObject({seconds: personManual.stopped ? personManual.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: personManual.on, percent: personManual.on / personInteraction.total, time: Duration.fromObject({seconds: personManual.on ? personManual.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: personManual.off, percent: personManual.off / personInteraction.total, time: Duration.fromObject({seconds: personManual.off ? personManual.off : 0}).toFormat("hh:mm:ss")},
    },
    maintenance: {
      total: {seconds: personMaintenance.total, percent: 1, time: Duration.fromObject({seconds: personMaintenance.total ? personMaintenance.total : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: personMaintenance.worked, percent: personMaintenance.worked / personInteraction.total, time: Duration.fromObject({seconds: personMaintenance.worked ? personMaintenance.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: personMaintenance.stopped, percent: personMaintenance.stopped / personInteraction.total, time: Duration.fromObject({seconds: personMaintenance.stopped ? personMaintenance.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: personMaintenance.on, percent: personMaintenance.on / personInteraction.total, time: Duration.fromObject({seconds: personMaintenance.on ? personMaintenance.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: personMaintenance.off, percent: personMaintenance.off / personInteraction.total, time: Duration.fromObject({seconds: personMaintenance.off ? personMaintenance.off : 0}).toFormat("hh:mm:ss")},
    },
    repair: {
      total: {seconds: personRepair.total, percent: 1, time: Duration.fromObject({seconds: personRepair.total ? personRepair.total : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: personRepair.worked, percent: personRepair.worked / personInteraction.total, time: Duration.fromObject({seconds: personRepair.worked ? personRepair.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: personRepair.stopped, percent: personRepair.stopped / personInteraction.total, time: Duration.fromObject({seconds: personRepair.stopped ? personRepair.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: personRepair.on, percent: personRepair.on / personInteraction.total, time: Duration.fromObject({seconds: personRepair.on ? personRepair.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: personRepair.off, percent: personRepair.off / personInteraction.total, time: Duration.fromObject({seconds: personRepair.off ? personRepair.off : 0}).toFormat("hh:mm:ss")},
    },
    service: {
      total: {seconds: personService.total, percent: 1, time: Duration.fromObject({seconds: personService.total ? personService.total : 0}).toFormat("hh:mm:ss")},
      worked: {seconds: personService.worked, percent: personService.worked / personInteraction.total, time: Duration.fromObject({seconds: personService.worked ? personService.worked : 0}).toFormat("hh:mm:ss")},
      stopped: {seconds: personService.stopped, percent: personService.stopped / personInteraction.total, time: Duration.fromObject({seconds: personService.stopped ? personService.stopped : 0}).toFormat("hh:mm:ss")},
      on: {seconds: personService.on, percent: personService.on / personInteraction.total, time: Duration.fromObject({seconds: personService.on ? personService.on : 0}).toFormat("hh:mm:ss")},
      off: {seconds: personService.off, percent: personService.off / personInteraction.total, time: Duration.fromObject({seconds: personService.off ? personService.off : 0}).toFormat("hh:mm:ss")},
    },
    interactions: interactions
  }
}

// SHIFTS

const fullDayShift = {id: "full", label: 'Полный день', start_day: DateTime.local(), end_day: DateTime.local(), start_time: DateTime.local().startOf('day'), end_time: DateTime.local().endOf('day'), duration: 86400}

function screenShiftsToDateShifts(working_shifts: ScreenShifts[]) {

  let shifts: DateShift[] = []
  working_shifts.map(shift => {
    // Проверка 
    if(!shift.working_shift) { return }
    let start = DateTime.fromISO(shift.working_shift.started_at)
    let end = DateTime.fromISO(shift.working_shift.ended_at)
    let build_start = DateTime.fromObject({year: 2000, month: 1, day: 1, hour: start.hour, minute: start.minute, second: start.second})
    let build_end = DateTime.fromObject({year: 2000, month: 1, day: 1, hour: end.hour, minute: end.minute, second: end.second})
    let diff = build_end.diff(build_start, 'seconds').seconds
    shifts.push({
      id: shift.working_shift.id,
      label: shift.working_shift.name, 
      start_day: build_start, 
      end_day: build_end,
      start_time: build_start, 
      end_time: build_end, 
      duration: diff > 0 ? diff : diff * -1
    })
  })
  let ordered = _.orderBy(shifts, ['start_time']).map((s, i) => ({...s, position: i + 1}))
  return ordered
}

function detectRealtimeShift(shifts: DateShift[], currentDateTime: DateTime) {
  let cs = shifts.filter(s => detectShift(s, currentDateTime))

  if(cs && cs.length > 0) {
      return cs[0]
  } else {
      return fullDayShift
  }
}

function detectShiftForEvent(shifts: DateShift[], currentDateTime: DateTime) {
  let cs = shifts.filter(s => detectShift(s, currentDateTime))

  if(cs && cs.length > 0) {
      return cs[0].id
  } else {
      return "full"
  }
}

function convertFshiftsToDateShifts(shifts: FShift[], date: DateTime):DateShift[] {
  let monitoringShifts: DateShift[] = []
  shifts.map(shift => {
    let transitiveShift = shift.start_at > shift.end_at
    if(transitiveShift) {
      let start = date.set({hour: shift.start_at.hour, minute: shift.start_at.minute})
      let end = date.plus({days: 1}).set({hour: shift.end_at.hour, minute: shift.end_at.minute})
      monitoringShifts.push({
        id: shift.id,
        label: shift.name, 
        start_day: start.startOf("day"), 
        end_day: end.startOf("day"),
        start_time: start, 
        end_time: end, 
        duration: end.diff(start).toMillis() / 1000
      })
    } else {
      let start = date.set({hour: shift.start_at.hour, minute: shift.start_at.minute})
      let end = date.set({hour: shift.end_at.hour, minute: shift.end_at.minute})
      monitoringShifts.push({
        id: shift.id,
        label: shift.name, 
        start_day: start.startOf("day"), 
        end_day: end.startOf('day'),
        start_time: start, 
        end_time: end, 
        duration: end.diff(start).toMillis() / 1000
      })
    }
  })
  let ordered = _.orderBy(monitoringShifts, ['start_time']).map((s, i) => ({...s, position: i + 1}))
  return ordered
}

function detectShift(shift: DateShift, currentDateTime: DateTime) {
  let transitiveShift = shift.start_time > shift.end_time
  if(transitiveShift) {
    if(currentDateTime.set({hour: shift.start_time.hour, minute: shift.start_time.minute}) < currentDateTime && currentDateTime < currentDateTime.endOf('day')) {
      return true
    } else if(currentDateTime.set({hour: shift.end_time.hour, minute: shift.end_time.minute}) > currentDateTime && currentDateTime > currentDateTime.startOf('day')) {
      return true
    } else {
      return false
    }
  } else if(currentDateTime.set(({hour: shift.start_time.hour, minute: shift.start_time.minute})) < currentDateTime && currentDateTime.set(({hour: shift.end_time.hour, minute: shift.end_time.minute})) > currentDateTime) {
    return true
  } else {
    return false
  }
}

function convertShiftForDate(shift: DateShift, date: DateTime | null) {
  let transitiveShift = shift.start_time > shift.end_time
  if(transitiveShift) {
    if(!date) {
      if(DateTime.local() > DateTime.local().set({hour: shift.start_time.hour, minute: shift.start_time.minute}) && DateTime.local() < DateTime.local().endOf('day')) {
        return {...shift, start_day: DateTime.local(), end_day: DateTime.local().plus({days: 1})}
      } else {
        return {...shift, start_day: DateTime.local().minus({days: 1}), end_day: DateTime.local()}
      }
    } else {
      return {...shift, start_day: date, end_day: date.plus({days: 1})}
    }
  } else {
    if(!date) {
      return {...shift, start_day: DateTime.local(), end_day: DateTime.local()}
    } else {
      return {...shift, start_day: date, end_day: date}
    } 
  }
}

// TODO Нужен способо определения смены к которым относятся события
function detectShiftByEvents(events: Event[], shifts: DateShift[]) {
  let shiftIds: string[] = []
  events.map(e => {
    shiftIds.push(detectShiftForEvent(shifts, DateTime.fromSQL(String(e[3]))))
  })

  return shifts.find(s => s.id === mode(shiftIds))
}

// Получение цвета по ключу
function colorByKey(p: WidgetParameter) {
  switch (p) {
    case "availability.total":
    case "works.total":
    case "maintenance.total":
    case "availability.on":
    case "works.on":
    case "maintenance.on":
      return "#5BC0EB"
    case "unavailability.total":
    case "broken.total":
    case "repair.total":
    case "service.total":
    case "other.total":
    case "unavailability.on":
    case "broken.on":
    case "repair.on":
    case "service.on":
    case "other.on":
      return "#FA7921"
    case "availability.worked":
    case "works.worked":
    case "maintenance.worked":
      return "#91cc75"
    case "availability.stopped":
    case "works.stopped":
    case "maintenance.stopped":
      return "#fac858"
    case "availability.off":
    case "works.off":
    case "maintenance.off":
      return "rgba(128,128,128,0.1)"
    default:
      return "rgba(128,128,128,0.1)"
  }
}


export { 
  // getEquipmentParamsWithExcludes, 
  getEquipmentInteractions,

  getEquipmentBreaksViolation,
  getEquipmentShiftFirstHourWorked, 
  getEquipmentShiftLastHourWorked, 
  extendEquipmentTimelineData,
  getEquipmentCorrectTimeline,

  // getPersonParamsWithExcludes,
  getPersonInteractions,
  
  getPersonShiftFirstHourWorked, 
  getPersonShiftLastHourWorked, 
  extendPersonTimelineData,
  getPersonCorrectTimeline,

  getTimelineByHours,

  // getDataByHours

  transformEvent,
  getCurrentState,
  getCurrentStatus,
  getStatusType,
  getCurrentStateColor,

  calculateEvents,

  calculateUsage,
  splitEventsByHours,
  splitEventByHour,
  extendEquipmentDataByEvents,

  // MONITOR CALCULATION
  calculateDataByEquipment,

  // REPORTS
  // calculateDataByEquipmentDays,
  calculateSummaryPersonUsage,
  calculatePersonUsageRow,
  calculateSummaryUsage,
  calculateUsageRow,

  // ANALYTICA
  calculateEquipmentRow,
  calculatePersonRow,
  calculateInteractions,
  colorByKey,
  calculateEquipmentParameter,

  // SHIFTS
  fullDayShift,
  screenShiftsToDateShifts,
  detectRealtimeShift,
  convertFshiftsToDateShifts,
  detectShift,
  convertShiftForDate,
  detectShiftByEvents
};
